import React, { useState, useEffect, useRef, createElement, Fragment, useCallback } from "react";
// Icons removed as requested
import { CheckIsEmptyArray } from '../Common';
import ReactMarkdown from 'react-markdown';
// NOTE: remark-gfm is intentionally NOT imported. remark-gfm@4 is ESM-only and
// pulls in modern micromark/mdast deps (numeric separators, 'devlop') that this
// CRA4/webpack4/babel toolchain cannot parse (build breaks). Markdown tables are
// instead rendered by the local splitMarkdownContent() parser below, which
// extracts pipe-tables into <table> blocks before ReactMarkdown sees them.
import './Chatbot.scss'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

// Interactive chart renderer (recharts) for the ```chart spec``` blocks emitted by the
// data-analysis tools. Hover = tooltip; click (in-bubble) = open full-screen.
const DA_PALETTE = ['#1963ae', '#2dd4bf', '#f59e0b', '#8b5cf6', '#ef4444', '#10b981', '#3b82f6', '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16', '#a855f7', '#6366f1'];
// Memoized chart wrapper. A finished message's chart JSON (content) is stable, so
// React.memo prevents re-render when a LATER message streams and re-renders the
// message list -- otherwise JSON.parse reruns, the spec object is recreated, and
// recharts' ResponsiveContainer is torn down mid-stream, making previously-rendered
// charts flicker / flash empty while the next reply comes in.
const ChartBlock = React.memo(function ChartBlock({ content, openLightbox }) {
    let spec;
    try { spec = JSON.parse(content); } catch (e) { return null; }
    return <DAChart spec={spec} onChartClick={openLightbox ? () => openLightbox(spec) : undefined} />;
});

// Interactive carrier/lane widget: a Carrier<->Lane toggle that switches BOTH a
// sortable table and a bar chart together. Rendered by DAChart for spec.type ===
// 'carrier_lane' (emitted by analyze_carrier_performance, which returns both views).
function CarrierLaneWidget({ spec, fullscreen, onChartClick }) {
    const viewNames = Object.keys(spec.views || {});
    const typeColor = (t) => DA_PALETTE[Math.max(0, viewNames.indexOf(t)) % DA_PALETTE.length];
    const dimTotal = {};
    viewNames.forEach(v => { const vd = spec.views[v] || { total: 0 }; dimTotal[v] = (typeof vd.total === 'number') ? vd.total : 0; });
    const fmt = (v) => (typeof v === 'number' ? v.toLocaleString() : v);
    const matrix = spec.matrix || null;
    const [tab, setTab] = useState((spec.default === 'matrix' || viewNames.indexOf(spec.default) < 0) ? 'matrix' : spec.default);
    const rowsFor = (v) => {
        const vd = spec.views[v] || { labels: [], values: [] };
        return (vd.labels || []).map((l, i) => ({ name: String(l), value: Number(vd.values ? vd.values[i] : 0) || 0 }));
    };
    const singleRows = (tab !== 'matrix') ? rowsFor(tab).sort((a, b) => b.value - a.value) : [];
    const pct = (val, dim) => (dimTotal[dim] ? ((val / dimTotal[dim] * 100).toFixed(1) + '%') : '0.0%');
    return (
        <div className={'cl-widget' + (fullscreen ? ' cl-widget--fullscreen' : '')}>
            <div className="cl-widget__head">
                {spec.title && <div className="cl-widget__title">{spec.title}</div>}
                <div className="cl-tabs" role="tablist">
                    <button type="button" role="tab" className={'cl-tabs__btn' + (tab === 'matrix' ? ' is-active' : '')} onClick={() => setTab('matrix')} aria-selected={tab === 'matrix'}>Heatmap</button>
                    {viewNames.map(v => (
                        <button key={v} type="button" role="tab" className={'cl-tabs__btn' + (tab === v ? ' is-active' : '')} onClick={() => setTab(v)} aria-selected={tab === v}>{v}</button>
                    ))}
                </div>
            </div>

            {tab === 'matrix' && matrix ? (
                <div className="cl-heat">
                    <div className="cl-heat__legend">
                        <span>{matrix.valueKey} per {matrix.colKey.toLowerCase()} x {matrix.rowKey.toLowerCase()}</span>
                        <span className="cl-heat__bar" />
                        <span>0 - {fmt(matrix.max)}</span>
                    </div>
                    <div className={'cl-heat__scroll' + (fullscreen ? ' cl-heat__scroll--fullscreen' : '')}>
                        <table className="cl-heat__table">
                            <thead>
                                <tr>
                                    <th className="cl-heat__corner">{matrix.rowKey} \ {matrix.colKey}</th>
                                    {matrix.cols.map(c => <th key={c} className="cl-heat__colh">{c}</th>)}
                                    <th className="cl-heat__tot">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {matrix.rows.map((lane, i) => (
                                    <tr key={lane}>
                                        <th className="cl-heat__rowh">{lane}</th>
                                        {matrix.cells[i].map((val, j) => {
                                            const a = matrix.max ? (val / matrix.max) : 0;
                                            return (
                                                <td key={j} className="cl-heat__cell" style={{ background: val ? ('rgba(25, 99, 174, ' + (0.1 + 0.8 * a).toFixed(2) + ')') : 'var(--cb-surface-3)', color: a > 0.5 ? '#fff' : 'var(--cb-ink)' }} title={lane + ' x ' + matrix.cols[j] + ': ' + fmt(val)}>
                                                    {val ? fmt(val) : ''}
                                                </td>
                                            );
                                        })}
                                        <td className="cl-heat__tot">{fmt(matrix.rowTotals[i])}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th className="cl-heat__rowh">Total</th>
                                    {matrix.colTotals.map((t, j) => <td key={j} className="cl-heat__tot">{fmt(t)}</td>)}
                                    <td className="cl-heat__tot cl-heat__grand">{fmt(matrix.total)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : null}

            {tab !== 'matrix' && (
                <>
                    <div className="cl-widget__tablewrap">
                        <table className="cl-table">
                            <thead>
                                <tr>
                                    <th className="cl-table__num">#</th>
                                    <th>{tab}</th>
                                    <th className="cl-table__num">Bookings</th>
                                    <th className="cl-table__num">Share</th>
                                </tr>
                            </thead>
                            <tbody>
                                {singleRows.map((r, i) => (
                                    <tr key={r.name}>
                                        <td className="cl-table__num">{i + 1}</td>
                                        <td>{r.name}</td>
                                        <td className="cl-table__num">{fmt(r.value)}</td>
                                        <td className="cl-table__num">{pct(r.value, tab)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className={'da-rechart' + (fullscreen ? ' da-rechart-fullscreen' : '')} onClick={!fullscreen && onChartClick ? onChartClick : undefined}>
                        <div className="da-rechart-title">{tab}</div>
                        <ResponsiveContainer width="100%" height={fullscreen ? 460 : 300}>
                            <BarChart data={singleRows} margin={{ top: 8, right: 16, left: 4, bottom: 40 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef1f4" />
                                <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} height={64} />
                                <YAxis tick={{ fontSize: 11 }} tickFormatter={fmt} />
                                <Tooltip formatter={fmt} />
                                <Bar dataKey="value" radius={[4, 4, 0, 0]}>{singleRows.map((_, i) => <Cell key={i} fill={typeColor(tab)} />)}</Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </>
            )}
        </div>
    );
}

function DAChart({ spec, fullscreen, onChartClick }) {
    if (spec && spec.type === 'carrier_lane') {
        return <CarrierLaneWidget spec={spec} fullscreen={fullscreen} onChartClick={onChartClick} />;
    }
    // Empty data (e.g. a 0-result analysis) would draw a chart shell with axes but
    // no bars — render nothing instead. The server also short-circuits these, but
    // guard here too so any empty spec stays clean.
    if (!spec || !Array.isArray(spec.labels) || !Array.isArray(spec.values) || spec.values.length === 0) return null;
    const data = spec.labels.map((l, i) => ({ name: String(l), value: Number(spec.values[i]) || 0 }));
    const title = spec.title || '';
    const fmt = (v) => (typeof v === 'number' ? v.toLocaleString() : v);
    const cells = data.map((_, i) => <Cell key={i} fill={DA_PALETTE[i % DA_PALETTE.length]} />);
    const wrap = (content, h) => (
        <div className={'da-rechart' + (fullscreen ? ' da-rechart-fullscreen' : '')} onClick={!fullscreen && onChartClick ? onChartClick : undefined}>
            {title && <div className="da-rechart-title">{title}</div>}
            <ResponsiveContainer width="100%" height={h}>{content}</ResponsiveContainer>
        </div>
    );
    if (spec.type === 'pie') {
        return wrap((
            <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={fullscreen ? 180 : 90}
                     label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {cells}
                </Pie>
                <Tooltip formatter={fmt} />
                <Legend />
            </PieChart>
        ), fullscreen ? 440 : 300);
    }
    const horizontal = spec.type === 'barh';
    const height = fullscreen ? 500 : (horizontal ? Math.max(220, data.length * 42 + 30) : 300);
    if (horizontal) {
        return wrap((
            <BarChart data={data} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmt} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={fullscreen ? 170 : 140} />
                <Tooltip formatter={fmt} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>{cells}</Bar>
            </BarChart>
        ), height);
    }
    return wrap((
        <BarChart data={data} margin={{ top: 8, right: 16, left: 4, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef1f4" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} height={64} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={fmt} />
            <Tooltip formatter={fmt} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>{cells}</Bar>
        </BarChart>
    ), height);
}
// >>> LANGGRAPH CHATBOT INTEGRATION — BMS Navigation Tools >>>
import { executeTool, setToolProgressReporter, setFlowStep, ACTION_VERIFY_BUTTONS, previewConsigneeCustomer, searchPartyOptions, getRefInfo } from './bmsTools';
// <<< LANGGRAPH CHATBOT INTEGRATION — BMS Navigation Tools <<<


// >>> Chatbot role gate (mirrors backend auth_middleware CHATBOT_ALLOWED_ROLES) >>>
// The chatbot only SHOWS for these profile_type_id values (the JWT 'profile' claim in the
// id_token). UX layer only - the backend gate (403) is authoritative. Keep in sync with
// app_external/.env CHATBOT_ALLOWED_ROLES. OP = M+R Operations (incl. consolidation work).
// const CHATBOT_ALLOWED_PROFILES = ['OP', 'ADMIN'];
// function getChatbotProfileClaim() {
//     try {
//         const tok = localStorage.getItem('id_token');
//         if (!tok) return null;
//         const part = tok.split('.')[1];
//         if (!part) return null;
//         const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
//         return payload.profile || null;
//     } catch (e) {
//         return null;
//     }
// }
// function isChatbotAllowedForCurrentUser() {
//     const p = (getChatbotProfileClaim() || '').trim().toUpperCase();
//     return !!p && CHATBOT_ALLOWED_PROFILES.some(x => x.toUpperCase() === p);
// }
// <<< Chatbot role gate >>>

// >>> Confirmation gate (Accept / Keep-edit / Reject) >>>
// Tools that PERSIST a change are intercepted in handleToolCall and shown to the
// user BEFORE the DOM mutation runs. Classification mirrors bmsTools.js so the
// gate and the executor's outcome-verification agree (ACTION_VERIFY_BUTTONS is
// imported from there - the single source of truth).

// >>> Approval mode (Ask before changes / Edit automatically) >>>
// User-level chatbot setting, toggled from the header. "ask" (default) keeps
// every confirmation card; "auto" lets flows run start-to-finish with no
// yes/no clicks: the run config below tells the server to skip its
// __FLOW_CONFIRM__ pre-flight park and HITL interrupt(), and the client-side
// save gate below auto-accepts (the card still renders, pre-resolved, as an
// audit record). ask_user_choice question cards ALWAYS keep asking — auto mode
// removes approvals, not the chatbot's questions for missing information.
const APPROVAL_MODE_KEY = 'bmsChatApprovalMode';
function readApprovalMode() {
    try {
        return window.localStorage.getItem(APPROVAL_MODE_KEY) === 'auto' ? 'auto' : 'ask';
    } catch (e) { return 'ask'; }
}
function writeApprovalMode(mode) {
    try {
        if (mode === 'auto') window.localStorage.setItem(APPROVAL_MODE_KEY, 'auto');
        else window.localStorage.removeItem(APPROVAL_MODE_KEY);
    } catch (e) { /* storage unavailable — state just won't persist */ }
}
// Read fresh (NOT from React state) so callbacks created earlier in the turn
// never act on a stale mode, and so no dependency arrays need to change.
function approvalModeConfig() {
    return { configurable: { approval_mode: readApprovalMode() } };
}
const APPROVAL_MODE_LABELS = { ask: 'Ask before changes', auto: 'Edit automatically' };

const PERSIST_TOOLS = new Set(['save_and_confirm', 'approve_booking']);
function isPersistToolCall(toolName, params) {
    const p = params || {};
    if (PERSIST_TOOLS.has(toolName)) return true;
    if (toolName === 'click_button') {
        return ACTION_VERIFY_BUTTONS.test(String(p.button_name || '').trim());
    }
    if (toolName === 'click_ref') {
        // Ref clicks gate on the referenced element's label (Save-class still
        // needs the card). Unknown/dead ref -> gate ON (fail-safe); the click
        // would only fail anyway.
        const info = getRefInfo(p.ref);
        return info ? ACTION_VERIFY_BUTTONS.test(String(info.label || '').trim()) : true;
    }
    if (toolName === 'handle_popup_confirm') {
        const a = String(p.action || '').toLowerCase();
        return a === 'ok' || a === 'confirm' || a === 'yes' || Boolean(p.button_text);
    }
    if (toolName === 'batch_form_actions') {
        const acts = Array.isArray(p.actions) ? p.actions : [];
        return acts.some(function (ac) {
            if (!ac) return false;
            const ap = ac.params || ac;
            if (ac.action === 'click_button') return ACTION_VERIFY_BUTTONS.test(String(ap.button_name || '').trim());
            if (ac.action === 'handle_popup_confirm') return String(ap.action || '').toLowerCase() !== 'cancel';
            return false;
        });
    }
    return false;
}
function humanizePersist(toolName, params) {
    const p = params || {};
    if (toolName === 'approve_booking') return 'Approve shipping order ' + (p.so_number || '');
    if (toolName === 'save_and_confirm') return 'Save (' + (p.button_name || 'Save') + ')';
    if (toolName === 'handle_popup_confirm') return 'Confirm popup';
    if (toolName === 'batch_form_actions') return 'Run batch actions';
    if (toolName === 'click_button') return 'Click "' + (p.button_name || '') + '"';
    if (toolName === 'click_ref') return 'Click [' + (p.ref || '') + '] "' + ((getRefInfo(p.ref) || {}).label || '') + '"';
    return toolName;
}
function rejectTextFor(toolName) {
    return 'RESULT: user_rejected -- the user rejected the ' + toolName
        + ' action at the confirmation gate. No changes were committed to the Shipping Order.'
        + ' Do NOT claim success, do NOT say Saved/Approved/Updated/Submitted, and do NOT quote any'
        + ' success toast. Acknowledge the decision in one short natural sentence, confirm the record'
        + ' is unchanged, and offer concrete alternatives. Do NOT silently retry the same commit unless'
        + ' the user explicitly asks.';
}
// Form-mutation tools whose values we capture during the turn so the user can
// edit them via Keep(edit) before the persist fires.
const FORM_MUTATION_TOOLS = new Set(['fill_form', 'select_option', 'set_date', 'toggle_checkbox', 'select_radio', 'fill_table_cell', 'select_proposed_sailing', 'fill_ref', 'select_ref']);
function buildFormChangeEntries(toolName, params) {
    const p = params || {};
    const out = [];
    const id = (label, name, extra) => toolName + '|' + (label || name || extra || '');
    if (toolName === 'fill_form') {
        (Array.isArray(p.fields) ? p.fields : []).forEach(function (f) {
            out.push({ id: id(f.label, f.name), tool: 'fill_form', label: f.label || f.name || 'field', name: f.name || '', value: f.value, displayValue: f.value, type: 'text' });
        });
    } else if (toolName === 'fill_ref') {
        // Ref fills capture via the ref's label so Keep(edit) can re-apply them.
        const info = getRefInfo(p.ref);
        const label = (info && info.label) || p.ref || 'field';
        out.push({ id: id(label, null, p.ref), tool: 'fill_ref', label: label, name: p.ref || '', value: p.value, displayValue: p.value, type: 'text', ref: p.ref });
    } else if (toolName === 'select_ref') {
        const info = getRefInfo(p.ref);
        const label = (info && info.label) || p.ref || 'select';
        out.push({ id: id(label, null, p.ref), tool: 'select_ref', label: label, name: p.ref || '', value: p.option_text, displayValue: p.option_text, type: 'text', ref: p.ref });
    } else if (toolName === 'select_option') {
        out.push({ id: id(p.label, p.name), tool: 'select_option', label: p.label || p.name || 'option', name: p.name || '', value: p.value, displayValue: p.value, type: 'text' });
    } else if (toolName === 'set_date') {
        out.push({ id: id(p.label, p.name), tool: 'set_date', label: p.label || p.name || 'date', name: p.name || '', value: p.value, displayValue: p.value, type: 'date' });
    } else if (toolName === 'toggle_checkbox') {
        out.push({ id: id(p.label, p.name), tool: 'toggle_checkbox', label: p.label || p.name || 'checkbox', name: p.name || '', value: p.checked, displayValue: p.checked ? 'on' : 'off', type: 'checkbox' });
    } else if (toolName === 'select_radio') {
        out.push({ id: id(p.label, p.name), tool: 'select_radio', label: p.label || p.name || 'radio', name: p.name || '', value: p.value, displayValue: p.value, type: 'radio' });
    } else if (toolName === 'fill_table_cell') {
        out.push({ id: id(p.column_name, null, p.row_identifier), tool: 'fill_table_cell', label: (p.column_name || '') + ' @ ' + (p.row_identifier || ''), name: '', value: p.value, displayValue: p.value, type: 'text', row_identifier: p.row_identifier, column_name: p.column_name });
    } else if (toolName === 'select_proposed_sailing') {
        out.push({ id: id('Approved Sailing'), tool: 'select_proposed_sailing', label: 'Approved Sailing', name: '', value: 'option ' + p.option_number, displayValue: 'option ' + p.option_number, type: 'text' });
    }
    return out;
}
// <<< Confirmation gate >>>

// Configuration - centralized for easy updates
const CONFIG = {
    // Auto-detect environment based on hostname
    isDevelopment: typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' ||
         window.location.hostname === '127.0.0.1'),

    MODE: 'azure',  // 'docker' uses localhost:8123, 'local' uses localhost:2024, 'azure' uses Azure Container App

    get mode() {
        return this.MODE;
    },

    // Azure Functions Key for production mode
    FUNCTIONS_KEY: localStorage.getItem('azure_functions_key') || '',

    // URLs based on mode
    get LANGGRAPH_URL() {
        // ben 20260910: same-origin reverse proxy — the browser calls this .NET site itself
        // (/api/chatbot, Controllers/ChatbotProxy.cs), which streams to the container app
        // configured server-side in appsettings.json "ChatbotProxy:Target". Office networks
        // only need the website domain; the azurecontainerapps.io URL is no longer in this file.
        if (!this.isDevelopment) {
            return '/api/chatbot';
        }

        // Manual azure override: go through the same-origin proxy even in development
        // (the target is chosen server-side, so dev azure mode no longer hardcodes a URL)
        if (this.mode === 'azure') {
            return '/api/chatbot';
        }

        // Development: Use local or docker based on mode (direct, bypasses the proxy)
        return this.mode === 'docker'
            ? 'http://localhost:8123'  // Docker LangGraph server
            : 'http://localhost:2024'; // Local LangGraph server
    },

    // Assistant ID
    ASSISTANT_ID: 'agent'
};

const LANGGRAPH_URL = CONFIG.LANGGRAPH_URL;
const ASSISTANT_ID = CONFIG.ASSISTANT_ID;
const CONNECTION_STATUS_COPY = {
    idle: 'Offline',
    connecting: 'Connecting to assistant...',
    online: 'Online',
    error: 'Offline - retrying...'
};

// Starter-prompt chips are fully backend-driven: the LIVE list is fetched on mount
// from ${LANGGRAPH_URL}/suggestions (served by the langgraph docker image). The
// backend can hide ALL chips by returning [] (CHATBOT_SUGGESTIONS_ENABLED=false in
// app_external/.env). Intentionally empty here too, so an unreachable backend also
// shows no chips — there is no hardcoded fallback chip anymore.
const DEFAULT_SUGGESTED_PROMPTS = [];

// Only these suggestion agents surface in the welcome UI. Analytics (da) and
// tracking (p44/dnd) suggestions are deliberately filtered out client-side.
const WELCOME_AGENTS = ['meta', 'bms', 'rag'];

// Which glyph a suggestion chip shows, per backend agent key.
const AGENT_CHIP_ICON = { meta: 'sparkle', bms: 'booking', rag: 'doc' };

// Welcome-screen capability cards. Two only — Bookings (BMS operations) and
// Documents (manual retrieval with page-cited answers). Each card sends its
// prompt exactly like a suggested chip does.
const CAPABILITY_CARDS = [
    {
        key: 'bookings',
        icon: 'booking',
        title: 'Bookings',
        desc: 'Find, open, and check shipping orders in BMS.',
        prompt: 'Show me the most recent shipping orders'
    },
    {
        key: 'documents',
        icon: 'doc',
        title: 'Documents',
        desc: 'Search the BMS manual with page-cited answers.',
        prompt: 'How do I approve a booking?'
    }
];

// Time-of-day greeting for the welcome hero.
const getWelcomeGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
};

// Document/file glyph — used for citation pills, PDF attachment chips, and the
// source-panel header (replaces the 📄 emoji).
function FileIcon({ className }) {
    return (
        <span className={className} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="8" y1="13" x2="16" y2="13" />
                <line x1="8" y1="17" x2="13" y2="17" />
            </svg>
        </span>
    );
}

// ===== Chain-of-thought "action timeline" =====
// Every step of a turn — routing phases, manual retrieval, browser actions,
// server tools — becomes one chronological step entry:
//   { kind: 'phase'|'tool'|'agent', label, icon, status: 'running'|'ok'|'error',
//     params, output_text, detail, markdown, startedAt, durationMs }
// The SAME component renders it live (while streaming) and after the turn ends,
// so "what is it doing now" and "what did it do" always look identical.

// Raw tool name -> { label, icon }: human title + which glyph to show.
const TOOL_META = {
    retrieve_chroma_docs:     { label: 'Search the manual',        icon: 'search' },
    navigate_to_page:         { label: 'Open page',                icon: 'compass' },
    read_current_page:        { label: 'Read current screen',      icon: 'eye' },
    click_button:             { label: 'Click button',             icon: 'pointer' },
    click_list_item:          { label: 'Select list item',         icon: 'pointer' },
    switch_so_tab:            { label: 'Switch booking tab',       icon: 'tab' },
    read_so_tab:              { label: 'Read booking tab',         icon: 'eye' },
    read_so_header:           { label: 'Read booking header',      icon: 'eye' },
    read_so:                  { label: 'Read booking',             icon: 'eye' },
    open_shipping_order:      { label: 'Open shipping order',      icon: 'open' },
    open_so_for_edit:         { label: 'Open booking for editing', icon: 'edit' },
    apply_custom_filter:      { label: 'Apply filter',             icon: 'filter' },
    apply_filters:            { label: 'Apply filters',            icon: 'filter' },
    paginate:                 { label: 'Change list page',         icon: 'stack' },
    search_booking:           { label: 'Search bookings',          icon: 'search' },
    read_form:                { label: 'Read form',                icon: 'eye' },
    fill_form:                { label: 'Fill form fields',         icon: 'edit' },
    fill_table_cell:          { label: 'Fill table cell',          icon: 'edit' },
    batch_form_actions:       { label: 'Batch form actions',       icon: 'edit' },
    select_option:            { label: 'Select option',            icon: 'edit' },
    set_date:                 { label: 'Set date',                 icon: 'edit' },
    toggle_checkbox:          { label: 'Toggle checkbox',          icon: 'edit' },
    select_radio:             { label: 'Select radio',             icon: 'edit' },
    handle_popup_confirm:     { label: 'Confirm popup',            icon: 'popup' },
    dismiss_all_popups:       { label: 'Dismiss popups',           icon: 'popup' },
    poll_popups:              { label: 'Check for popups',         icon: 'popup' },
    show_list:                { label: 'Show list',                icon: 'stack' },
    select_proposed_sailing:  { label: 'Select proposed sailing',  icon: 'ship' },
    save_and_confirm:         { label: 'Save and confirm',         icon: 'save' },
    approve_booking:          { label: 'Approve booking',          icon: 'check' },
    read_page_interactive:    { label: 'Scan page elements',       icon: 'eye' },
    click_ref:                { label: 'Click element',            icon: 'pointer' },
    fill_ref:                 { label: 'Fill field by ref',        icon: 'edit' },
    select_ref:               { label: 'Select dropdown option',   icon: 'edit' },
    // Server-side tools (LangGraph): analytics, schedules, tariffs.
    analyze_booking_volume:   { label: 'Analyze booking volume',   icon: 'chart' },
    analyze_shipment_status:  { label: 'Analyze shipment status',  icon: 'chart' },
    analyze_carrier_performance: { label: 'Analyze carrier performance', icon: 'chart' },
    analyze_revenue:          { label: 'Analyze revenue',          icon: 'chart' },
    fetch_future_sailings:    { label: 'Fetch future sailings',    icon: 'ship' },
    fetch_vessel_schedule:    { label: 'Fetch vessel schedule',    icon: 'ship' },
    fetch_shipment_sailing:   { label: 'Fetch shipment sailing',   icon: 'ship' },
    fetch_shipment_sailing_history: { label: 'Fetch sailing history', icon: 'ship' },
    fetch_dnd_rates:          { label: 'Fetch D&D rates',          icon: 'search' },
};

// Conceptual phase -> icon + a short "what happens here" subtitle shown as the
// row preview. Phases are the non-tool steps of a turn (routing, retrieval, answer).
const PHASE_META = {
    'Analyzing your question':       { icon: 'brain',   sub: 'Understanding the request and routing it' },
    'Retrieving relevant documents': { icon: 'search',  sub: 'Fetching matching manual sections' },
    'Searching knowledge base':      { icon: 'search',  sub: 'Dense + keyword search over the manual' },
    'Generating response':           { icon: 'pen',     sub: 'Composing the answer' },
    'Knowledge Retrieval Agent':     { icon: 'bot',     sub: 'Manual lookup specialist' },
    'BMS Operations Agent':          { icon: 'bot',     sub: 'Live booking operations' },
    'Analytics Agent':               { icon: 'bot',     sub: 'Data analysis and charts' },
    'Schedule & Vessel Agent':       { icon: 'bot',     sub: 'Schedules and vessel data' },
    'D&D Tariff Agent':              { icon: 'bot',     sub: 'Detention & demurrage tariffs' },
    'Specialist Agent':              { icon: 'bot',     sub: 'Domain specialist' },
};

// Fallback title for a tool missing from TOOL_META: read_so_header -> Read So Header.
const titleizeToolName = (name) => String(name || '')
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ') || 'Action';

// One-line, human-readable digest of WHAT a tool call targets, from its params.
// Shown on the step row without expanding ("Open page → Booking Approval").
function describeToolParams(toolName, params) {
    const p = params || {};
    const val = (v) => {
        if (v === undefined || v === null || v === '') return null;
        const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
        return s.length > 48 ? `${s.slice(0, 45)}…` : s;
    };
    switch (toolName) {
        case 'navigate_to_page':        return val(p.page_name || p.page || p.menu);
        case 'read_current_page':       return val(p.tab_name) && `tab ${val(p.tab_name)}`;
        case 'click_button':            return val(p.button_name);
        case 'click_list_item':         return val(p.item_text || p.label || p.name);
        case 'switch_so_tab':           return val(p.tab_name);
        case 'read_so_tab':             return val(p.so_number) && val(p.tab_name) ? `${val(p.so_number)} · ${val(p.tab_name)}` : (val(p.tab_name) || val(p.so_number));
        case 'read_so_header':          return val(p.so_number);
        case 'read_so':                 return val(p.so_number);
        case 'open_shipping_order':     return val(p.so_number);
        case 'open_so_for_edit':        return val(p.so_number);
        case 'search_booking':          return val(p.keyword || p.so_number || p.query);
        case 'retrieve_chroma_docs':    return val(p.query);
        case 'apply_custom_filter':     return val(p.filter_name || p.filter_label);
        case 'apply_filters':           return Object.keys(p).length ? `${Object.keys(p).length} filter${Object.keys(p).length === 1 ? '' : 's'}` : null;
        case 'paginate':                return val(p.page || p.page_number || p.direction);
        case 'fill_form':               return Array.isArray(p.fields) ? `${p.fields.length} field${p.fields.length === 1 ? '' : 's'}` : val(p.fields);
        case 'fill_table_cell':         return val(p.row || p.row_index) && val(p.column || p.column_name) ? `row ${val(p.row || p.row_index)}, ${val(p.column || p.column_name)}` : val(p.column || p.column_name);
        case 'batch_form_actions':      return Array.isArray(p.actions) ? `${p.actions.length} action${p.actions.length === 1 ? '' : 's'}` : null;
        case 'select_option':           return val(p.label || p.name) && val(p.value) ? `${val(p.label || p.name)} = ${val(p.value)}` : val(p.value);
        case 'set_date':                return val(p.label || p.name) ? `${val(p.label || p.name)} = ${val(p.value || p.date)}` : val(p.value || p.date);
        case 'toggle_checkbox':         return val(p.label || p.name);
        case 'select_radio':            return val(p.label || p.name);
        case 'select_proposed_sailing': return val(p.vessel || p.sailing || p.option);
        case 'show_list':               return val(p.page_name || p.page);
        case 'poll_popups':             return null;
        case 'read_form':               return null;
        case 'click_ref':               return val(p.ref) && (getRefInfo(p.ref) || {}).label ? `[${val(p.ref)}] ${val((getRefInfo(p.ref) || {}).label)}` : val(p.ref);
        case 'fill_ref':                return val(p.ref) && val(p.value) ? `${val(p.ref)} = ${val(p.value)}` : val(p.ref);
        case 'select_ref':              return val(p.ref) && val(p.option_text) ? `${val(p.ref)} = ${val(p.option_text)}` : val(p.ref);
        default: {
            try {
                const s = JSON.stringify(p);
                return (!s || s === '{}') ? null : (s.length > 48 ? `${s.slice(0, 45)}…` : s);
            } catch (e) { return null; }
        }
    }
}

// First meaningful line(s) of a tool's output, for the row preview. Long outputs
// (retrieved manual chunks, screen snapshots) collapse to a short digest; the
// full text stays available in the expandable body.
function digestToolOutput(text, max = 110) {
    if (!text) return '';
    let t = String(text)
        .replace(/^[a-z_0-9]+:\s*/i, '')      // leading "read_so_tab: "
        .replace(/^Tab:\s*[^\n]*\n?/i, '');    // "Tab: Booking Approval"
    // Raw __TOOL_CALL__ JSON echoes read as pure noise on the preview row —
    // they carry no human-relevant digest, so show nothing rather than JSON.
    if (/\{"tool_call"/.test(t.slice(0, 40))) return '';
    t = t.replace(/\s*\n\s*/g, ' ').trim();
    if (!t) return '';
    if (t.length > max) t = `${t.slice(0, max - 1).trimEnd()}…`;
    return t;
}

const isToolStepError = (outputText) => /^error\b/i.test(String(outputText || '')) || /\bnot found\b/i.test(String(outputText || ''));

// getToolResultText prefixes outputs with the raw tool name ("read_so_tab: ...")
// or "Error executing X: ..." — strip those for human display in the timeline.
const prettyToolOutput = (toolName, text) => String(text || '')
    .replace(new RegExp(`^Error executing ${toolName}:\\s*`, 'i'), 'Error: ')
    .replace(new RegExp(`^${toolName}:\\s*`, 'i'), '')
    .trim();

// Synthesized gate outcomes get a human phrasing on the step row.
const friendlyOutcome = (text) => {
    const t = String(text || '').trim();
    if (/^RESULT:\s*user_rejected$/i.test(t)) return 'cancelled — no changes made';
    if (/^RESULT:\s*user_kept$/i.test(t)) return 'applied with your edits';
    return null;
};

// Named glyphs for step badges. Kept as one component so phase and tool rows
// share the exact same icon language.
function StepIcon({ name }) {
    const common = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
        case 'search':  return <svg {...common}><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" /></svg>;
        case 'eye':     return <svg {...common}><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></svg>;
        case 'pointer': return <svg {...common}><path d="M9 3v10.5" /><path d="M9 13.5 5.6 10a1.8 1.8 0 0 0-2.6 2.5l5.5 6.4A5 5 0 0 0 12.3 21h3.2a5 5 0 0 0 5-5v-3a2 2 0 0 0-4 0" /><path d="M13 13V5a1.5 1.5 0 0 0-3 0" /><path d="M9 5.5V4a1.5 1.5 0 0 1 3 0" /></svg>;
        case 'compass': return <svg {...common}><circle cx="12" cy="12" r="9" /><polygon points="15.5 8.5 13.5 13.5 8.5 15.5 10.5 10.5 15.5 8.5" /></svg>;
        case 'edit':    return <svg {...common}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>;
        case 'tab':     return <svg {...common}><rect x="3" y="4" width="18" height="16" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="4" x2="9" y2="9" /></svg>;
        case 'open':    return <svg {...common}><path d="M14 3h7v7" /><path d="M21 3 11 13" /><path d="M19 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" /></svg>;
        case 'filter':  return <svg {...common}><polygon points="22 3 2 3 10 12.5 10 19 14 21 14 12.5 22 3" /></svg>;
        case 'stack':   return <svg {...common}><rect x="3" y="3" width="18" height="6" rx="1.5" /><rect x="3" y="15" width="18" height="6" rx="1.5" /></svg>;
        case 'popup':   return <svg {...common}><rect x="8" y="3" width="13" height="13" rx="2" /><path d="M16 16v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h3" /></svg>;
        case 'ship':    return <svg {...common}><path d="M4 18.5 3 11l9-3 9 3-1 7.5" /><path d="M3 11v-2l9-3 9 3v2" /><path d="M12 8v11" /><path d="M2.5 19c1.6 0 1.6 1.2 3.2 1.2s1.6-1.2 3.2-1.2 1.6 1.2 3.2 1.2 1.5-1.2 3.1-1.2 1.6 1.2 3.2 1.2 1.6-1.2 3.1-1.2" /></svg>;
        case 'save':    return <svg {...common}><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>;
        case 'check':   return <svg {...common}><path d="M22 11.1V12a10 10 0 1 1-5.9-9.1" /><polyline points="22 4 12 14 9 11" /></svg>;
        case 'brain':   return <svg {...common}><path d="M9.5 2A2.5 2.5 0 0 0 7 4.5v.6A3.5 3.5 0 0 0 4.5 8.5c0 .6.15 1.2.42 1.7A3.5 3.5 0 0 0 3 13.5 3.5 3.5 0 0 0 6.5 17H7v.5A2.5 2.5 0 0 0 9.5 20 2.5 2.5 0 0 0 12 17.5v-13A2.5 2.5 0 0 0 9.5 2z" /><path d="M14.5 2A2.5 2.5 0 0 1 17 4.5v.6a3.5 3.5 0 0 1 2.5 3.4c0 .6-.15 1.2-.42 1.7a3.5 3.5 0 0 1 1.92 3.3A3.5 3.5 0 0 1 17.5 17H17v.5a2.5 2.5 0 0 1-2.5 2.5A2.5 2.5 0 0 1 12 17.5v-13A2.5 2.5 0 0 1 14.5 2z" /></svg>;
        case 'chart':   return <svg {...common}><line x1="4" y1="20" x2="20" y2="20" /><rect x="6" y="12" width="3.5" height="6" rx="0.75" /><rect x="11" y="7" width="3.5" height="11" rx="0.75" /><rect x="16" y="10" width="3.5" height="8" rx="0.75" /></svg>;
        case 'pen':     return <svg {...common}><path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /><path d="M2 2l7.586 7.586" /><circle cx="11" cy="11" r="2" /></svg>;
        case 'bot':     return <svg {...common}><rect x="4" y="8" width="16" height="12" rx="2" /><path d="M12 8V4" /><circle cx="12" cy="3" r="1" /><line x1="9" y1="13" x2="9" y2="13.01" /><line x1="15" y1="13" x2="15" y2="13.01" /><path d="M9 17h6" /></svg>;
        case 'booking': return <svg {...common}><rect x="3" y="5" width="18" height="16" rx="2" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="8" y1="3" x2="8" y2="7" /><line x1="16" y1="3" x2="16" y2="7" /><polyline points="9 15.5 11 17.5 15 13.5" /></svg>;
        case 'doc':     return <svg {...common}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="13" y2="17" /></svg>;
        case 'sparkle': return <svg {...common}><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" /><path d="M19 15l.9 2.4L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.6L19 15z" /></svg>;
        case 'shield':  return <svg {...common}><path d="M12 22s8-3.5 8-10V5l-8-3-8 3v7c0 6.5 8 10 8 10z" /><polyline points="8.5 11.5 11 14 15.5 9.5" /></svg>;
        case 'alert':   return <svg {...common}><circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="12.5" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>;
        case 'zoomin':  return <svg {...common}><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" /></svg>;
        case 'zoomout': return <svg {...common}><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" /><line x1="8" y1="11" x2="14" y2="11" /></svg>;
        case 'left':    return <svg {...common}><polyline points="15 18 9 12 15 6" /></svg>;
        case 'right':   return <svg {...common}><polyline points="9 18 15 12 9 6" /></svg>;
        case 'down':    return <svg {...common}><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></svg>;
        case 'newchat': return <svg {...common}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /><line x1="13" y1="7" x2="17" y2="11" /></svg>;
        default:        return <svg {...common}><polyline points="20 6 9 17 4 12" /></svg>;
    }
}

// Format a step duration for the row's right edge: sub-second shows "ms".
const formatStepDuration = (ms) => {
    if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return null;
    if (ms < 950) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
};

// One row of the action timeline — used BOTH in the live panel (status
// 'running' rows show a spinner and a ticking duration) and in the finalized
// "Thought for X" section. The always-visible line is: icon + title + preview;
// expanding reveals full input/output.
function StepRow({ step, sources, detail, markdown = false }) {
    const hasBody = Boolean(step.output_text || detail || (sources && sources.length));
    const [open, setOpen] = useState(false);
    const running = step.status === 'running';
    const isError = step.status === 'error';
    const duration = running ? (Date.now() - (step.startedAt || Date.now())) : step.durationMs;

    const toggle = () => { if (hasBody) setOpen((o) => !o); };

    return (
        <div className={`cot-step${isError ? ' is-error' : ''}${running ? ' is-running' : ''}${open ? ' is-open' : ''}`}>
            <div
                className={`cot-step__row${hasBody ? ' is-expandable' : ''}`}
                onClick={toggle}
                role={hasBody ? 'button' : undefined}
                tabIndex={hasBody ? 0 : undefined}
                aria-expanded={hasBody ? open : undefined}
                onKeyDown={(e) => { if (hasBody && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggle(); } }}
            >
                <span className="cot-step__badge" aria-hidden="true">
                    <StepIcon name={step.icon} />
                    {!running && (
                        <span className={`cot-step__tick${isError ? ' is-err' : ' is-ok'}`}>
                            {isError ? (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round">
                                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            ) : (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            )}
                        </span>
                    )}
                    {running && <span className="cot-step__spinner" />}
                </span>
                <span className="cot-step__text">
                    <span className="cot-step__title">{step.label}</span>
                    {step.preview && <span className="cot-step__preview">{step.preview}</span>}
                </span>
                {/* Ticks live on a running row: nowTick re-renders every 150ms and
                    duration recomputes from startedAt, so the counter counts up. */}
                {duration != null && duration > 0 && (
                    <span className="cot-step__time">{formatStepDuration(duration)}</span>
                )}
                {hasBody && (
                    <svg className={`cot-step__chevron${open ? ' is-open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                )}
            </div>
            {open && hasBody && (
                <div className="cot-step__body">
                    {step.params != null && Object.keys(step.params).length > 0 && (
                        <div className="cot-step__block">
                            <span className="cot-step__label">input</span>
                            <pre>{JSON.stringify(step.params, null, 2)}</pre>
                        </div>
                    )}
                    {detail && (
                        <div className="cot-step__block">
                            <span className="cot-step__label">{markdown ? 'answer' : 'detail'}</span>
                            {markdown
                                ? <div className="cot-step__markdown"><ReactMarkdown>{detail}</ReactMarkdown></div>
                                : <pre>{detail}</pre>}
                        </div>
                    )}
                    {step.output_text && (
                        <div className="cot-step__block">
                            <span className="cot-step__label">{isError ? 'error' : 'result'}</span>
                            <pre>{step.output_text}</pre>
                        </div>
                    )}
                    {sources && sources.length > 0 && (
                        <div className="cot-step__block">
                            <span className="cot-step__label">sources</span>
                            <ul className="cot-step__sources">
                                {sources.map((s, i) => (
                                    <li key={i}>
                                        {s.source || 'Unknown'}{s.page_no && s.page_no !== 'N/A' ? ` — p.${s.page_no}` : ''}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// The full timeline of a turn. `steps` is the chronological capture; citations
// attach to retrieval phases; reasoning text attaches to the analysis phase;
// intermediate multi-agent answers attach to their agent phase. `active` marks
// the LIVE render (turn still in flight) — it appends a heartbeat row whenever
// no step is running (the silent LLM round-trips between tool calls / before
// the first token), so the timeline never sits fully static and read as frozen.
function ActionTimeline({ steps, citations, reasoningText, agentSteps, active = false }) {
    if (!steps || steps.length === 0) return null;
    const agentTextByLabel = {};
    (agentSteps || []).forEach((s) => { if (s && s.label) agentTextByLabel[s.label] = s.text; });
    const showHeartbeat = active && !steps.some((s) => s.status === 'running');
    return (
        <div className="cot-timeline">
            {steps.map((step, idx) => {
                // Sources attach to retrieval: either a retrieval phase or the
                // "Search the manual" tool step that produced the citations.
                const isRetrieval = (step.kind === 'phase' && /retriev|knowledge base|manual/i.test(step.label || ''))
                    || (step.kind === 'tool' && /retriev|chroma|manual|knowledge/i.test(step.toolName || ''));
                const isAnalysis = step.kind === 'phase' && /analyz/i.test(step.label || '');
                const agentText = agentTextByLabel[step.label];
                return (
                    <StepRow
                        key={step.id || `step-${idx}`}
                        step={step}
                        sources={isRetrieval ? (citations || []) : null}
                        detail={isAnalysis && reasoningText ? reasoningText : (agentText || null)}
                        markdown={Boolean(agentText)}
                    />
                );
            })}
            {showHeartbeat && (
                <div className="cot-step cot-step--heartbeat" aria-hidden="true">
                    <div className="cot-step__row">
                        <span className="cot-step__badge">
                            <span className="cot-step__heartbeat-dots"><span /><span /><span /></span>
                        </span>
                        <span className="cot-step__text">
                            <span className="cot-step__title">Thinking</span>
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}

// In-chat Accept / Keep(edit) / Reject confirmation card shown before a persist
// tool runs. Rendered as its own message bubble (kind === 'confirmation') so it
// never passes through ReactMarkdown.
function ConfirmationCard({ payload, edit, resolvedChoice, onAccept, onKeep, onReject, onApplyEdits, onCancelEdit }) {
    const title = humanizePersist(payload.tool_name, payload.params);
    const list = edit ? edit.items : (payload.prepared || []);
    const disabled = !!resolvedChoice;
    const [edits, setEdits] = useState({});
    const setItem = (id, v) => setEdits(prev => ({ ...prev, [id]: v }));
    const editedItems = edit
        ? list.map(it => ({ ...it, editValue: edits[it.id] !== undefined ? edits[it.id] : it.editValue }))
              .filter(it => String(it.editValue) !== String(it.value))
        : [];
    return (
        <div className={'confirm-card' + (resolvedChoice ? ' is-resolved' : '')}>
            <div className="confirm-card__title">
                <span className="confirm-card__title-icon"><StepIcon name="shield" /></span>
                {title}
            </div>
            <div className="confirm-card__body">
                {list.length > 0 ? (
                    <ul className="confirm-card__changes">
                        {list.map(c => (
                            <li key={c.id} className="confirm-card__change">
                                <span className="confirm-card__change-label">{c.label || c.name}</span>
                                {edit ? (
                                    c.type === 'checkbox'
                                      ? <select className="confirm-card__input" value={edits[c.id] !== undefined ? edits[c.id] : c.editValue} onChange={e => setItem(c.id, e.target.value === 'true')} disabled={disabled}>
                                            <option value={true}>on</option>
                                            <option value={false}>off</option>
                                        </select>
                                      : <input className="confirm-card__input" type={c.type === 'date' ? 'date' : c.type === 'number' ? 'number' : 'text'} value={edits[c.id] !== undefined ? edits[c.id] : c.editValue} onChange={e => setItem(c.id, e.target.value)} disabled={disabled} />
                                ) : (
                                    <span className="confirm-card__change-value">{String(c.displayValue != null ? c.displayValue : c.value)}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                ) : <span className="confirm-card__body-text">This will commit the record to BMS. Please review before proceeding.</span>}
            </div>
            <div className="confirm-card__actions">
                {payload.autoApproved ? (
                    <span className="confirm-card__auto-note">Auto-accepted — Edit automatically mode</span>
                ) : !edit ? (
                    <>
                        <button type="button" className="confirm-btn confirm-btn--reject" disabled={disabled} onClick={onReject}>Reject</button>
                        <button type="button" className="confirm-btn confirm-btn--keep" disabled={disabled || list.length === 0} onClick={onKeep}>Keep (edit)</button>
                        <button type="button" className="confirm-btn confirm-btn--accept" disabled={disabled} onClick={onAccept}>Accept</button>
                    </>
                ) : (
                    <>
                        <button type="button" className="confirm-btn confirm-btn--keep" onClick={onCancelEdit}>Cancel</button>
                        <button type="button" className="confirm-btn confirm-btn--accept" onClick={() => onApplyEdits(editedItems)}>Apply &amp; keep</button>
                    </>
                )}
            </div>
        </div>
    );
}

// ===== Flow pre-flight card v2 — editable parameter rows =====
// In-chat pre-flight confirmation card for DETERMINISTIC FLOWS. Before the first
// mutation of a flow (create/details/amend/confirm/approve) the server ends its
// turn with a __FLOW_CONFIRM__:{...} marker; the card (FlowConfirmCard below)
// shows the full parameter summary with Confirm / Cancel buttons. The decision
// travels back as a normal __TOOL_RESULT__ envelope (tool_name
// '__flow_confirm__'), so the flow engine resumes deterministically on Confirm
// and aborts on Cancel. v2 payloads additionally make the card EDITABLE (see
// FlowConfirmCard). Helpers first:
// Strip a formatted party display value down to the plain name the site party
// search expects: "RIESS KELOMAT GMBH [RIESSKEL] (office VIE)" ->
// "RIESS KELOMAT GMBH" (the decorations _party_display/precheck add).
function stripPartyDecorations(v) {
    return String(v || '')
        .replace(/\s*\[[^\]]*\]/, '')          // strip [CODE]
        .replace(/\s*\(office [^)]*\)/i, '')    // strip (office X)
        .trim();
}
function formatPartyDisplay(name, code, office) {
    let out = String(name || '').trim();
    if (code) out += ` [${code}]`;
    if (office) out += ` (office ${office})`;
    return out;
}

// A newly appended flow/choice card supersedes every earlier unanswered card of
// the same family: mark them stale so they render read-only. The pending-card
// refs only ever hold the NEWEST card's promise — without this, a late click on
// an old card would resolve (and envelope!) the wrong, newer turn.
const markStaleFlowCards = (msgs) => msgs.map((m) => (
    (m.kind === 'flow_confirmation' || m.kind === 'user_choice') && !m.resolvedChoice && !m.stale
        ? { ...m, stale: true }
        : m
));

// Cosmetic user bubble for a card edit ("✏️ Consignee → WING FAT TRADING CO LTD
// [WINGFATHKG]") — deliberately NOT the raw __FLOW_EDIT__ marker JSON, which
// must never render verbatim in chat (the server sees only the raw marker).
function prettyEditLabel(item, edit) {
    const label = (item && item.label) || (edit && edit.slot) || 'value';
    let val = '';
    if (edit && edit.kind === 'party') {
        val = edit.code ? formatPartyDisplay(edit.name, edit.code, edit.office) : String(edit.name || '');
    } else if (edit) {
        val = String(edit.value != null ? edit.value : '');
    }
    return `✏️ ${label} → ${val}`;
}

// Searchable party picker for v2 flow-card rows (user ask: "if we wanna change
// the consignee, it should be a drop down list"). Typeahead over the SITE's own
// party API (searchPartyOptions in bmsTools — same endpoint/auth/scoping as the
// SO Header dropdowns), so every offered row is a row the form can accept and
// its ids are site-space. debounce/minChars/segment come from the card payload
// (item.search, server-controlled, with local fallbacks). Picking a row fires
// onPick with {slot,kind:'party',name,code,office,party_id}; Enter with no
// match (or the "Use as typed" button) fires onPick with the typed name only —
// the free-text path the server re-resolves. An API failure (null) degrades to
// an inline hint; typing + Enter keeps working.
function PartySelect({ item, disabled, onPick }) {
    const search = (item && item.search) || {};
    const minChars = Number(search.minChars) > 0 ? Number(search.minChars) : 2;
    const debounceMs = Number(search.debounceMs) > 0 ? Number(search.debounceMs) : 400;
    const segment = search.segment || '';
    const [query, setQuery] = useState(stripPartyDecorations(item.value));
    const [dirty, setDirty] = useState(false);   // user typed — the prefill never auto-searches
    const [picked, setPicked] = useState(false); // a pick landed — don't re-search the picked name
    const [rows, setRows] = useState(null);      // null = no list; [] = answered-but-empty
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [open, setOpen] = useState(false);
    const [hi, setHi] = useState(0);             // highlighted row (ArrowUp/Down + Enter)
    const seqRef = useRef(0);

    useEffect(() => {
        if (!dirty || picked) return undefined;
        const q = query.trim();
        if (!segment || q.length < minChars) {
            setRows(null); setOpen(false); setFailed(false); setLoading(false);
            return undefined;
        }
        const seq = ++seqRef.current;
        const timer = setTimeout(() => {
            setLoading(true); setFailed(false);
            searchPartyOptions(segment, q).then((opts) => {
                if (seq !== seqRef.current) return; // a newer keystroke won — drop this reply
                setLoading(false);
                if (opts === null) { setFailed(true); setRows(null); setOpen(true); return; }
                setRows(opts);
                setHi(0);
                setOpen(true);
            });
        }, debounceMs);
        return () => clearTimeout(timer);
    }, [query, dirty, picked, segment, minChars, debounceMs]);

    const pickRow = (r) => {
        if (disabled || !r) return;
        setQuery(String(r.name || query));
        setRows(null); setOpen(false); setPicked(true);
        onPick({ slot: item.slot, kind: 'party', name: r.name, code: r.code, office: r.office, party_id: r.party_id });
    };
    const pickTyped = () => {
        if (disabled) return;
        const name = query.trim();
        if (!name) return;
        setRows(null); setOpen(false); setPicked(true);
        onPick({ slot: item.slot, kind: 'party', name: name });
    };

    const onKeyDown = (e) => {
        if (disabled) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            if (!open || !Array.isArray(rows) || rows.length === 0) return;
            e.preventDefault();
            setHi(e.key === 'ArrowDown' ? Math.min(hi + 1, rows.length - 1) : Math.max(hi - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (open && Array.isArray(rows) && rows.length > 0) pickRow(rows[hi] || rows[0]);
            else pickTyped();
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    const showList = open && !failed && !loading && Array.isArray(rows) && rows.length > 0;
    return (
        <div className="flow-edit__party">
            <input
                className="confirm-card__input flow-edit__party-input"
                type="text"
                value={query}
                disabled={disabled}
                placeholder={'Search ' + (item.label || 'party') + '…'}
                aria-label={'Edit ' + (item.label || item.slot)}
                autoComplete="off"
                onChange={(e) => { setDirty(true); setPicked(false); setQuery(e.target.value); }}
                onKeyDown={onKeyDown}
            />
            {open && failed && (
                <div className="flow-edit__party-msg">Party search unavailable — type the exact name and press Enter.</div>
            )}
            {open && !failed && loading && <div className="flow-edit__party-msg">Searching…</div>}
            {open && !failed && !loading && Array.isArray(rows) && rows.length === 0 && (
                <div className="flow-edit__party-msg">No match — press Enter to use “{query.trim()}” as typed.</div>
            )}
            {showList && (
                <div className="flow-edit__party-list" role="listbox" aria-label={(item.label || 'party') + ' options'}>
                    {rows.map((r, i) => (
                        <button
                            key={(r.party_id != null ? String(r.party_id) : 'row') + '-' + i}
                            type="button"
                            role="option"
                            aria-selected={i === hi}
                            className={'flow-edit__party-row' + (i === hi ? ' is-hi' : '')}
                            disabled={disabled}
                            onMouseEnter={() => setHi(i)}
                            onClick={() => pickRow(r)}
                        >
                            <span className="flow-edit__party-name">{r.name}{r.code ? ` (${r.code})` : ''}</span>
                            {r.addr ? <span className="flow-edit__party-addr">{r.addr}</span> : null}
                        </button>
                    ))}
                </div>
            )}
            {dirty && !picked && query.trim() ? (
                <button type="button" className="flow-edit__use-typed" disabled={disabled} onClick={pickTyped}>
                    Use “{query.trim()}” as typed
                </button>
            ) : null}
        </div>
    );
}

// Inline one-field text edit for v2 flow-card rows: value + pencil → input;
// Enter/blur commits (an unchanged or emptied draft commits nothing); Escape
// reverts. Native input only — no new dependencies.
function TextInlineEdit({ label, value, disabled, onCommit }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const current = String(value != null ? value : '');
    const begin = () => {
        if (disabled) return;
        setDraft(current);
        setEditing(true);
    };
    const commit = () => {
        setEditing(false);
        const v = draft.trim();
        if (v && v !== current && onCommit) onCommit(v.slice(0, 300));
    };
    if (!editing) {
        return (
            <span className="flow-edit__text-wrap">
                <span className="confirm-card__change-value">{current}</span>
                <button type="button" className="flow-edit__pencil" disabled={disabled} onClick={begin} aria-label={'Edit ' + label} title="Edit">✏️</button>
            </span>
        );
    }
    return (
        <input
            className="confirm-card__input flow-edit__text-input"
            type="text"
            value={draft}
            autoFocus
            aria-label={'Edit ' + label}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commit(); }
                else if (e.key === 'Escape') setEditing(false);
            }}
        />
    );
}

// The flow pre-flight card itself. v2 payload (payload.v >= 2, design flow-ux
// §3): items carry slot/kind/choices/search/defaulted and the payload gains
// note/errors — party rows become a searchable dropdown (PartySelect), choice
// rows chips (true/false renders Yes/No), date rows a native date input, text
// rows an inline pencil edit (TextInlineEdit). Each edit fires ONE
// __FLOW_EDIT__ marker via sendFlowEdit; the server's re-emitted card (with
// note/errors) is the ack and applies the new payload. v1 payloads and
// stale/superseded cards render the original read-only card — old servers keep
// working unchanged (the marker is never sent to them).
function FlowConfirmCard({ payload, resolvedChoice, stale, editInFlight, onConfirm, onCancel, onSendEdit }) {
    const groups = Array.isArray(payload.params) ? payload.params : [];
    const settled = !!resolvedChoice || !!stale;
    const disabled = settled;
    // v2 gate (design §4.4): edit affordances exist ONLY on payload.v >= 2
    // cards that are still live — a v1 payload (old server) or a superseded /
    // resolved card renders exactly the original read-only card, so the
    // __FLOW_EDIT__ marker is never sent to a server that can't parse it.
    const editable = Number(payload.v) >= 2 && !settled;
    const lockEdit = settled || !!editInFlight;
    // Optimistic row overrides keyed by slot: a dropdown pick updates the row
    // (and the derived Customer preview below) immediately, before the server
    // re-emits the card — the re-emitted card is the authoritative value.
    const [overrides, setOverrides] = useState({});
    const effValue = (it) => (it && overrides[it.slot] != null ? String(overrides[it.slot]) : String(it.value != null ? it.value : ''));
    const sendEdit = (item, edit) => {
        if (!editable || lockEdit || !onSendEdit || !edit) return;
        if (edit.kind === 'party') {
            const disp = edit.code ? formatPartyDisplay(edit.name, edit.code, edit.office) : String(edit.name || '');
            setOverrides((prev) => Object.assign({}, prev, { [item.slot]: disp }));
        } else if (edit.value != null) {
            setOverrides((prev) => Object.assign({}, prev, { [item.slot]: String(edit.value) }));
        }
        onSendEdit(item, edit);
    };
    // Create-flow only: derive the Customer the fill will stamp from the card's
    // own consignee and show it BEFORE confirmation (user request 2026-10-02:
    // "shouldn't the customer be filled before I confirm the booking?"). Same
    // site party API the dropdown uses; best-effort — quiet on failure, and the
    // by-id derivation at fill time stays authoritative. On v2 cards this runs
    // against the OPTIMISTIC consignee so it refreshes the moment a dropdown
    // pick lands, before the server re-emit arrives.
    const [customerPreview, setCustomerPreview] = useState(null);
    const consigneeForCustomer = (() => {
        const fromGroup = (groupName) => {
            const g = groups.find(x => x.group === groupName);
            const it = g && (g.items || []).find(y => y.label === 'Consignee');
            return it ? effValue(it) : '';
        };
        const fromSlot = (() => {
            for (const g of groups) {
                const it = (g.items || []).find(y => y.slot === 'consignee');
                if (it) return effValue(it);
            }
            return '';
        })();
        // Prefer the RESOLVED canonical name ("RIESS KELOMAT GMBH") over the raw
        // slot value ("Riess Kelomat") — the site search is spelling-sensitive.
        // An optimistic dropdown pick (overrides.consignee) wins over both.
        let v = overrides.consignee != null ? String(overrides.consignee) : '';
        if (!v) v = fromGroup('Resolved parties') || fromGroup('Parties') || fromSlot;
        return stripPartyDecorations(v);
    })();
    useEffect(() => {
        if (payload.flow !== 'create_booking' || !consigneeForCustomer) return undefined;
        let alive = true;
        previewConsigneeCustomer(consigneeForCustomer).then((r) => {
            if (!alive) return;
            setCustomerPreview(r
                ? (r.customer_code
                    ? `${r.customer_full_name} [${r.customer_code}]`
                    : r.customer_full_name)
                : 'Other (no customer linkage found — fill will leave Customer empty for you to set)');
        });
        return () => { alive = false; };
    }, [payload.flow, consigneeForCustomer]);
    const displayGroups = groups.map(g => ({ ...g, items: [...(g.items || [])] }));
    if (customerPreview && payload.flow === 'create_booking') {
        if (editable) {
            // v2 hides the "Resolved parties" group (party rows carry the
            // formatted value themselves) — the derived Customer becomes its
            // own labeled row instead (design §5.2).
            displayGroups.push({ group: 'Customer', items: [{ label: 'Customer (derived from consignee)', value: customerPreview }] });
        } else {
            let rp = displayGroups.find(g => g.group === 'Resolved parties');
            if (!rp) { rp = { group: 'Resolved parties', items: [] }; displayGroups.push(rp); }
            rp.items.push({ label: 'Customer (derived from consignee)', value: customerPreview });
        }
    }
    const note = payload.note ? String(payload.note) : '';
    const errors = Array.isArray(payload.errors) ? payload.errors.filter((e) => e && e.slot) : [];
    const errFor = (it) => (it && it.slot ? errors.find((e) => e.slot === it.slot) : null);
    return (
        <div className={'confirm-card confirm-card--flow' + (settled ? ' is-resolved' : '')}>
            <div className="confirm-card__title">
                <span className="confirm-card__title-icon"><StepIcon name="shield" /></span>
                {payload.title || 'Confirm action'}
            </div>
            <div className="confirm-card__body">
                {payload.intro && <div className="confirm-card__body-text">{payload.intro}</div>}
                {editable && note ? <div className="flow-edit__note">{note}</div> : null}
                {displayGroups.map((g, gi) => {
                    // v2: the "Resolved parties" echo group is redundant (the
                    // party rows show the same formatted value) — hidden.
                    if (editable && g.group === 'Resolved parties') return null;
                    return (
                        <div key={g.group || ('group-' + gi)} className="confirm-card__group">
                            {g.group && <div className="confirm-card__group-label">{g.group}</div>}
                            <ul className="confirm-card__changes">
                                {(g.items || []).map((it, i) => {
                                    const err = editable ? errFor(it) : null;
                                    return (
                                        <li key={i} className={'confirm-card__change' + (editable ? ' flow-edit__row' : '') + (err ? ' has-error' : '')}>
                                            <span className="flow-edit__row-head">
                                                <span className="confirm-card__change-label">{it.label}</span>
                                                {editable && it.defaulted ? <span className="flow-edit__badge" title="Value equals the flow default">default</span> : null}
                                            </span>
                                            {editable && it.kind === 'party' && it.search ? (
                                                <PartySelect item={{ ...it, value: effValue(it) }} disabled={lockEdit} onPick={(edit) => sendEdit(it, edit)} />
                                            ) : editable && it.kind === 'choice' && Array.isArray(it.choices) && it.choices.length > 0 ? (
                                                <div className="flow-edit__chips" role="group" aria-label={it.label}>
                                                    {it.choices.map((c) => {
                                                        const cv = String(c);
                                                        const sel = cv.trim().toLowerCase() === effValue(it).trim().toLowerCase();
                                                        const pairIsBool = it.choices.length === 2
                                                            && it.choices.every((x) => ['true', 'false'].indexOf(String(x).trim().toLowerCase()) !== -1);
                                                        return (
                                                            <button
                                                                key={cv}
                                                                type="button"
                                                                className={'flow-edit__chip' + (sel ? ' is-selected' : '')}
                                                                disabled={lockEdit}
                                                                onClick={() => { if (!sel) sendEdit(it, { slot: it.slot, kind: 'choice', value: cv }); }}
                                                            >
                                                                {pairIsBool && (cv.trim().toLowerCase() === 'true' || cv.trim().toLowerCase() === 'false')
                                                                    ? (cv.trim().toLowerCase() === 'true' ? 'Yes' : 'No')
                                                                    : cv}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            ) : editable && it.kind === 'date' ? (() => {
                                                const cur = effValue(it);
                                                const iso = /^\d{4}-\d{2}-\d{2}$/.test(cur) ? cur : '';
                                                return (
                                                    <span className="flow-edit__date-wrap">
                                                        <input
                                                            className="flow-edit__date"
                                                            type="date"
                                                            value={iso}
                                                            disabled={lockEdit}
                                                            aria-label={'Edit ' + it.label}
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                if (/^\d{4}-\d{2}-\d{2}$/.test(v)) sendEdit(it, { slot: it.slot, kind: 'date', value: v });
                                                            }}
                                                        />
                                                        {!iso && cur ? <span className="flow-edit__date-current">{cur}</span> : null}
                                                    </span>
                                                );
                                            })() : editable && it.kind === 'text' ? (
                                                <TextInlineEdit
                                                    label={it.label}
                                                    value={effValue(it)}
                                                    disabled={lockEdit}
                                                    onCommit={(v) => sendEdit(it, { slot: it.slot, kind: 'text', value: v })}
                                                />
                                            ) : (
                                                <span className="confirm-card__change-value">{effValue(it)}</span>
                                            )}
                                            {err ? <div className="flow-edit__error">{String(err.message || '')}</div> : null}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    );
                })}
                {payload.mutating && (
                    <div className="confirm-card__body-text">
                        {payload.autoApproved
                            ? 'Edit automatically mode: the fixed step list is running now and makes real changes in BMS without further confirmation clicks.'
                            : 'Confirming runs the fixed step list and makes real changes in BMS. You will still get the usual save confirmation before each commit.'}
                    </div>
                )}
            </div>
            <div className="confirm-card__actions">
                {payload.autoApproved ? (
                    <span className="confirm-card__auto-note">Auto-confirmed — Edit automatically mode</span>
                ) : (
                    <>
                        <button type="button" className="confirm-btn confirm-btn--reject" disabled={disabled || !!editInFlight} onClick={onCancel}>Cancel</button>
                        <button type="button" className="confirm-btn confirm-btn--accept" disabled={disabled || !!editInFlight} onClick={onConfirm}>
                            {resolvedChoice === 'confirm' ? 'Confirmed' : (editInFlight ? 'Updating…' : 'Confirm')}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

// In-chat selection card for the ask_user_choice pseudo tool. The server asks a
// discrete question (2-9 options, each with an optional detail line, plus an
// optional free-text "Other" row); the pick travels back as a normal
// __TOOL_RESULT__ envelope (tool_name 'ask_user_choice'). Option rows are real
// buttons (native keyboard access) and ArrowUp/ArrowDown move the selection.
function UserChoiceCard({ payload, resolvedChoice, stale, onSubmit, onCancel }) {
    const options = Array.isArray(payload.options) ? payload.options : [];
    const [selected, setSelected] = useState(null); // 1-based option index
    const [otherText, setOtherText] = useState('');
    const rowRefs = useRef([]);
    const disabled = !!resolvedChoice || !!stale;
    const otherChosen = otherText.trim().length > 0;
    const canSubmit = !disabled && (selected !== null || otherChosen);

    const pickOption = (idx) => {
        if (disabled) return;
        setSelected(idx);
        setOtherText(''); // options and Other are mutually exclusive
    };

    // Arrow keys move between option rows (selection + focus) for keyboard users.
    const onKeyDown = (e) => {
        if (disabled || options.length === 0) return;
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        e.preventDefault();
        const cur = selected === null ? 0 : selected;
        const next = e.key === 'ArrowDown' ? Math.min(cur + 1, options.length) : Math.max(cur - 1, 1);
        pickOption(next);
        const row = rowRefs.current[next - 1];
        if (row && row.focus) row.focus();
    };

    const handleSubmit = () => {
        if (!canSubmit) return;
        if (otherChosen) {
            onSubmit({ text: otherText.trim() });
        } else if (selected !== null) {
            const opt = options[selected - 1] || {};
            onSubmit({ index: selected, label: opt.label != null ? String(opt.label) : '' });
        }
    };

    const letterFor = (i) => 'abcdefghijklmnopqrstuvwxyz'[i] || (i + 1);

    return (
        <div className={'confirm-card choice-card' + (resolvedChoice || stale ? ' is-resolved' : '')}>
            <div className="confirm-card__title">
                <span className="confirm-card__title-icon"><StepIcon name="shield" /></span>
                {payload.question || 'Please choose an option'}
            </div>
            <div className="confirm-card__body">
                <div className="choice-card__options" role="radiogroup" aria-label={payload.question || 'Options'} onKeyDown={onKeyDown}>
                    {options.map((opt, i) => {
                        const idx = i + 1;
                        const isSel = selected === idx && !otherChosen;
                        return (
                            <button
                                key={idx}
                                type="button"
                                ref={(el) => { rowRefs.current[i] = el; }}
                                className={'choice-card__option' + (isSel ? ' is-selected' : '')}
                                role="radio"
                                aria-checked={isSel}
                                disabled={disabled}
                                onClick={() => pickOption(idx)}
                            >
                                <span className="choice-card__letter" aria-hidden="true">{letterFor(i)})</span>
                                <span className="choice-card__option-text">
                                    <span className="choice-card__option-label">{opt && opt.label != null ? String(opt.label) : ('Option ' + idx)}</span>
                                    {opt && opt.detail ? <span className="choice-card__option-detail">{opt.detail}</span> : null}
                                </span>
                            </button>
                        );
                    })}
                    {payload.other_hint ? (
                        <div className={'choice-card__option choice-card__option--other' + (otherChosen ? ' is-selected' : '')}>
                            <span className="choice-card__letter" aria-hidden="true">{letterFor(options.length)})</span>
                            <div className="choice-card__other-row">
                                <span className="choice-card__other-label">Other:</span>
                                <input
                                    className="confirm-card__input choice-card__other-input"
                                    type="text"
                                    placeholder={payload.other_hint}
                                    value={otherText}
                                    disabled={disabled}
                                    onChange={(e) => { if (disabled) return; setSelected(null); setOtherText(e.target.value); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); } }}
                                    aria-label={'Other: ' + payload.other_hint}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
            <div className="confirm-card__actions">
                <button type="button" className="confirm-btn confirm-btn--reject" disabled={disabled} onClick={onCancel}>Cancel</button>
                <button type="button" className="confirm-btn confirm-btn--accept" disabled={!canSubmit} onClick={handleSubmit}>
                    {resolvedChoice === 'submit' ? 'Submitted' : 'Submit'}
                </button>
            </div>
        </div>
    );
}

export default function Chatbot(props) {
    let threadId = null;
    let isOpen = false;
    let streamingContent = '';
    let currentMessageId = null;
    let isDragging = false;
    let dragOffsetX = 0;
    let dragOffsetY = 0;
    let conversationHistory = []; // Track conversation for context
    // Keep bubble within viewport
    let maxX;
    let maxY;

    // DOM elements
    let chatBubble;
    let chatWindow;
    let closeChat;
    let chatForm;
    //let chatInput;
    let chatMessages;
    let sendButton;
    let statusContainer;
    let statusTextEl = null;
    let defaultInputPlaceholder = 'Type your message...';

    let isInitializingThread = false;
    let threadInitializationPromise = null;

    // Resize functionality
    let isResizing = false;
    let resizeDirection = '';
    let startX, startY, startWidth, startHeight, startLeft, startBottom;
    let offset = { left: 0, top: 0 };
    let last_mouse = { x: 0, y: 0 };

    const [chatMsg, setChatMsg] = useState([]);
    const [didMount, setDidMount] = useState(false);
    const [showProcessing, setShowProcessing] = useState(false);
    const [showTyping, setShowTyping] = useState(false);
    const [disableInput, setDisableInput] = useState(false);
    const [lightbox, setLightbox] = useState(null);
    const openChartLightbox = useCallback((spec) => setLightbox({ kind: 'chart', spec }), []);
    const [errorMsg, setErrorMsg] = useState('');
    const [selectedCitation, setSelectedCitation] = useState(null);
    const [showSourcePanel, setShowSourcePanel] = useState(false);
    const [imageEnlarged, setImageEnlarged] = useState(false);
    // Source-panel zoom (1 = fit panel width); reset whenever the shown citation changes.
    const [sourceZoom, setSourceZoom] = useState(1);
    // Unread replies that landed while the window was minimized (launcher badge).
    const [unreadCount, setUnreadCount] = useState(0);
    const isOpenRef = useRef(false);
    // Scroll-to-bottom pill: shown when the list is scrolled away from the bottom;
    // counts messages that arrived while the user was scrolled up.
    const [showScrollBottom, setShowScrollBottom] = useState(false);
    const [scrollNewCount, setScrollNewCount] = useState(0);
    const isNearBottomRef = useRef(true);
    const prevMsgCountRef = useRef(0);
    // Input text presence → dims the send button when there is nothing to send.
    const [hasInputText, setHasInputText] = useState(false);
    const inputRef = useRef('');
    const chatMsgRef = useRef(null);
    const processingStartTimeRef = useRef(null);
    const minProcessingDelayRef = useRef(500); // Minimum 500ms for processing indicator
    const currentTurnAssistantMessageIdsRef = useRef([]);
    // Mirror of the threadId closure variable. It is written from several
    // closure generations (mount-time DOM listeners vs fresh JSX handlers), so
    // stable callbacks must read the thread id through this ref, never through
    // their own (possibly stale) `threadId` capture.
    const threadIdRef = useRef(null);
    const currentTurnThinkingPhasesRef = useRef([]);
    const currentTurnReasoningTokensRef = useRef(0);
    const currentTurnReasoningTextRef = useRef('');
    const currentTurnStartedAtRef = useRef(null);
    const currentTurnToolHistoryRef = useRef([]);
    const currentTurnLastMeaningfulResultRef = useRef(null);
    const currentTurnFinalizedRef = useRef(false);
    // Multi-agent turn tracking: the ordered agent plan, the cursor into it, and
    // each intermediate agent's streamed answer (collapsed under the final reply).
    const currentTurnAgentPlanRef = useRef([]);
    const currentTurnAgentIdxRef = useRef(0);
    const currentTurnAgentStepsRef = useRef([]);
    // Chronological COT steps (phases + tool actions) for the live and finalized
    // action timeline. The ref is the source of truth; liveSteps mirrors it into
    // state so a new/finished step re-renders the live panel immediately.
    const currentTurnStepsRef = useRef([]);
    const [liveSteps, setLiveSteps] = useState([]);
    // Citations captured so far this turn — feeds the live retrieval row's
    // expandable sources list (the finalized view reads them off the message).
    const currentTurnCitationsRef = useRef([]);
    // Tool-result message ids already folded into the timeline (partials repeat).
    const currentTurnToolMsgIdsRef = useRef(new Set());
    const [thinkingPhase, setThinkingPhase] = useState('');
    // Tracks messages the user has manually COLLAPSED. The thinking timeline is
    // EXPANDED by default so the full COT is visible after each turn; clicking
    // the summary collapses it. (Inverted from the old "expanded" set, which
    // started collapsed and only auto-opened for Tool:/Running phases.)
    const [expandedThinking, setExpandedThinking] = useState(new Set());
    const [copiedId, setCopiedId] = useState(null);
    // Live "now" tick: re-renders ~every 150ms while a turn is in flight so the
    // live-thinking duration counter advances and the phase-chip rail (which
    // reads from a ref) stays reactive. Idle when nothing is streaming/processing.
    const [nowTick, setNowTick] = useState(0);
    const [attachments, setAttachments] = useState([]);
    // Starter-prompt chips: fetched from the backend on mount (fallback to DEFAULT_SUGGESTED_PROMPTS).
    const [suggestedPrompts, setSuggestedPrompts] = useState(DEFAULT_SUGGESTED_PROMPTS);
    // Visible chips: a random subset of 4 from the backend list. Reshuffled each time the
    // welcome screen (re)appears (on load AND after the user clears the chat) so the chips
    // actually vary across sessions — not just once per page load.
    const [visiblePrompts, setVisiblePrompts] = useState([]);
    const isWelcome = CheckIsEmptyArray(chatMsg);
    useEffect(() => {
        if (!isWelcome) return;  // only (re)shuffle when the welcome screen is showing
        const a = [...suggestedPrompts];
        for (let i = a.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [a[i], a[j]] = [a[j], a[i]];
        }
        setVisiblePrompts(a.slice(0, 4));
    }, [isWelcome, suggestedPrompts]);
    const fileInputRef = useRef(null);
    // Confirmation gate state. toolStreamTimeoutRef hoists sendToolResultToServer's
    // 300s watchdog so the gate can disarm it before awaiting a human decision.
    const toolStreamTimeoutRef = useRef(null);
    // Watchdog fire needs to abort the stream it belongs to; those handles are
    // local to sendToolResultToServer, so they ride refs the callback can read.
    const toolStreamAbortRef = useRef(null);
    const toolStreamInactivityClearRef = useRef(null);
    const pendingConfirmationRef = useRef(null);
    const preparedFormChangesRef = useRef([]);
    const [pendingConfirmation, setPendingConfirmation] = useState(null);
    const [confirmationEdit, setConfirmationEdit] = useState(null);
    // A __FLOW_EDIT__ card edit is mid-flight (sendFlowEdit → sendMessage stream
    // still open). Flow-card edit affordances and Confirm/Cancel lock until it
    // clears when the ack card renders, so two edits never race on one thread
    // and the re-emitted card arrives as the only ack. editSeqRef serializes the
    // fallback clears (a slower older stream must not clear a newer edit's flag).
    const [editInFlight, setEditInFlight] = useState(false);
    const editSeqRef = useRef(0);

    // Approval mode (Ask before changes / Edit automatically). localStorage is
    // the source of truth (readApprovalMode reads it fresh at every decision
    // point); this state only re-renders the header toggle.
    const [approvalMode, setApprovalMode] = useState(readApprovalMode);
    const [approvalMenuOpen, setApprovalMenuOpen] = useState(false);
    const approvalMenuRef = useRef(null);
    const chooseApprovalMode = useCallback((mode) => {
        writeApprovalMode(mode);
        setApprovalMode(mode);
        setApprovalMenuOpen(false);
    }, []);
    // Outside click / Escape closes the mode popover.
    useEffect(() => {
        if (!approvalMenuOpen) return undefined;
        const onDown = (e) => {
            if (approvalMenuRef.current && !approvalMenuRef.current.contains(e.target)) {
                setApprovalMenuOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === 'Escape') setApprovalMenuOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [approvalMenuOpen]);

    // Set default mode to docker on component mount
    useEffect(() => {
        if (!localStorage.getItem('chatbot_mode')) {
            localStorage.setItem('chatbot_mode', 'docker');
        }
    }, []);

    useEffect(() => {
        init();
    }, []);

    // Fetch backend-driven starter prompts (served by the langgraph image at
    // ${LANGGRAPH_URL}/suggestions). Plain GET, NO auth header -> stays a CORS
    // "simple request" (no preflight). The backend is the single source of truth:
    // an empty list (CHATBOT_SUGGESTIONS_ENABLED=false) hides ALL chips, so we apply
    // the response even when it is empty. Only a failed/non-OK request leaves the
    // (also empty) default in place.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const resp = await fetch(`${LANGGRAPH_URL}/suggestions`, { method: 'GET' });
                if (!resp.ok) return;
                const data = await resp.json();
                const items = Array.isArray(data && data.suggestions) ? data.suggestions : [];
                if (!cancelled) {
                    // Keep the agent tag (drives the chip icon) and hide analytics/
                    // tracking suggestions in the welcome UI (WELCOME_AGENTS filter).
                    setSuggestedPrompts(items
                        .filter(s => !s.agent || WELCOME_AGENTS.indexOf(s.agent) !== -1)
                        .map(s => ({ preview: s.preview, prompt: s.prompt, agent: s.agent || 'meta' })));
                }
            } catch (_e) {
                // Backend unreachable / non-JSON -> keep the (empty) default.
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Copy handler: reformat selected chat text with "user:" / "chatbot:" prefixes
    useEffect(() => {
        const container = chatMsgRef.current;
        if (!container) return;

        const handleCopy = (e) => {
            const selection = window.getSelection();
            if (!selection || selection.isCollapsed) return;

            const range = selection.getRangeAt(0);
            if (!container.contains(range.commonAncestorContainer)) return;

            // Collect the messages the selection touches
            const messages = container.querySelectorAll('.message');
            const hits = [];

            messages.forEach(msg => {
                const textEl = msg.querySelector('.message-text');
                if (!textEl) return;

                // Check if this message intersects with the selection
                try {
                    const msgRange = document.createRange();
                    msgRange.selectNodeContents(textEl);
                    const overlap = range.compareBoundaryPoints(Range.END_TO_START, msgRange) <= 0 &&
                                   range.compareBoundaryPoints(Range.START_TO_END, msgRange) >= 0;
                    if (overlap) hits.push({ msg, textEl, msgRange });
                } catch (err) {
                    // skip this message
                }
            });

            if (hits.length === 0) return;

            // Selection fully inside one bubble: copy exactly what the user
            // highlighted (native copy), no prefix and no rewrite
            if (hits.length === 1 &&
                hits[0].textEl.contains(range.startContainer) &&
                hits[0].textEl.contains(range.endContainer)) {
                return;
            }

            // Selection spanning bubbles: keep only the selected portion of
            // each bubble, prefixed with "user:" / "chatbot:"
            const parts = [];

            hits.forEach(({ msg, msgRange }) => {
                const isUser = msg.classList.contains('user-message');
                try {
                    const inter = range.cloneRange();
                    if (inter.compareBoundaryPoints(Range.START_TO_START, msgRange) < 0) {
                        inter.setStart(msgRange.startContainer, msgRange.startOffset);
                    }
                    if (inter.compareBoundaryPoints(Range.END_TO_END, msgRange) > 0) {
                        inter.setEnd(msgRange.endContainer, msgRange.endOffset);
                    }
                    const selectedText = inter.toString().trim();
                    if (!selectedText) return;

                    const prefix = isUser ? 'user: ' : 'chatbot: ';
                    parts.push(prefix + selectedText);
                } catch (err) {
                    // skip this message
                }
            });

            if (parts.length > 0) {
                e.preventDefault();
                e.clipboardData.setData('text/plain', parts.join('\n\n'));
            }
        };

        container.addEventListener('copy', handleCopy);
        return () => container.removeEventListener('copy', handleCopy);
    }, [chatMsg]);

    useEffect(() => {
        const panel = document.getElementById('sourcePanel');
        const chatWin = document.getElementById('chatWindow');
        if (!panel || !chatWin) return;

        if (showSourcePanel) {
            const chatRect = chatWin.getBoundingClientRect();
            const gap = 16;
            // Always dock the sources panel to the LEFT of the chat window.
            // The old logic fell back to the RIGHT whenever the space left of
            // the chatbox was narrower than the panel (panelWidth + gap). On a
            // typical laptop the chatbox sits well inside that threshold, so the
            // panel almost always ended up on the wrong side. Clamp the left
            // offset to the viewport so the panel never overflows off-screen on
            // narrow windows (it has max-width: 90vw, so it shrinks to fit).
            const panelWidth = panel.offsetWidth || 750;
            let left = chatRect.left - panelWidth - gap;
            if (left < gap) left = gap;
            panel.style.left = left + 'px';
            panel.style.top = chatRect.top + 'px';
            panel.style.height = chatRect.height + 'px';
        }
    }, [showSourcePanel]);

    // Run the live clock only while a turn is in flight (processing OR a message
    // is streaming). anyStreaming flips once per turn, so the interval is set up
    // once when streaming starts and cleared once when it ends — token-by-token
    // chatMsg updates do NOT churn it.
    const anyStreaming = chatMsg.some(m => m.streaming);
    useEffect(() => {
        if (!showProcessing && !anyStreaming) return undefined;
        const id = setInterval(() => setNowTick(t => (t + 1) % 1000000000), 150);
        return () => clearInterval(id);
    }, [showProcessing, anyStreaming]);

    const endDrag = () => {
        if (isDragging) {
            //set temp chat window display css to set final drag position
            chatWindow.style.display = 'flex';

            const chatBubblePosition = {
                left: Math.min(maxX, chatBubble.offsetLeft + offset.left),
                top: Math.min(maxY, chatBubble.offsetTop + offset.top)
            };
            const chatWindowPosition = {
                left: chatBubblePosition.left - chatWindow.offsetWidth + 60,
                top: chatBubblePosition.top - chatWindow.offsetHeight - 10
            };

            chatBubble.style.left = `${chatBubblePosition.left}px`;
            chatBubble.style.top = `${chatBubblePosition.top}px`;
            chatBubble.style.right = 'auto';
            chatBubble.style.bottom = 'auto';

            chatWindow.style.left = `${chatWindowPosition.left}px`;
            chatWindow.style.top = `${chatWindowPosition.top}px`;
            chatWindow.style.right = 'auto';
            chatWindow.style.bottom = 'auto';
            //reset temp chat window display css 
            chatWindow.style.display = null;
        }
        else {
            toggleChat();
        }

        isDragging = false;

        chatBubble.style.transform = 'none';
        chatBubble.style.willChange = '';

        chatWindow.style.transform = 'none';
        chatWindow.style.willChange = '';
        
        //setTimeout(() => {
        //    if (!isDragging) {
        //        toggleChat();
        //    }
        //    isDragging = false;
        //}, 300);

        document.removeEventListener('mousemove', drag, true);
        document.removeEventListener('mouseup', endDrag, true);
    };

    const drag = (e) => {
        isDragging = true;
        const headerHeight = 65;

        if (e.clientX < maxX) {
            offset.left = Math.round(offset.left + e.clientX - last_mouse.x);
        }
        if (e.clientY < maxY && e.clientY > headerHeight) {
            offset.top = Math.round(offset.top + e.clientY - last_mouse.y);
        }
        
        chatBubble.style.transform = `translate(${offset.left}px, ${offset.top}px)`;

        // Move chat window to follow bubble
        if (isOpen) {
            chatWindow.style.transform = `translate(${offset.left + 16}px, ${offset.top + 16}px)`;
        }

        last_mouse.x = e.clientX;
        last_mouse.y = e.clientY;
    };

    const startDrag = (e) => {
        // Check if it's a left click
        if (e.button !== 0) return;

        isDragging = false;

        maxX = window.innerWidth - chatBubble.offsetWidth;
        maxY = window.innerHeight - chatBubble.offsetHeight;

        offset.left = 0;
        offset.top = 0;
        
        last_mouse.x = e.clientX;
        last_mouse.y = e.clientY;

        chatBubble.style.willChange = 'transform';

        if (isOpen) {
            chatWindow.style.willChange = 'transform';
        }

        document.addEventListener('mousemove', drag, true);
        document.addEventListener('mouseup', endDrag, true);

        e.preventDefault();
    };

    const init = () => {
        // DOM elements
        chatBubble = document.getElementById('chatBubble');
        chatWindow = document.getElementById('chatWindow');
        closeChat = document.getElementById('closeChat');
        chatForm = document.getElementById('chatForm');

        // Use refs for elements that may not be available on first render
        chatMessages = chatMsgRef.current;

        sendButton = chatForm.querySelector('button[type="submit"]');
        statusContainer = document.querySelector('.status');
        statusTextEl = statusContainer ? statusContainer.querySelector('.status-text') : null;
        
        // Event Listeners
        chatBubble.addEventListener('mousedown', startDrag, true);

        closeChat.addEventListener('click', () => {
            if (isOpen) {
                toggleChat();
            }
        });

        // Minimize button event listener
        const minimizeChatBtn = document.getElementById('minimizeChat');
        if (minimizeChatBtn) {
            minimizeChatBtn.addEventListener('click', () => {
                if (isOpen) {
                    minimizeChat();
                }
            });
        }

        document.addEventListener('mouseup', () => {
            if (isResizing) {
                isResizing = false;
                resizeDirection = '';
            }
        });

        // NOTE: No blanket click listener on chatWindow.
        // Previously e.preventDefault() was used here to "prevent closing", but it
        // also cancelled the file-input's programmatic .click() — the file dialog is
        // the default action of a file-input click, so preventDefault() suppressed it.
        // The chat window is only closed by the close/minimize buttons and the chat
        // bubble's mouseup handler, so a blanket click handler is not needed.

        document.querySelectorAll('.resize-handle').forEach(handle => {
            handle.addEventListener('mousedown', (e) => {
                if (!isOpen) return; // Only allow resize when chat is open

                // Defensive (M6): never start a resize when the pointer actually
                // landed on the messages area or its native scrollbar. The handles
                // are siblings (not descendants) of .chat-messages, so legitimate
                // handle drags (e.target = the handle div) are unaffected; a
                // mousedown on the scrollbar resolves e.target to .chat-messages.
                if (e.target && e.target.closest && e.target.closest('.chat-messages')) {
                    return;
                }

                isResizing = true;
                resizeDirection = Array.from(handle.classList)
                    .find(c => c.startsWith('resize-') && c !== 'resize-handle');

                const rect = chatWindow.getBoundingClientRect();
                startX = e.clientX;
                startY = e.clientY;
                startWidth = rect.width;
                startHeight = rect.height;
                startLeft = rect.left;
                startBottom = window.innerHeight - rect.bottom;

                // Add visual feedback - use a temporary overlay instead of disabling pointerEvents
                const overlay = document.createElement('div');
                overlay.id = 'resizeOverlay';
                overlay.style.cssText = `
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    z-index: 1019;
                    cursor: ${handle.style.cursor || 'default'};
                `;
                document.body.appendChild(overlay);

                // Clean up overlay on mouse up
                const removeOverlay = () => {
                    const existingOverlay = document.getElementById('resizeOverlay');
                    if (existingOverlay) {
                        existingOverlay.remove();
                    }
                };

                const originalMouseUp = () => {
                    removeOverlay();
                    document.removeEventListener('mouseup', originalMouseUp);
                };

                document.addEventListener('mouseup', originalMouseUp);

                e.preventDefault();
                e.stopPropagation();
            });
        });

        document.addEventListener('mousemove', (e) => {
            if (!isResizing) return;

            const deltaX = e.clientX - startX;
            const deltaY = startY - e.clientY; // Inverted for bottom-anchored

            let newWidth = startWidth;
            let newHeight = startHeight;
            let newLeft = startLeft;
            let newBottom = startBottom;

            // Handle different resize directions
            if (resizeDirection.includes('right')) {
                newWidth = startWidth + deltaX;
            }
            if (resizeDirection.includes('left')) {
                newWidth = startWidth - deltaX;
                newLeft = startLeft + deltaX;
            }
            if (resizeDirection.includes('top')) {
                newHeight = startHeight + deltaY;
            }
            if (resizeDirection.includes('bottom')) {
                newHeight = startHeight - deltaY;
                newBottom = startBottom + deltaY;
            }

            // Apply constraints
            const minWidth = 300;
            const minHeight = 400;
            const maxWidth = window.innerWidth * 0.9;
            const maxHeight = window.innerHeight * 0.9;

            newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
            newHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));

            // Ensure position stays within viewport
            newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - newWidth));
            newBottom = Math.max(0, newBottom);

            chatWindow.style.width = `${newWidth}px`;
            chatWindow.style.height = `${newHeight}px`;

            // Only apply position changes for left/top resizing
            if (resizeDirection.includes('left')) {
                chatWindow.style.right = `${window.innerWidth - newLeft - newWidth}px`;
                chatWindow.style.left = 'auto';
            }
            if (resizeDirection.includes('top')) {
                chatWindow.style.bottom = `${newBottom}px`;
                chatWindow.style.top = 'auto';
            }

            e.preventDefault();
        });

        // Initialize
        initializeChat({ silent: true })
            .then((id) => {
                if (!id) {
                    console.warn('Initial chat warm-up did not establish a thread. The assistant will retry when opened.');
                }
            });

        setDidMount(true);
    };
    
    const setChatInputEnabled = (enabled, placeholder = defaultInputPlaceholder) => {
        setDisableInput(!enabled);

        // The send button's disabled state is owned by React (JSX combines
        // disableInput + empty-input here) — only the legacy class is synced.
        if (sendButton) {
            sendButton.classList.toggle('disabled', !enabled);
        }
    };

    const updateConnectionStatus = (state, message) => {
        if (!statusContainer) {
            return;
        }
        statusContainer.dataset.state = state;
        const copy = message || CONNECTION_STATUS_COPY[state] || state;
        if (statusTextEl) {
            statusTextEl.textContent = copy;
        } else {
            statusContainer.textContent = copy;
        }
    };

    const fetchWithTimeout = (url, options = {}, timeout = 15000) => {
        const controller = new AbortController();
        const fetchOptions = { ...options, signal: controller.signal };
        const timeoutId = setTimeout(() => {
            try {
                return controller.abort('fetchWithTimeout');
            }
            catch (err) {
                console.error('fetchWithTimeout setTimeout error', err);
            }
        }, timeout);
        return fetch(url, fetchOptions).finally(() => clearTimeout(timeoutId));
    };

    const getAuthHeaders = () => {
        const headers = {
            'Content-Type': 'application/json'
        };

        // Use the ASP.NET id_token directly as the Bearer token
        const authToken = localStorage.getItem('id_token');

        if (authToken) {
            headers.Authorization = `Bearer ${authToken}`;
        } else {
            console.warn('No id_token in localStorage - request will be unauthenticated');
        }

        // Add x-functions-key for production mode
        if (!CONFIG.isDevelopment && CONFIG.FUNCTIONS_KEY) {
            headers['x-functions-key'] = CONFIG.FUNCTIONS_KEY;
        }

        return headers;
    };

    // Utility Functions
    const getCurrentTime = () => {
        return new Date().toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    // Day-divider label from a message timestamp: Today / Yesterday / "Sep 3".
    const formatDayLabel = (ts) => {
        if (!ts) return '';
        const d = new Date(ts);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const day = new Date(d);
        day.setHours(0, 0, 0, 0);
        const diffDays = Math.round((today - day) / 86400000);
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Citation pill label: filename without the .pdf extension, kept short.
    const shortSourceName = (source) => {
        const s = String(source || 'Unknown').replace(/\.pdf$/i, '').trim();
        return s.length > 26 ? (s.slice(0, 25).trimEnd() + '…') : s;
    };

    // Auto-scroll only when the user is already at (or near) the bottom; never
    // yank the list or steal focus mid-turn. Pass { force, focus } to do both
    // (used right after the USER sends a message).
    const scrollToBottom = (opts) => {
        const el = chatMsgRef.current;
        if (el && (opts?.force || isNearBottomRef.current)) {
            el.scrollTo({ top: el.scrollHeight });
        }
        if (opts && opts.focus && inputRef.current) {
            inputRef.current.focus();
        }
    };

    // Scroll listener on the message list: tracks "near bottom" for autoscroll,
    // drives the scroll-to-bottom pill.
    const handleMessagesScroll = () => {
        const el = chatMsgRef.current;
        if (!el) return;
        const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
        isNearBottomRef.current = distance < 80;
        setShowScrollBottom(distance > 200);
        if (isNearBottomRef.current && scrollNewCount !== 0) {
            setScrollNewCount(0);
        }
    };

    // Pill click: smooth-scroll back to the newest message.
    const jumpToBottom = () => {
        const el = chatMsgRef.current;
        if (el) {
            el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        }
        isNearBottomRef.current = true;
        setShowScrollBottom(false);
        setScrollNewCount(0);
    };

    // Prev/next through a message's deduped citation list (source panel header).
    const navigateCitation = (dir) => {
        setSelectedCitation(prev => {
            if (!prev || !prev._list || prev._list.length < 2) return prev;
            const next = prev._index + dir;
            if (next < 0 || next >= prev._list.length) return prev;
            setSourceZoom(1);
            return { ...prev._list[next], _index: next, _list: prev._list };
        });
    };

    const SOURCE_ZOOM_MIN = 0.6;
    const SOURCE_ZOOM_MAX = 2.5;
    const changeSourceZoom = (delta) => {
        setSourceZoom(z => Math.min(SOURCE_ZOOM_MAX, Math.max(SOURCE_ZOOM_MIN, Math.round((z + delta) * 100) / 100)));
    };

    // Bubble badge: count finalized replies that arrive while the window is
    // minimized (confirmation cards are excluded — they are removed on resolve).
    const finalizedBotCount = chatMsg.filter(m => !m.isUser && !m.streaming && m.kind !== 'confirmation').length;
    const finalizedBotCountRef = useRef(0);
    useEffect(() => {
        const prev = finalizedBotCountRef.current;
        finalizedBotCountRef.current = finalizedBotCount;
        if (finalizedBotCount > prev && !isOpenRef.current) {
            setUnreadCount(c => c + (finalizedBotCount - prev));
        }
    }, [finalizedBotCount]);

    useEffect(() => {
        //console.log('useEffect', chatMsg, showProcessing, showTyping)
        if (didMount) {
            // Messages arrived while the user was scrolled up → count them on
            // the pill instead of yanking the list to the bottom.
            const count = chatMsg.length;
            if (count > prevMsgCountRef.current && !isNearBottomRef.current) {
                setScrollNewCount(c => c + (count - prevMsgCountRef.current));
            }
            prevMsgCountRef.current = count;
            scrollToBottom();
        }
    }, [chatMsg, showProcessing, showTyping]);
    
    const addMessage = (content, isUser = false, messageId = null, citations = null, thinking = null, msgAttachments = null, streaming = false) => {
        // Stamp the wall-clock time ONCE at creation — render just reads m.time,
        // so re-renders never re-stamp old messages.
        const time = getCurrentTime();
        const ts = Date.now();
        let newMsg = [...chatMsg];

        let pushObj = {
            content,
            isUser,
            messageId,
            citations,
            streaming,
            time,
            ts
        };
        if (msgAttachments && msgAttachments.length > 0) {
            pushObj.attachments = msgAttachments;
        }
        newMsg.push(pushObj);

        setChatMsg((prevChatMsg) => {
            if (!isUser) {
                const lastAssistantMessage = [...prevChatMsg].reverse().find((message) => !message.isUser);
                if (lastAssistantMessage && lastAssistantMessage.content === content) {
                    return prevChatMsg;
                }
            }

            // Resolved choice cards (retireOnReply) drop out together with the
            // assistant reply that supersedes them — one update, no blank gap
            // between the user's pick and the answer replacing the card.
            let newMsg = isUser ? [...prevChatMsg] : prevChatMsg.filter(m => !m.retireOnReply);
            let newObj = {
                content,
                isUser,
                messageId,
                streaming,
                time,
                ts
            };

            // Add citations if provided
            if (citations && citations.length > 0) {
                newObj.citations = citations;
            }

            // Add thinking info if provided
            if (thinking) {
                newObj.thinking = thinking;
            }

            // Add attachments if provided
            if (msgAttachments && msgAttachments.length > 0) {
                newObj.attachments = msgAttachments;
            }

            newMsg.push(newObj);

            return newMsg;
        });
    };

    const updateMessage = (messageId, content, citations = null, thinking = null, streaming = null) => {
        setChatMsg((prevChatMsg) => {
            let newMsg = [...prevChatMsg]
            let idx = prevChatMsg.findLastIndex(m => {
                return m.messageId === messageId && !m.isUser;
            });

            if (idx > -1) {
                newMsg[idx].content = content;
                // Only update citations if explicitly provided (not null/undefined)
                // This preserves existing citations during streaming updates
                if (citations !== null && citations !== undefined) {
                    newMsg[idx].citations = citations;
                }
                // If citations parameter is explicitly provided but empty, clear them
                else if (citations === null && prevChatMsg[idx].citations) {
                    // Keep existing citations if not explicitly clearing them
                    // Don't do anything - preserve the existing citations
                }
                // Update thinking info if provided
                if (thinking) {
                    newMsg[idx].thinking = thinking;
                }
                // Streaming flag: while true the message renders as a live
                // "Thinking…" panel; finalized answers set it to false.
                if (streaming !== null && streaming !== undefined) {
                    newMsg[idx].streaming = streaming;
                }
            }
            else {
                const lastAssistantMessage = [...prevChatMsg].reverse().find((message) => !message.isUser);
                if (lastAssistantMessage && lastAssistantMessage.content === content) {
                    return prevChatMsg;
                }

                let newObj = {
                    content,
                    isUser: false,
                    messageId,
                    time: getCurrentTime(),
                    ts: Date.now()
                };
                if (citations && citations.length > 0) {
                    newObj.citations = citations;
                }
                if (thinking) {
                    newObj.thinking = thinking;
                }
                // Same retire-on-reply rule as addMessage: a resolved choice
                // card drops out in the update that shows this new reply.
                newMsg = newMsg.filter(m => !m.retireOnReply);
                newMsg.push(newObj);
            }

            return newMsg;
        });
    };

    const trackAssistantMessage = (messageId) => {
        if (!messageId) {
            return;
        }

        if (!currentTurnAssistantMessageIdsRef.current.includes(messageId)) {
            currentTurnAssistantMessageIdsRef.current = [
                ...currentTurnAssistantMessageIdsRef.current,
                messageId
            ];
        }
    };

    const clearCurrentTurnAssistantMessages = () => {
        const messageIds = currentTurnAssistantMessageIdsRef.current;
        if (!messageIds.length) {
            return;
        }

        setChatMsg((prevChatMsg) => prevChatMsg.filter(
            message => message.isUser || !messageIds.includes(message.messageId)
        ));
        currentTurnAssistantMessageIdsRef.current = [];
    };

    const beginCurrentTurnThinking = () => {
        currentTurnThinkingPhasesRef.current = [];
        currentTurnReasoningTokensRef.current = 0;
        currentTurnReasoningTextRef.current = '';
        currentTurnStartedAtRef.current = Date.now();
        currentTurnToolHistoryRef.current = [];
        currentTurnLastMeaningfulResultRef.current = null;
        currentTurnFinalizedRef.current = false;
        currentTurnAgentPlanRef.current = [];
        currentTurnAgentIdxRef.current = 0;
        currentTurnAgentStepsRef.current = [];
        currentTurnStepsRef.current = [];
        setLiveSteps([]);
        currentTurnCitationsRef.current = [];
        currentTurnToolMsgIdsRef.current = new Set();
    };

    // ---- Action-timeline step capture ----
    // Begin a new step; any still-running step is auto-closed as 'ok' (the turn
    // is strictly sequential, so a superseded step finished by definition).
    // opts.beforeLastPhase inserts the step before a trailing "answer" phase
    // (Generating response / agent phases) — used when folding the terminal
    // messages/complete batch so tool rows stay in true chronological order.
    const beginTurnStep = (step, opts = {}) => {
        kickToolStreamWatchdog();
        const now = Date.now();
        const prev = currentTurnStepsRef.current;
        if (step.kind === 'phase' && prev.length
            && prev[prev.length - 1].kind === 'phase'
            && prev[prev.length - 1].label === step.label) {
            return; // duplicate consecutive phase — ignore
        }
        let insertAt = prev.length;
        if (opts.beforeLastPhase) {
            for (let i = prev.length - 1; i >= 0; i -= 1) {
                const s = prev[i];
                if (s.kind === 'phase' && /generating response|agent$/i.test(s.label || '')) insertAt = i;
                else break;
            }
        }
        const closed = prev.map((s, i) => (
            s.status === 'running' && i < insertAt
                ? { ...s, status: 'ok', endedAt: now, durationMs: now - s.startedAt }
                : s
        ));
        const meta = step.kind === 'tool'
            ? (TOOL_META[step.toolName] || {})
            : (PHASE_META[step.label] || {});
        const entry = {
            id: `step-${now}-${Math.random().toString(36).slice(2, 7)}`,
            startedAt: now,
            status: 'running',
            ...step,
            icon: meta.icon || step.icon || 'tool',
            // Phases default to their short "what happens here" subtitle.
            preview: step.preview != null ? step.preview : (meta.sub || null),
        };
        currentTurnStepsRef.current = [...closed.slice(0, insertAt), entry, ...closed.slice(insertAt)];
        setLiveSteps(currentTurnStepsRef.current);
    };

    // Complete the most recent running step (patch carries summary/output/status;
    // appendPreview tacks a result digest onto the existing target preview).
    // patch.targetKind prefers a running step of that kind (tool over phase).
    const completeTurnStep = (patch = {}) => {
        kickToolStreamWatchdog();
        const { targetKind, ...rest } = patch;
        const steps = currentTurnStepsRef.current;
        let hit = -1;
        for (let i = steps.length - 1; i >= 0; i -= 1) {
            if (steps[i].status === 'running' && (!targetKind || steps[i].kind === targetKind)) {
                hit = i;
                break;
            }
        }
        if (hit === -1 && targetKind) {
            for (let i = steps.length - 1; i >= 0; i -= 1) {
                if (steps[i].status === 'running') { hit = i; break; }
            }
        }
        if (hit !== -1) {
            const now = Date.now();
            currentTurnStepsRef.current = steps.map((s, j) => {
                if (j !== hit) return s;
                const merged = { ...s, ...rest, status: rest.status || 'ok', endedAt: now, durationMs: now - s.startedAt };
                if (rest.appendPreview) {
                    merged.preview = [s.preview, rest.appendPreview].filter(Boolean).join(' — ');
                }
                return merged;
            });
            setLiveSteps(currentTurnStepsRef.current);
        }
        return hit !== -1;
    };

    // Close out every running step (turn end) so no spinner is ever stuck. status
    // defaults to 'ok'; stream-failure paths pass 'error' so interrupted rows show
    // the X marker instead of a fake green tick.
    const finishTurnSteps = (status = 'ok') => {
        const steps = currentTurnStepsRef.current;
        if (!steps.some((s) => s.status === 'running')) return;
        const now = Date.now();
        currentTurnStepsRef.current = steps.map((s) => (
            s.status === 'running'
                ? { ...s, status, endedAt: now, durationMs: now - s.startedAt }
                : s
        ));
        setLiveSteps(currentTurnStepsRef.current);
    };

    // Live progress note on the most recent running tool step (e.g. read_so's
    // "Opening SO…" → "Reading header…" phases, reported from bmsTools). Liveness
    // only — never mutates status, so completion logic is untouched.
    const noteRunningToolStep = (note) => {
        if (!note) return;
        kickToolStreamWatchdog();
        const steps = currentTurnStepsRef.current;
        for (let i = steps.length - 1; i >= 0; i -= 1) {
            if (steps[i].kind === 'tool' && steps[i].status === 'running') {
                currentTurnStepsRef.current = steps.map((s, j) => (
                    j === i ? { ...s, preview: note } : s
                ));
                setLiveSteps(currentTurnStepsRef.current);
                return;
            }
        }
    };

    // ---- Tool-stream watchdog (300s of NO progress, not 300s total) ----
    // sendToolResultToServer arms the watchdog before reading each tool-result
    // stream. It used to fire on a fixed 300s-from-arm budget, which killed
    // healthy deterministic-flow turns: a create-booking turn shows NO
    // assistant message until its final summary, and one long-but-healthy hop
    // (save_and_confirm's party wipe-repair ladders run ~45s per party, plus
    // stabilization rounds / popup chains) can outlive 300s with the Save
    // already in flight — the user then saw the false "took too long" fallback
    // while the booking committed. Every visible turn progress signal now
    // KICKS (re-arms) the timer: begin/complete of a timeline row and bmsTools
    // reportProgress sub-phase notes. The watchdog therefore fires only when
    // 300s pass with nothing on screen — a genuine hang. Arm/disarm stay owned
    // by sendToolResultToServer (and the card paths' deliberate disarms); the
    // kick is a no-op unless a timer is actually armed, so it can never
    // orphan one.
    const fireToolStreamWatchdog = () => {
        toolStreamTimeoutRef.current = null;
        if (currentTurnAssistantMessageIdsRef.current.length > 0) {
            return; // slow-but-productive turn — an assistant reply is already up
        }
        console.error('[Chatbot] Tool-result stream timeout (300s, no progress)');
        if (toolStreamInactivityClearRef.current) { toolStreamInactivityClearRef.current(); }
        if (toolStreamAbortRef.current) { toolStreamAbortRef.current.abort(); }
        finishTurnSteps('error');
        hideProcessingIndicator();
        addMessage(
            'The browser action ran, but the assistant took too long to finish its reply. Would you like me to re-open the Shipping Order and verify the result?',
            false,
            `tool-timeout-${Date.now()}`
        );
        setDisableInput(false);
    };
    const armToolStreamWatchdog = () => {
        if (toolStreamTimeoutRef.current) { clearTimeout(toolStreamTimeoutRef.current); }
        toolStreamTimeoutRef.current = setTimeout(fireToolStreamWatchdog, 300000);
    };
    const disarmToolStreamWatchdog = () => {
        if (toolStreamTimeoutRef.current) { clearTimeout(toolStreamTimeoutRef.current); toolStreamTimeoutRef.current = null; }
        toolStreamAbortRef.current = null;
        toolStreamInactivityClearRef.current = null;
    };
    const kickToolStreamWatchdog = () => {
        if (!toolStreamTimeoutRef.current) return; // not armed (card wait / stream ended)
        armToolStreamWatchdog();
    };

    // Server tool results arrive as streamed tool messages. Native tool_calls
    // already began a step (complete it); Kimi text-marker flows emit NO
    // tool_calls event, so synthesize the completed row from the message itself.
    // Deduped per message id — partial chunks re-deliver the same message.
    const completeServerToolMessage = (msg, outputText, opts = {}) => {
        const msgId = msg && msg.id;
        if (msgId) {
            if (currentTurnToolMsgIdsRef.current.has(msgId)) return;
            currentTurnToolMsgIdsRef.current.add(msgId);
        }
        const capped = outputText && outputText.length > 4000 ? `${outputText.slice(0, 4000)}…` : outputText;
        const patch = {
            targetKind: 'tool',
            output_text: capped,
            appendPreview: digestToolOutput(outputText, 80),
        };
        if (currentTurnStepsRef.current.some((s) => s.status === 'running')) {
            completeTurnStep(patch);
            return;
        }
        const toolName = (msg && (msg.name || msg.tool_name)) || '';
        const meta = TOOL_META[toolName] || {};
        beginTurnStep({
            kind: 'tool',
            label: meta.label || titleizeToolName(toolName || 'Retrieved context'),
            toolName: toolName || undefined,
            params: {},
        }, opts);
        completeTurnStep({ ...patch, status: 'ok' });
    };

    // Begin timeline rows for native server tool calls (Luna/Responses API).
    // dedupeKey: message id — the same AI message can arrive via partial AND
    // the terminal complete batch.
    const beginServerToolSteps = (toolCalls, opts = {}, dedupeKey = null) => {
        if (dedupeKey) {
            if (currentTurnToolMsgIdsRef.current.has(dedupeKey)) return;
            currentTurnToolMsgIdsRef.current.add(dedupeKey);
        }
        (toolCalls || []).forEach((tc) => {
            const name = (tc && tc.name) || (tc && tc.function && tc.function.name) || 'tool';
            let args = (tc && tc.args) || {};
            if ((!args || Object.keys(args).length === 0) && tc && tc.function && tc.function.arguments) {
                try { args = JSON.parse(tc.function.arguments); } catch (e) { args = {}; }
            }
            const meta = TOOL_META[name] || {};
            beginTurnStep({
                kind: 'tool',
                label: meta.label || titleizeToolName(name),
                toolName: name,
                params: args || {},
                preview: describeToolParams(name, args),
            }, opts);
        });
    };

    // Fold a terminal messages/complete batch into the timeline: AI messages
    // with tool_calls open rows, tool messages close them with their output.
    // Tool-call units and results often bypass token streaming entirely, so
    // this is the only place they become visible actions. Rows are inserted
    // before a trailing answer phase to preserve chronological order.
    const foldCompletedMessagesIntoTimeline = (messages) => {
        (messages || []).forEach((msg) => {
            if (!msg || typeof msg !== 'object') return;
            if (msg.type === 'tool' || msg.role === 'tool') {
                completeServerToolMessage(msg, getStreamMessageContent(msg), { beforeLastPhase: true });
            } else if (msg.tool_calls && msg.tool_calls.length > 0) {
                beginServerToolSteps(msg.tool_calls, { beforeLastPhase: true }, msg.id ? `tc-${msg.id}` : null);
            }
        });
    };

    const addCurrentTurnPhase = (phase) => {
        if (!phase) {
            return;
        }

        const phases = currentTurnThinkingPhasesRef.current;
        if (phases[phases.length - 1] !== phase) {
            currentTurnThinkingPhasesRef.current = [...phases, phase];
        }
        beginTurnStep({ kind: 'phase', label: phase });
    };

    // ---- Multi-agent (rag->bms->da) intermediate-output routing ----
    // Per-domain labels for the multi-agent action trace — surfaces the agentic
    // architecture (a roster of specialized agents) instead of a generic "Sub-agent".
    const AGENT_LABELS = {
        rag: 'Knowledge Retrieval Agent',
        bms: 'BMS Operations Agent',
        da: 'Analytics Agent',
        p44: 'Schedule & Vessel Agent',
        dnd: 'D&D Tariff Agent',
    };
    // True when the agent currently streaming is the LAST in the plan (its output is
    // the reply). Single-intent turns (no plan) are always final -> today's behavior.
    const isFinalAgentStreaming = () => {
        const plan = currentTurnAgentPlanRef.current;
        if (plan.length < 2) return true;
        return currentTurnAgentIdxRef.current >= plan.length - 1;
    };
    const currentAgentLabel = () => {
        const plan = currentTurnAgentPlanRef.current;
        const key = plan[currentTurnAgentIdxRef.current];
        return AGENT_LABELS[key] || 'Specialist Agent';
    };
    // Accumulate an intermediate agent's streamed answer as a trace entry (replaces
    // the previous chunk for the same label so the final text is the full answer).
    const addCurrentTurnAgentStep = (label, text) => {
        const t = (text || '').trim();
        if (!t) return;
        const steps = currentTurnAgentStepsRef.current;
        const ex = steps.find((s) => s.label === label);
        if (ex) ex.text = t;
        else steps.push({ label, text: t });
        currentTurnAgentStepsRef.current = [...steps];
    };

    const addCurrentTurnReasoningTokens = (count) => {
        if (typeof count === 'number' && Number.isFinite(count) && count > 0) {
            currentTurnReasoningTokensRef.current += count;
        }
    };

    // Capture the model's real reasoning/thinking text when the deployment
    // streams it (Kimi reasoning_content). Surfaced in the "Analyzing your
    // question" phase detail instead of a canned description. No-op otherwise.
    const addCurrentTurnReasoningText = (text) => {
        const t = (text || '').trim();
        if (t && currentTurnReasoningTextRef.current.indexOf(t) === -1) {
            currentTurnReasoningTextRef.current = currentTurnReasoningTextRef.current
                ? `${currentTurnReasoningTextRef.current}\n${t}`
                : t;
        }
    };

    const getCurrentTurnThinkingInfo = () => {
        const phases = currentTurnThinkingPhasesRef.current;
        const reasoningTokens = currentTurnReasoningTokensRef.current;
        const startedAt = currentTurnStartedAtRef.current || processingStartTimeRef.current;

        // Surface the full action chain (already tracked for loop-guarding) so the
        // expandable thinking section can show each tool's name + params + result.
        // `ok` is a heuristic: getToolResultText prefixes real errors with
        // "Error executing ..."/"ERROR:" and tool not-found cases report "not found".
        const tools = currentTurnToolHistoryRef.current.map((entry) => {
            const output = entry.output_text || '';
            return {
                tool_name: entry.tool_name,
                params: entry.params || {},
                output_text: output,
                ok: !/^error\b/i.test(output) && !/\bnot found\b/i.test(output)
            };
        });

        const agentSteps = currentTurnAgentStepsRef.current;
        if (!phases.length && reasoningTokens <= 0 && tools.length === 0 && agentSteps.length === 0) {
            return null;
        }

        // Unified chronological timeline for the new COT UI. Any step still
        // marked running (its completion event was missed) closes as 'ok' here;
        // tool steps whose output signals an error keep the error status.
        const agentTextByLabel = {};
        agentSteps.forEach((s) => { if (s && s.label) agentTextByLabel[s.label] = s.text; });
        const now = Date.now();
        const steps = currentTurnStepsRef.current.map((s) => {
            const closed = s.status === 'running'
                ? { ...s, status: 'ok', endedAt: now, durationMs: now - s.startedAt }
                : s;
            if (closed.kind === 'phase' && agentTextByLabel[closed.label]) {
                return { ...closed, agent: true };
            }
            return closed;
        });

        return {
            phases,
            reasoningTokens,
            reasoningText: currentTurnReasoningTextRef.current,
            duration: startedAt ? Date.now() - startedAt : 0,
            tools,
            agentSteps,
            steps
        };
    };

    const stripToolPrefix = (text) => (text || '')
        .replace(/^[a-z_]+:\s*/i, '')        // drop 'read_so_tab: '
        .replace(/^Tab:\s*[^\n]*\n?/i, '')   // drop 'Tab: Booking Approval\n'
        .trim();

    const recordToolResult = (toolName, params, result, flowStep = false) => {
        const outputText = getToolResultText(toolName, result);
        const entry = { tool_name: toolName, params, output_text: outputText, __flowStep: !!flowStep };

        currentTurnToolHistoryRef.current = [
            ...currentTurnToolHistoryRef.current,
            entry
        ];

        if (
            toolName === 'read_so'
            || toolName === 'read_so_tab'
            || toolName === 'read_so_header'
            || toolName === 'read_current_page'
        ) {
            currentTurnLastMeaningfulResultRef.current = entry;
        }
    };

    const buildLoopGuardReply = () => {
        const lastMeaningful = currentTurnLastMeaningfulResultRef.current;
        if (!lastMeaningful) {
            return null;
        }

        const content = stripToolPrefix(lastMeaningful.output_text);
        if (!content) {
            return null;
        }

        if (content.includes('/AccessDeclined') || content.includes('403 Forbidden')) {
            return "I reached the requested booking page, but the system returned 403 Forbidden, so your account does not currently have permission to view that record.";
        }

        // The loop guard is a safety backstop. When it fires after the user asked
        // for an ACTION (approve/save/edit), the action was NOT completed this turn.
        // Say so plainly and invite a retry — never present raw DOM text as a
        // normal answer, and never claim the action succeeded. (M2/M4/M5)
        const tabOrPage = lastMeaningful.params && (lastMeaningful.params.tab_name || lastMeaningful.params.tab)
            ? (lastMeaningful.params.tab_name || lastMeaningful.params.tab)
            : 'the current page';
        const guardNote = `[Action not completed] I read ${tabOrPage} but did not finish the requested action in this turn. If you asked me to approve, change, or submit something, please re-send the request and I will perform it.\n\n`;
        const fenced = content.split('\n').length > 1 || content.length > 120
            ? `\`\`\`\n${content}\n\`\`\``
            : content;
        return `${guardNote}${fenced}`;
    };

    const shouldFinalizeToolLoop = (nextToolCalls) => {
        const lastMeaningful = currentTurnLastMeaningfulResultRef.current;
        const history = currentTurnToolHistoryRef.current;

        // 403 / access-declined short-circuit. read_so now PROPAGATES the underlying
        // open_shipping_order ERROR (it used to swallow it and report "Opened SO X
        // but could not read header"), so the compound tool's output can carry the
        // denial too — finalize immediately with the canned no-permission reply
        // instead of letting the agent retry read tools against a 403 page.
        if (['read_current_page', 'read_so', 'read_so_tab', 'read_so_header'].includes(lastMeaningful?.tool_name)) {
            const output = lastMeaningful.output_text || '';
            if (output.includes('/AccessDeclined') || output.includes('403 Forbidden')) {
                return true;
            }
        }

        // Only cut the loop on a TRUE redundant read: the LLM is about to call
        // read_so_tab for a tab it ALREADY read this turn, with NO intervening
        // action tool. Previously the guard fired on ANY next call that wasn't
        // open_shipping_order, which discarded click_button('Approve') and other
        // actions after a read and substituted a canned "I already retrieved the
        // requested tab data..." reply (the exact M4 symptom). Everything else —
        // click_button, open_so_for_edit, handle_popup_confirm, navigate, paginate,
        // another read_so_header, etc. — must be allowed to run. (M2/M4)
        const ACTION_TOOL_NAMES = new Set([
            'click_button', 'click_list_item', 'open_so_for_edit', 'open_shipping_order',
            'switch_so_tab', 'navigate_to_page', 'search_booking', 'paginate',
            'apply_filters', 'apply_custom_filter', 'handle_popup_confirm',
            'dismiss_all_popups', 'batch_form_actions', 'fill_form', 'read_form',
            'fill_table_cell', 'select_option', 'set_date', 'toggle_checkbox', 'select_radio',
            'click_ref', 'fill_ref', 'select_ref'
        ]);
        const lastMeaningfulIdx = history.findIndex((h) => h === lastMeaningful);
        const anyActionSinceLastRead = history
            .slice(lastMeaningfulIdx + 1)
            .some((h) => ACTION_TOOL_NAMES.has(h.tool_name));

        if (
            !anyActionSinceLastRead
            && lastMeaningful?.tool_name === 'read_so_tab'
            && nextToolCalls.some((c) => {
                // The flow engine's settle-delayed verify re-read carries
                // _verify_retry — a deliberate same-tab re-read after a post-save
                // DOM race, NOT an LLM loop. This guard polices the improvising
                // LLM only; let the deterministic engine's re-read through
                // (it has its own hard-stop failure policy).
                if (c.params && c.params._verify_retry) return false;
                const incomingTab = c.params && (c.params.tab_name || c.params.tab);
                const lastTab = lastMeaningful.params && (lastMeaningful.params.tab_name || lastMeaningful.params.tab);
                return c.tool_name === 'read_so_tab'
                    && (!incomingTab || !lastTab || String(incomingTab).toLowerCase() === String(lastTab).toLowerCase());
            })
        ) {
            return true;
        }

        // Absolute safety cap for runaway loops, split by call origin
        // (2026-10-23, live S202610137). FREE-FORM LLM calls keep the 40 cap.
        // DETERMINISTIC-FLOW steps — recorded with __flowStep, since the
        // engine stamps every emitted step's marker with "flow": true — are
        // exempt from it: they are bounded server-side (finite step lists ×
        // the chain-queue cap, settle/retry/poll caps, the recovery action
        // budget), and the seven-entry ultimate chain (repeated approval
        // halves) legitimately issued ~45+ steps in ONE turn and died exactly
        // on call #40 mid docs flow — create_confirm's result was discarded,
        // the backend waited forever for it, and the turn ended with the
        // "[Action not completed]" guard note while the final approval half
        // silently never ran. Flow steps keep a far higher absolute backstop
        // so a true runaway still dies; STREAM_INACTIVITY_TIMEOUT_MS bounds
        // stalls either way.
        const flowCallCount = history.filter((h) => !!h.__flowStep).length;
        const freeformCallCount = history.length - flowCallCount;
        if (freeformCallCount >= 40) {
            return true;
        }
        return flowCallCount >= 250;
    };

    const finalizeToolLoop = () => {
        if (currentTurnFinalizedRef.current) {
            return true;
        }

        const fallbackReply = buildLoopGuardReply();
        if (!fallbackReply && currentTurnAssistantMessageIdsRef.current.length === 0) {
            return false;
        }

        currentTurnFinalizedRef.current = true;

        const thinkingInfo = getCurrentTurnThinkingInfo();
        const existingMessageIds = currentTurnAssistantMessageIdsRef.current;

        if (existingMessageIds.length > 0) {
            const latestMessageId = existingMessageIds[existingMessageIds.length - 1];
            if (thinkingInfo) {
                const latestMessage = chatMsg.find((message) => message.messageId === latestMessageId);
                if (latestMessage) {
                    updateMessage(latestMessageId, latestMessage.content, latestMessage.citations, thinkingInfo);
                }
            }
            setDisableInput(false);
            hideProcessingIndicator();
            return true;
        }

        const finalMessageId = `tool-loop-final-${Date.now()}`;
        addMessage(fallbackReply, false, finalMessageId, null, thinkingInfo);
        trackAssistantMessage(finalMessageId);
        setDisableInput(false);
        hideProcessingIndicator();
        return true;
    };

    const showTypingIndicator = () => {
        setShowTyping(true);
    };

    const showProcessingIndicator = () => {
        processingStartTimeRef.current = Date.now();
        setShowProcessing(true);
        setThinkingPhase('Analyzing your question');
    };

    const hideProcessingIndicator = () => {
        // Ensure processing indicator shows for at least 500ms
        const elapsed = processingStartTimeRef.current ? Date.now() - processingStartTimeRef.current : 0;
        const minDelay = minProcessingDelayRef.current;

        if (elapsed < minDelay) {
            // Hide it after the minimum delay
            setTimeout(() => {
                setShowProcessing(false);
                setThinkingPhase('');
            }, minDelay - elapsed);
        } else {
            // Already shown for enough time, hide immediately
            setShowProcessing(false);
            setThinkingPhase('');
        }
    };

    const hideTypingIndicator = () => {
        setShowTyping(false);
    };

    const toggleThinking = (messageId) => {
        setExpandedThinking(prev => {
            const next = new Set(prev);
            if (next.has(messageId)) {
                next.delete(messageId);
            } else {
                next.add(messageId);
            }
            return next;
        });
    };

    // Copy the rendered text of a bot bubble (what the user sees, not raw
    // markdown) to the clipboard, with a brief "copied" check-mark feedback.
    const copyMessage = async (e, messageId) => {
        e.stopPropagation();
        try {
            const bubble = e.currentTarget.closest('.message-text');
            const text = bubble ? bubble.innerText : '';
            await navigator.clipboard.writeText(text);
            setCopiedId(messageId);
            setTimeout(() => setCopiedId((id) => (id === messageId ? null : id)), 1500);
        } catch (err) {
            console.warn('[Chatbot] copy failed:', err);
        }
    };

    // Resolve the in-flight confirmation card. Single-flight: a late second click
    // or a late stream chunk is a no-op once pendingConfirmationRef is null.
    const resolveConfirmation = useCallback((choice, edits) => {
        const p = pendingConfirmationRef.current;
        if (!p) return;
        pendingConfirmationRef.current = null;
        setPendingConfirmation(null);
        setConfirmationEdit(null);
        setChatMsg(prev => prev.map(m => m.messageId === p.cardMessageId ? { ...m, resolvedChoice: choice } : m));
        setTimeout(() => setChatMsg(prev => prev.filter(m => m.messageId !== p.cardMessageId)), 1500);
        p.resolve({ choice, edits: edits || [] });
    }, []);
    const startKeepEdit = useCallback((cardMessageId, confirmation) => {
        const items = (confirmation.prepared || []).map(x => ({ ...x, editValue: x.value }));
        setConfirmationEdit({ cardMessageId: cardMessageId, items: items });
    }, []);
    // Pending flow pre-flight card (same single-flight pattern as the save gate).
    const pendingFlowConfirmRef = useRef(null);
    const resolveFlowConfirm = useCallback((choice) => {
        const p = pendingFlowConfirmRef.current;
        if (!p) return;
        pendingFlowConfirmRef.current = null;
        setChatMsg(prev => prev.map(m => m.messageId === p.cardMessageId ? { ...m, resolvedChoice: choice } : m));
        setTimeout(() => setChatMsg(prev => prev.filter(m => m.messageId !== p.cardMessageId)), 1500);
        p.resolve({ choice: choice });
    }, []);
    const rejectPendingFlowConfirm = useCallback(() => {
        const p = pendingFlowConfirmRef.current;
        if (!p) return;
        pendingFlowConfirmRef.current = null;
        sendAbandonedCardResult('__flow_confirm__', 'RESULT: user_rejected (flow cancelled by user)');
        setChatMsg(prev => prev.filter(m => m.messageId !== p.cardMessageId));
        p.reject(new Error('CLEARED'));
    }, [sendAbandonedCardResult]);
    // Pending ask_user_choice card (same single-flight pattern as the flow card).
    const pendingUserChoiceRef = useRef(null);
    // Best-effort server-side cancellation of an abandoned client-side pseudo
    // tool (ask_user_choice / __flow_confirm__ / persist gate). When the chat
    // window is closed, the page unloaded, or a new conversation started while
    // a card is pending, the awaited promise is rejected and the turn aborts
    // locally — but the server thread keeps the unanswered tool call as its
    // last message. The thread is silently RESUMED on the next open (via
    // sessionStorage), so the model then meets its own dangling question and
    // replies confused (observed 2026-10-02: "I haven't identified a specific
    // IDE MOEBLER party in this chat. Please share the party-list entries…").
    // Fire-and-forget the same RESULT envelope a Cancel click would have sent,
    // so the thread ends clean instead.
    const sendAbandonedCardResult = useCallback((toolName, outputText) => {
        const tid = threadIdRef.current;
        if (!tid || !toolName) return;
        const payload = {
            assistant_id: ASSISTANT_ID,
            input: {
                messages: [{
                    role: 'user',
                    content: `__TOOL_RESULT__:${JSON.stringify({
                        tool_name: toolName,
                        result: {
                            success: true,
                            result: {
                                content: [{ type: 'text', text: outputText }],
                                tool_results: [{ tool_name: toolName, output_text: outputText }]
                            }
                        }
                    })}`
                }]
            },
            config: approvalModeConfig(),
            stream_mode: ['messages', 'updates']
        };
        try {
            // keepalive: the abandon paths (window close, page unload) must not
            // cancel the request; a failure is silently ignored — this is
            // cleanup, never a user-visible action.
            fetch(`${LANGGRAPH_URL}/threads/${tid}/runs/stream`, {
                method: 'POST',
                headers: getAuthHeaders(),
                mode: 'cors',
                keepalive: true,
                body: JSON.stringify(payload)
            }).catch(() => { });
        } catch (e) { /* best-effort by design */ }
    }, []);
    const resolveUserChoice = useCallback((choice, sel) => {
        const p = pendingUserChoiceRef.current;
        if (!p) return;
        pendingUserChoiceRef.current = null;
        // A resolved card used to self-delete after a fixed 1.5s, but the
        // tool-result round-trip back to the server takes seconds — the card
        // vanished and the chat sat blank until the next reply streamed in.
        // Instead flag it: it stays visible (frozen on the user's pick) and is
        // retired in the same state update that renders the next assistant
        // reply (see addMessage / updateMessage / sendMessage).
        setChatMsg(prev => prev.map(m => m.messageId === p.cardMessageId ? { ...m, resolvedChoice: choice, retireOnReply: true } : m));
        p.resolve({ choice: choice, index: sel ? sel.index : undefined, label: sel ? sel.label : undefined, text: sel ? sel.text : undefined });
    }, []);
    const rejectPendingUserChoice = useCallback(() => {
        const p = pendingUserChoiceRef.current;
        if (!p) return;
        pendingUserChoiceRef.current = null;
        sendAbandonedCardResult('ask_user_choice', 'RESULT: user_rejected (user cancelled the selection)');
        setChatMsg(prev => prev.filter(m => m.messageId !== p.cardMessageId));
        p.reject(new Error('CLEARED'));
    }, [sendAbandonedCardResult]);
    // Retire (WITHOUT a server envelope) any pending flow/choice card when the
    // user sends a chat message or a card edit instead of clicking. The reject
    // helpers above fire a 'RESULT: user_rejected…' envelope — a SERVER-SIDE
    // CANCEL — which would abort a flow the user is still completing; this only
    // clears the local promise and flags the card 'superseded' (dimmed, kept as
    // audit). handleToolCall's catch swallows the 'CLEARED' rejection silently.
    const retirePendingCards = useCallback(() => {
        for (const ref of [pendingFlowConfirmRef, pendingUserChoiceRef]) {
            const p = ref.current;
            if (!p) continue;
            ref.current = null;
            setChatMsg(prev => prev.map(m => (m.messageId === p.cardMessageId
                ? { ...m, resolvedChoice: 'superseded' }
                : m)));
            p.reject(new Error('CLEARED'));
        }
    }, []);
    const rejectAllPendingConfirmations = useCallback(() => {
        rejectPendingFlowConfirm();
        rejectPendingUserChoice();
        const p = pendingConfirmationRef.current;
        if (!p) return;
        pendingConfirmationRef.current = null;
        sendAbandonedCardResult(p.tool_name, 'RESULT: user_rejected');
        setPendingConfirmation(null);
        setConfirmationEdit(null);
        setChatMsg(prev => prev.filter(m => m.messageId !== p.cardMessageId));
        p.reject(new Error('CLEARED'));
    }, [rejectPendingFlowConfirm, rejectPendingUserChoice, sendAbandonedCardResult]);

    // Last-chance abandon-cancel for paths the reject handlers never reach: page
    // refresh/close (beforeunload/pagehide) and component unmount (SPA route
    // change). Nulls each ref as it fires so the send is idempotent when more
    // than one hook fires on the same teardown (e.g. beforeunload + unmount).
    useEffect(() => {
        const abandonPendingCards = () => {
            const flow = pendingFlowConfirmRef.current;
            if (flow) {
                pendingFlowConfirmRef.current = null;
                sendAbandonedCardResult('__flow_confirm__', 'RESULT: user_rejected (flow cancelled by user)');
            }
            const choice = pendingUserChoiceRef.current;
            if (choice) {
                pendingUserChoiceRef.current = null;
                sendAbandonedCardResult('ask_user_choice', 'RESULT: user_rejected (user cancelled the selection)');
            }
            const confirm = pendingConfirmationRef.current;
            if (confirm && confirm.tool_name) {
                pendingConfirmationRef.current = null;
                sendAbandonedCardResult(confirm.tool_name, 'RESULT: user_rejected');
            }
        };
        window.addEventListener('beforeunload', abandonPendingCards);
        window.addEventListener('pagehide', abandonPendingCards);
        return () => {
            window.removeEventListener('beforeunload', abandonPendingCards);
            window.removeEventListener('pagehide', abandonPendingCards);
            abandonPendingCards();
        };
    }, [sendAbandonedCardResult]);

    const showError = (message) => {
        setErrorMsg(message);

        setTimeout(() => {
            setErrorMsg('');
        }, 5000);
        
    };

    // LangGraph API Functions
    const createThread = async () => {
        try {
            const response = await fetchWithTimeout(`${LANGGRAPH_URL}/threads`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    metadata: { created_at: new Date().toISOString() }
                }),
                mode: 'cors'
            });

            if (!response.ok) {
                throw new Error(`Failed to create thread: ${response.statusText}`);
            }

            const data = await response.json();

            // Store thread ID in sessionStorage for persistence across page refreshes
            sessionStorage.setItem('langgraph_thread_id', data.thread_id);
            //console.log('Thread created:', data.thread_id);

            // Clear conversation history when creating a new thread
            conversationHistory = [];
            console.log('Conversation history cleared for new thread');

            return data.thread_id;
        } catch (error) {
            console.error('Error creating thread:', error);
            throw error;
        }
    };

    const verifyThreadExists = async (id) => {
        const response = await fetchWithTimeout(`${LANGGRAPH_URL}/threads/${id}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            mode: 'cors'
        });
        if (response.ok) {
            return true;
        }
        if (response.status === 404) {
            return false;
        }
        throw new Error(`Unexpected status verifying thread: ${response.status}`);
    };

    // >>> LANGGRAPH CHATBOT INTEGRATION — Kimi Tool Call Parser >>>
    /**
     * Parse Kimi-K2.5's raw tool call tokens from streaming content.
     * Kimi outputs: <|tool_call_begin|>functions.NAME:ID<|tool_call_argument_begin|>JSON<|tool_call_end|>
     * Converts to: __TOOL_CALL__:{"tool_call": {"tool_name": "...", "params": {...}}}
     */
    function parseKimiToolCall(content) {
        const match = content.match(/<\|tool_call_begin\|>functions\.([^:]+):\d+<\|tool_call_argument_begin\|>(.*?)<\|tool_call_end\|>/);
        if (!match) return null;

        let toolName = match[1];

        try {
            const params = JSON.parse(match[2]);
            // Map parameter names from prompt conventions to actual tool params
            const paramMapping = { page_key: 'page' };
            const mappedParams = {};
            for (const [k, v] of Object.entries(params)) {
                mappedParams[paramMapping[k] || k] = v;
            }
            return `__TOOL_CALL__:${JSON.stringify({
                tool_call: { tool_name: toolName, params: mappedParams }
            })}`;
        } catch (e) {
            console.error('[Chatbot] Failed to parse Kimi tool args:', e);
            return null;
        }
    }

        /**
         * Extract embedded tool instructions from assistant content.
         * Supports JSON markers and XML-style <function_calls> blocks.
         */
        function extractToolInstructionsFromContent(content) {
            if (typeof content !== 'string' || !content) {
                return null;
            }

            const xmlStart = content.indexOf('<function_calls>');
            const xmlEnd = content.indexOf('</function_calls>');
            if (xmlStart !== -1 && xmlEnd !== -1) {
                const xmlBlock = content.slice(xmlStart, xmlEnd + '</function_calls>'.length);
                const toolCalls = [];
                const invokePattern = /<invoke\s+name="([^"]+)">([\s\S]*?)<\/invoke>/g;
                let invokeMatch;

                while ((invokeMatch = invokePattern.exec(xmlBlock)) !== null) {
                    const [, toolName, body] = invokeMatch;
                    const params = {};
                    const argumentPattern = /<(parameter|arg)\s+name="([^"]+)">([\s\S]*?)<\/(parameter|arg)>/g;
                    let argumentMatch;

                    while ((argumentMatch = argumentPattern.exec(body)) !== null) {
                        const [, , name, value] = argumentMatch;
                        params[name] = value.trim();
                    }

                    toolCalls.push({ tool_name: toolName, params });
                }

                if (toolCalls.length > 0) {
                    return {
                        toolCalls,
                        leadingText: content.slice(0, xmlStart).trim(),
                        trailingText: content.slice(xmlEnd + '</function_calls>'.length).trim()
                    };
                }
            }

            const prefixes = ['__TOOL_CALL__:', 'TOOL_CALL:', 'TOOL_CALL('];
            const genericMarkerMatches = Array.from(content.matchAll(/__[A-Z_]+__:/g)).map(match => match[0]);
            for (const marker of genericMarkerMatches) {
                if (!prefixes.includes(marker)) {
                    prefixes.push(marker);
                }
            }
            let markerIndex = -1;
            let matchedPrefix = '';

            for (const prefix of prefixes) {
                const index = content.indexOf(prefix);
                if (index !== -1 && (markerIndex === -1 || index < markerIndex)) {
                    markerIndex = index;
                    matchedPrefix = prefix;
                }
            }

            if (markerIndex === -1) {
                return null;
            }

            const jsonStart = markerIndex + matchedPrefix.length;
            const jsonSlice = content.slice(jsonStart).trimStart();
            const firstBraceIndex = jsonSlice.indexOf('{');

            if (firstBraceIndex === -1) {
                return null;
            }

            let depth = 0;
            let inString = false;
            let isEscaped = false;
            let endIndex = -1;

            for (let index = firstBraceIndex; index < jsonSlice.length; index += 1) {
                const ch = jsonSlice[index];

                if (inString) {
                    if (isEscaped) {
                        isEscaped = false;
                    } else if (ch === '\\') {
                        isEscaped = true;
                    } else if (ch === '"') {
                        inString = false;
                    }
                    continue;
                }

                if (ch === '"') {
                    inString = true;
                    continue;
                }

                if (ch === '{') {
                    depth += 1;
                } else if (ch === '}') {
                    depth -= 1;
                    if (depth === 0) {
                        endIndex = index;
                        break;
                    }
                }
            }

            if (endIndex === -1) {
                return null;
            }

            const commandJson = jsonSlice.slice(firstBraceIndex, endIndex + 1);

            try {
                const parsedCommand = JSON.parse(commandJson);
                let command = parsedCommand;

                // Deterministic-flow pre-flight card: the server ends its turn with
                // __FLOW_CONFIRM__:{...} (flow title + grouped parameters). Wrap it as
                // a pseudo tool call so every marker-scanning site hands it to
                // handleToolCall, whose '__flow_confirm__' branch renders the card.
                if (matchedPrefix === '__FLOW_CONFIRM__:' && command.flow) {
                    command = {
                        tool_call: {
                            tool_name: '__flow_confirm__',
                            params: command
                        }
                    };
                }

                if (!command.tool_call && command.tool) {
                    command = {
                        tool_call: {
                            tool_name: command.tool,
                            params: command.args || {}
                        }
                    };
                }

                if (!command.tool_call) {
                    return null;
                }

                // Flow-step stamp (2026-10-22): the server marks deterministic-
                // flow steps with a top-level "flow" field on the marker (same
                // shape __FLOW_CONFIRM__ uses). Carried on the tool_call so the
                // execution loop can tell bmsTools a pre-confirmed flow step is
                // running — its navigation guard then auto-answers the SO
                // leave-guard instead of freezing the page on a native
                // window.confirm an unattended chain can never answer.
                return {
                    toolCalls: [{ ...command.tool_call, __flowStep: !!command.flow }],
                    commandText: `__TOOL_CALL__:${commandJson}`,
                    leadingText: content.slice(0, markerIndex).trim(),
                    trailingText: jsonSlice.slice(endIndex + 1).trim()
                };
            } catch (e) {
                console.error('[Chatbot] Failed to parse embedded tool call:', e, content);
                return null;
            }
        }

        function extractToolCallFromContent(content) {
            const extracted = extractToolInstructionsFromContent(content);
            if (!extracted || !extracted.toolCalls || extracted.toolCalls.length === 0) {
                return null;
            }

            return {
                command: {
                    tool_call: extracted.toolCalls[0],
                    tool_calls: extracted.toolCalls
                },
                leadingText: extracted.leadingText,
                trailingText: extracted.trailingText,
                commandText: extracted.commandText || ''
            };
        }

        function getToolResultText(toolName, result) {
            if (result?.error) {
                return `Error executing ${toolName}: ${result.error}`;
            }

            const text = result?.result?.content?.[0]?.text;
            if (text) {
                return `${toolName}: ${text}`;
            }

            return `${toolName}: completed successfully`;
        }

        function hasToolCallMarker(content) {
            return typeof content === 'string'
                && (
                    content.includes('__TOOL_CALL')
                    || /__[A-Z_]+__:/i.test(content)
                    || content.includes('TOOL_CALL')
                    || content.includes('<function_calls')
                    || content.includes('<invoke ')
                );
        }

        function looksLikeToolCallPrefix(content) {
            const trimmed = typeof content === 'string' ? content.trim() : '';
            return trimmed.startsWith('__') || trimmed.startsWith('<') || hasToolCallMarker(trimmed);
        }

        /**
         * Strip Kimi/tool-call artifacts from streaming content so raw tokens
         * (e.g. `<|tool_calls_section_begin|>functions.retrieve_chroma_docs:0{...}`)
         * NEVER render to the user as the answer. Anything from the first tool
         * marker onward is dropped; stray partial markers are removed. This is
         * the core fix for the "unreadable chain-of-thought" leak (M2/CoT).
         */
        const TOOL_STREAM_MARKERS = [
            '<|tool_calls_section_begin|>', '<|tool_call_begin|>',
            '<|tool_call_argument_begin|>', '<|tool_call_end|>',
            '<|tool_calls_section_end|>', '__TOOL_CALL__:', '__FLOW_CONFIRM__:',
            '<function_calls>', '<invoke ',
        ];
        function sanitizeStreamContent(text) {
            if (!text) return '';
            let t = String(text);
            let cut = t.length;
            for (const mk of TOOL_STREAM_MARKERS) {
                const i = t.indexOf(mk);
                if (i !== -1 && i < cut) cut = i;
            }
            t = t.slice(0, cut);
            // remove any stray partial <|...|> tokens or XML tool tags
            t = t.replace(/<\|[^|]*\|>/g, '').replace(/<\/?(?:function_calls|invoke|parameter|arg)\b[^>]*>/g, '');
            return t.trim();
        }

        function isStreamToolMessage(msg) {
            return msg?.type === 'ai' || msg?.role === 'assistant' || msg?.type === 'tool';
        }

        function getStreamMessageContent(msg) {
            if (!msg) {
                return '';
            }

            if (typeof msg.content === 'string') {
                return msg.content;
            }

            if (Array.isArray(msg.content)) {
                return msg.content
                    .map(c => typeof c === 'string' ? c : c.text || '')
                    .join('');
            }

            return '';
        }

        // Extract reasoning/thinking text the model emitted (Kimi
        // reasoning_content, or LangGraph content blocks of type
        // thinking/reasoning). Returns '' when the deployment streams none —
        // caller then falls back to the canned description.
        function getStreamReasoningContent(msg) {
            if (!msg) return '';
            let out = '';
            if (Array.isArray(msg.content)) {
                out = msg.content
                    .map((c) => {
                        if (!c || typeof c === 'string') return '';
                        if (c.type === 'thinking' || c.type === 'reasoning' || c.type === 'reasoning_text') {
                            return c.thinking || c.reasoning || c.text || '';
                        }
                        return '';
                    })
                    .join('')
                    .trim();
            }
            if (!out) {
                const rc = msg.reasoning_content || msg.reasoning || msg.additional_kwargs?.reasoning_content;
                if (typeof rc === 'string') out = rc.trim();
            }
            return out;
        }
    // <<< LANGGRAPH CHATBOT INTEGRATION — Kimi Tool Call Parser <<<

    // >>> LANGGRAPH CHATBOT INTEGRATION — Tool Call Handler >>>
    /**
     * Handle a __TOOL_CALL__ message from the LangGraph server.
     * Executes the tool and sends the result back.
     * Returns true if handled (don't display as chat message).
     */
    // Capture a form-mutation tool's values into the per-turn prepared-changes ref
    // so the confirmation card can show (and Keep-edit) exactly what the agent set.
    function captureFormMutation(toolName, params) {
        if (!FORM_MUTATION_TOOLS.has(toolName)) return;
        const entries = buildFormChangeEntries(toolName, params);
        if (!entries.length) return;
        const arr = preparedFormChangesRef.current;
        entries.forEach(function (e) {
            const idx = arr.findIndex(function (x) { return x.id === e.id; });
            if (idx >= 0) arr[idx] = e; else arr.push(e);
        });
    }

    // Re-apply the user's edited values to the LIVE form via the primitive tools
    // (these go through bmsTools.executeTool, NOT handleToolCall, so no re-gate).
    async function reapplyFormChanges(editedItems) {
        const applied = [];
        const failed = [];
        for (const it of (editedItems || [])) {
            let tool = it.tool;
            let prms = null;
            if (it.tool === 'fill_form') prms = { fields: [{ label: it.label, name: it.name, value: it.editValue }] };
            else if (it.tool === 'select_option') prms = { label: it.label, name: it.name, value: it.editValue };
            else if (it.tool === 'set_date') prms = { label: it.label, name: it.name, value: it.editValue };
            else if (it.tool === 'toggle_checkbox') prms = { label: it.label, name: it.name, checked: it.editValue };
            else if (it.tool === 'select_radio') prms = { label: it.label, name: it.name, value: it.editValue };
            else if (it.tool === 'fill_table_cell') prms = { row_identifier: String(it.row_identifier || ''), column_name: it.column_name, value: it.editValue };
            else if (it.tool === 'select_proposed_sailing') prms = { option_number: parseInt(String(it.editValue).replace(/[^0-9]/g, ''), 10) || 1 };
            else if (it.tool === 'fill_ref') {
                // Re-apply by LABEL via the named tool — a ref can go stale while
                // the gate card waits for the user, a label cannot.
                tool = 'fill_form';
                prms = { fields: [{ label: it.label, value: it.editValue }] };
            }
            else if (it.tool === 'select_ref') {
                tool = 'select_option';
                prms = { label: it.label, value: it.editValue };
            }
            if (!prms) continue;
            try {
                const r = await executeTool(tool, prms);
                const rText = (r && r.success && r.result && r.result.content && r.result.content[0] && r.result.content[0].text) ? r.result.content[0].text : '';
                if (r && r.error) failed.push(it.label + ': ' + r.error);
                else if (/not found|could not|failed|not editable|no proposed/i.test(rText)) failed.push(it.label + ': ' + rText);
                else applied.push(it.label + '=' + it.editValue);
            } catch (err) {
                failed.push(it.label + ': ' + err.message);
            }
        }
        const summary = applied.length ? ('Re-applied: ' + applied.join(', ')) : 'no edits';
        return { summary: summary, failed: failed };
    }

    function failedKeepText(reapply) {
        if (reapply.failed && reapply.failed.length) {
            return 'RESULT: user_kept (re-apply failed: ' + reapply.failed.join('; ') + ') -- the SO remains in edit mode, unsaved, NOTHING committed. Do NOT retry the save; tell the user which field(s) could not be re-applied and ask how to proceed.';
        }
        return 'RESULT: user_kept (' + reapply.summary + '; record left unsaved) -- the SO remains in edit mode, unsaved, NOTHING committed. Tell the user the edits are kept but not saved, and offer to save on request.';
    }

    // Gate a persist tool: show the confirmation card, await the user's decision,
    // then execute (accept), re-apply edits without saving (keep), or abort (reject).
    async function runPersistWithConfirmation(toolName, params) {
        // Disarm the 300s tool-stream watchdog before awaiting a human, else a slow
        // user trips the false 'took too long' fallback under the still-pending card.
        if (toolStreamTimeoutRef.current) { clearTimeout(toolStreamTimeoutRef.current); toolStreamTimeoutRef.current = null; }
        let prepared = preparedFormChangesRef.current.slice();
        if (toolName === 'approve_booking') {
            prepared = prepared.concat([
                { id: 'approve|approval_status', tool: 'select_option', label: 'Approval Status', name: '', value: 'Approved', displayValue: 'Approved', type: 'text' },
                { id: 'approve|sailing_option', tool: 'select_proposed_sailing', label: 'Sailing option', name: '', value: 'option ' + (params.sailing_option || 1), displayValue: 'option ' + (params.sailing_option || 1), type: 'text' }
            ]);
        }
        const cardMessageId = 'confirm-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7);
        // "Edit automatically": no click. The card still renders — pre-resolved
        // on Accept and retired with the next reply — as an audit record of what
        // was committed, then the persist runs immediately.
        if (readApprovalMode() === 'auto') {
            setChatMsg(function (prev) {
                return prev.concat([{ messageId: cardMessageId, isUser: false, kind: 'confirmation', content: '', time: getCurrentTime(), ts: Date.now(), resolvedChoice: 'accept', retireOnReply: true, confirmation: { tool_name: toolName, params: params, prepared: prepared, autoApproved: true } }]);
            });
            setShowProcessing(true);
            return await executeTool(toolName, params);
        }
        setShowProcessing(false);
        setChatMsg(function (prev) {
            return prev.concat([{ messageId: cardMessageId, isUser: false, kind: 'confirmation', content: '', time: getCurrentTime(), ts: Date.now(), confirmation: { tool_name: toolName, params: params, prepared: prepared } }]);
        });
        setPendingConfirmation({ tool_name: toolName, params: params, prepared: prepared, cardMessageId: cardMessageId });
        const decision = await new Promise(function (resolve, reject) {
            pendingConfirmationRef.current = { tool_name: toolName, params: params, prepared: prepared, resolve: resolve, reject: reject, cardMessageId: cardMessageId };
        });
        // Resume the processing timeline for the execution itself: the card was
        // shown with showProcessing off, and accept runs the persist tool
        // client-side for a minute or more (save_and_confirm = re-apply +
        // Save + popup chain). Without this the chat shows an empty gap until
        // the next server stream opens — the "reply UI disappeared" report
        // (2026-09-21). The timeline row for this tool is already open (the
        // outer loop began it before the gate), so the spinner + row render
        // again immediately.
        setShowProcessing(true);
        if (decision.choice === 'accept') {
            return await executeTool(toolName, params);
        }
        if (decision.choice === 'keep') {
            const reapply = await reapplyFormChanges(decision.edits || []);
            return { success: true, __gateDecision: 'keep', result: { content: [{ type: 'text', text: failedKeepText(reapply) }], tool_results: [{ tool_name: toolName, output_text: 'RESULT: user_kept' }] }, toolName: toolName };
        }
        return { success: true, __gateDecision: 'reject', result: { content: [{ type: 'text', text: rejectTextFor(toolName) }], tool_results: [{ tool_name: toolName, output_text: 'RESULT: user_rejected' }] }, toolName: toolName };
    }

    // No-data budget for a live SSE stream. Matches the documented 180s frontend
    // timeout: an open-but-silent stream (backend stall, Kimi full-buffer, proxy
    // drop) is aborted so the turn visibly ends instead of spinning forever.
    const STREAM_INACTIVITY_TIMEOUT_MS = 180000;

    // Show the flow pre-flight card and await the user's Confirm / Cancel. The
    // outcome is reported in the RESULT vocabulary the server engine classifies:
    // success = confirmed, user_rejected = cancelled.
    async function runFlowConfirmCard(params) {
        if (toolStreamTimeoutRef.current) { clearTimeout(toolStreamTimeoutRef.current); toolStreamTimeoutRef.current = null; }
        setShowProcessing(false);
        const cardMessageId = 'flow-confirm-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7);
        // "Edit automatically": a card should not normally arrive (the server
        // skips the precheck park in auto mode), but a thread parked at
        // "confirming" before the switch, or an older server build, still sends
        // one — resolve it as confirmed without waiting for a click. The card
        // renders pre-resolved and retires with the next reply (audit record).
        if (readApprovalMode() === 'auto') {
            setChatMsg(function (prev) {
                return markStaleFlowCards(prev).concat([{ messageId: cardMessageId, isUser: false, kind: 'flow_confirmation', content: '', time: getCurrentTime(), ts: Date.now(), flowConfirm: { ...(params || {}), autoApproved: true }, resolvedChoice: 'confirm', retireOnReply: true }]);
            });
            setEditInFlight(false); // a card rendered — clear the edit busy flag (see below)
            setShowProcessing(true);
            const autoText = 'RESULT: success (flow auto-confirmed — Edit automatically mode)';
            return {
                success: true,
                result: {
                    content: [{ type: 'text', text: autoText }],
                    tool_results: [{ tool_name: '__flow_confirm__', output_text: autoText }]
                }
            };
        }
        setChatMsg(function (prev) {
            return markStaleFlowCards(prev).concat([{ messageId: cardMessageId, isUser: false, kind: 'flow_confirmation', content: '', time: getCurrentTime(), ts: Date.now(), flowConfirm: params || {} }]);
        });
        // The re-emitted card IS the ack for an in-flight __FLOW_EDIT__: unlock
        // the edit affordances / Confirm/Cancel NOW (design §5.2 — the busy flag
        // clears "when the next card renders"), not when the edit's own stream
        // resolves — that stream is blocked inside handleToolCall awaiting THIS
        // card's promise, so waiting for it would deadlock the buttons.
        setEditInFlight(false);
        const decision = await new Promise(function (resolve, reject) {
            pendingFlowConfirmRef.current = { resolve: resolve, reject: reject, cardMessageId: cardMessageId };
        });
        // Resume the processing timeline immediately — the card was shown with
        // it off, and the tool-result round-trip to the server takes seconds;
        // without this the chat blanks out after pressing Confirm.
        setShowProcessing(true);
        const text = decision.choice === 'confirm'
            ? 'RESULT: success (flow confirmed by user)'
            : 'RESULT: user_rejected (flow cancelled by user)';
        return {
            success: true,
            result: {
                content: [{ type: 'text', text: text }],
                tool_results: [{ tool_name: '__flow_confirm__', output_text: text }]
            }
        };
    }

    // Show the ask_user_choice selection card and await the user's pick. Mirrors
    // runFlowConfirmCard: the decision travels back as a normal __TOOL_RESULT__
    // envelope (tool_name 'ask_user_choice') the server classifies — success =
    // a selected option (1-based) or a free-text "Other" entry, user_rejected =
    // cancelled.
    async function runUserChoiceCard(params) {
        if (toolStreamTimeoutRef.current) { clearTimeout(toolStreamTimeoutRef.current); toolStreamTimeoutRef.current = null; }
        setShowProcessing(false);
        const cardMessageId = 'choice-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7);
        setChatMsg(function (prev) {
            return markStaleFlowCards(prev).concat([{ messageId: cardMessageId, isUser: false, kind: 'user_choice', content: '', time: getCurrentTime(), ts: Date.now(), userChoice: params || {} }]);
        });
        // A choice card can also be the ack for an in-flight __FLOW_EDIT__ (the
        // ambiguous-party path parks here) — clear the edit busy flag so the
        // flow's next card renders unlocked (see runFlowConfirmCard).
        setEditInFlight(false);
        const decision = await new Promise(function (resolve, reject) {
            pendingUserChoiceRef.current = { resolve: resolve, reject: reject, cardMessageId: cardMessageId };
        });
        let text;
        if (decision.choice === 'submit') {
            text = (decision.text != null)
                ? 'RESULT: success (entered: ' + decision.text + ')'
                : 'RESULT: success (selected option ' + decision.index + ': ' + (decision.label != null ? decision.label : '') + ')';
        } else {
            text = 'RESULT: user_rejected (user cancelled the selection)';
        }
        return {
            success: true,
            result: {
                content: [{ type: 'text', text: text }],
                tool_results: [{ tool_name: 'ask_user_choice', output_text: text }]
            }
        };
    }

    async function handleToolCall(content) {
        try {
            const extracted = extractToolInstructionsFromContent(content);
            if (!extracted || !extracted.toolCalls || extracted.toolCalls.length === 0) return false;

            const { toolCalls } = extracted;
            console.log('[Chatbot] Executing tool chain:', toolCalls);

            if (currentTurnFinalizedRef.current) {
                return true;
            }

            if (shouldFinalizeToolLoop(toolCalls)) {
                return finalizeToolLoop();
            }

            clearCurrentTurnAssistantMessages();

            // Tool-execution progress is shown in the thinking/COT panel via the
            // action timeline (beginTurnStep per tool) — NOT as a raw chat message.
            // Disable input during tool execution
            setDisableInput(true);
            setShowProcessing(true);
            setThinkingPhase(toolCalls.length > 1 ? `Running ${toolCalls.length} actions` : '');

            const toolResults = [];
            for (let index = 0; index < toolCalls.length; index += 1) {
                const { tool_name, params } = toolCalls[index];
                console.log(`[Chatbot] Executing tool: ${tool_name}`, params);
                const actionLabel = (TOOL_META[tool_name] || {}).label || titleizeToolName(tool_name);
                setThinkingPhase(`${actionLabel}…`);
                // Live timeline row: spinner + target preview while the action runs.
                beginTurnStep({
                    kind: 'tool',
                    label: actionLabel,
                    toolName: tool_name,
                    params: params || {},
                    preview: describeToolParams(tool_name, params),
                });

                let result;
                if (tool_name === '__flow_confirm__') {
                    result = await runFlowConfirmCard(params);
                } else if (tool_name === 'ask_user_choice') {
                    result = await runUserChoiceCard(params);
                } else if (isPersistToolCall(tool_name, params)) {
                    result = await runPersistWithConfirmation(tool_name, params);
                } else {
                    captureFormMutation(tool_name, params);
                    // Long compound tools (read_so = open + read, 20-40s of DOM waits
                    // with no SSE stream open) report sub-phases so the timeline row
                    // keeps updating instead of looking frozen. Always cleared — a
                    // stale reporter must never touch a later turn's steps.
                    setToolProgressReporter((note) => noteRunningToolStep(note));
                    // Flow-step flag (2026-10-22): while a pre-confirmed flow
                    // step executes, bmsTools auto-answers the SO leave-guard
                    // instead of surfacing the page-freezing native confirm.
                    // Cleared in finally so a later free-form call never
                    // inherits flow privileges.
                    const flowStep = !!toolCalls[index].__flowStep;
                    if (flowStep) setFlowStep(true);
                    try {
                        result = await executeTool(tool_name, params);
                    } finally {
                        if (flowStep) setFlowStep(false);
                        setToolProgressReporter(null);
                    }
                }

                // Close the row with the outcome: status + one-line result digest.
                const rawOutput = getToolResultText(tool_name, result);
                const outputText = prettyToolOutput(tool_name, rawOutput);
                const outcome = friendlyOutcome(outputText);
                completeTurnStep({
                    status: isToolStepError(rawOutput) ? 'error' : 'ok',
                    output_text: outputText.length > 4000 ? `${outputText.slice(0, 4000)}…` : outputText,
                    preview: [describeToolParams(tool_name, params), outcome || digestToolOutput(outputText)]
                        .filter(Boolean)
                        .join(' — '),
                });
                recordToolResult(tool_name, params, result, !!toolCalls[index].__flowStep);
                toolResults.push({ tool_name, result });
                if (result && (result.__gateDecision === 'reject' || result.__gateDecision === 'keep')) break;
            }

            const lastToolResult = toolResults[toolResults.length - 1];
            const combinedText = toolResults
                .map(({ tool_name, result }) => getToolResultText(tool_name, result))
                .join('\n');

            const combinedResult = {
                success: true,
                result: {
                    content: [{ type: 'text', text: combinedText }],
                    tool_results: toolResults.map(({ tool_name, result }) => ({
                        tool_name,
                        output_text: getToolResultText(tool_name, result)
                    }))
                },
                toolName: lastToolResult.tool_name
            };

            // Send result back to LangGraph
            await sendToolResultToServer(lastToolResult.tool_name, combinedResult);

            // Ensure the chat unlocks after the full tool chain completes.
            setDisableInput(false);

            return true; // Handled — don't display __TOOL_CALL__ as message
        } catch (error) {
            if (error && error.message === 'CLEARED') {
                setDisableInput(false);
                return false;
            }
            console.error('[Chatbot] Error handling tool call:', error);
            setDisableInput(false);
            return false;
        }
    }

    /**
     * Send tool execution result back to the LangGraph server.
     * Uses the same SSE streaming endpoint as sendMessage().
     */
    async function sendToolResultToServer(toolName, result) {
        if (!threadId) {
            console.error('[Chatbot] No thread ID for tool result');
            setDisableInput(false);
            return;
        }

        const payload = {
            assistant_id: ASSISTANT_ID,
            input: {
                messages: [
                    {
                        role: 'user',
                        content: `__TOOL_RESULT__:${JSON.stringify({
                            tool_name: toolName,
                            result: result
                        })}`
                    }
                ]
            },
            config: approvalModeConfig(),
            stream_mode: ['messages', 'updates']
        };

        // Stream-failure hardening (mirrors sendMessage): an open-but-silent stream
        // used to leave the thinking panel spinning forever — the only watchdog was
        // the 300s tool timer, and only when NO assistant message existed yet. Abort
        // the fetch outright when no bytes arrive for STREAM_INACTIVITY_TIMEOUT_MS;
        // the catch below then closes the running timeline rows, detaches the bubble
        // and shows one error toast.
        const streamAbort = new AbortController();
        let inactivityTimer = null;
        const clearInactivity = () => {
            if (inactivityTimer) { clearTimeout(inactivityTimer); inactivityTimer = null; }
        };
        const kickInactivity = () => {
            clearInactivity();
            inactivityTimer = setTimeout(() => {
                console.error('[Chatbot] Tool-result stream inactive — no data received, aborting');
                streamAbort.abort();
            }, STREAM_INACTIVITY_TIMEOUT_MS);
        };

        // Hoisted so the catch can finalize whatever was already displayed.
        let thisStreamingContent = '';
        let hasStartedStreaming = false;
        let streamErrorText = null;
        const thisMessageId = `tool-response-${Date.now()}`;

        try {
            // 2026-10-23 lost-result wedge (live S202610128): a tool-result POST
            // that the server REJECTS before the run starts (HTTP 409 — the
            // thread is busy running a user nudge that landed mid-batch; 429/5xx)
            // used to fall straight into the catch below, which shows a toast and
            // DISCARDS the envelope. The engine keeps its emitted_step set and the
            // flow wedges silently until a nudge past the 90s grace re-emits the
            // step (12 minutes, live) — re-executing a mutating action. Retry the
            // POST itself when the run provably never started: a non-OK response
            // means zero server-side execution, so resending is idempotent from
            // the engine's point of view. Mid-stream failures (abort after bytes)
            // are NOT retried — the run may still complete server-side.
            let response = null;
            const POST_ATTEMPTS = 3;
            for (let attempt = 1; attempt <= POST_ATTEMPTS; attempt++) {
                response = await fetch(
                    `${LANGGRAPH_URL}/threads/${threadId}/runs/stream`,
                    {
                        method: 'POST',
                        headers: getAuthHeaders(),
                        mode: 'cors',
                        signal: streamAbort.signal,
                        body: JSON.stringify(payload)
                    }
                );
                if (response.ok) break;
                const retryable = [409, 429, 500, 502, 503, 504].includes(response.status);
                console.error(`[Chatbot] Tool-result POST rejected (attempt ${attempt}/${POST_ATTEMPTS}): HTTP ${response.status}`);
                if (!retryable || attempt === POST_ATTEMPTS) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                await new Promise(r => setTimeout(r, 1500 * attempt));
            }

            // Process the streaming response (same SSE pattern as sendMessage)
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let currentEvent = '';
            let kimiToolCallContent = null; // >>> LANGGRAPH CHATBOT INTEGRATION — Deferred Kimi tool call
            let citations = [];
            let lastPhase = '';
            const addThinkingPhase = (phase) => {
                if (phase && phase !== lastPhase) {
                    lastPhase = phase;
                    setThinkingPhase(phase);
                    addCurrentTurnPhase(phase);
                }
            };

            // M3: a tool-result round-trip previously had NO timeout (only the
            // initial sendMessage did), so a hung final hop spun the indicator
            // forever and then produced an empty reply. The watchdog is
            // PROGRESS-AWARE (see fireToolStreamWatchdog / kickToolStreamWatchdog):
            // this call arms it and every visible turn progress signal (timeline
            // rows, bmsTools reportProgress sub-phase notes) re-arms it — a
            // long-but-healthy hop (save_and_confirm's repair ladders run 45s+
            // per party) is never killed; only a genuine 300s stall is.
            // Clear-before-overwrite: a nested sendToolResultToServer (multi-hop
            // tool turns) must not orphan this call's timer in the shared ref;
            // the nested call inherits watchdog responsibility (and its own
            // abort/inactivity handles via the refs).
            toolStreamAbortRef.current = streamAbort;
            toolStreamInactivityClearRef.current = clearInactivity;
            armToolStreamWatchdog();
            const disarmToolStreamTimeout = disarmToolStreamWatchdog;

            kickInactivity();
            while (true) {
                const { done, value } = await reader.read();
                if (done) { clearInactivity(); break; }
                kickInactivity();

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('event: ')) {
                        currentEvent = line.slice(7).trim();
                    } else if (line.startsWith('data: ')) {
                        try {
                            const jsonStr = line.slice(6).trim();
                            if (!jsonStr) continue;
                            const data = JSON.parse(jsonStr);

                            // LangGraph emits `event: error` frames when the run itself
                            // fails (graph exception, model error). These were parsed
                            // and silently dropped, which looked exactly like a frozen
                            // stream. Record the reason and abort — the catch below
                            // finalizes the turn's UI and shows it.
                            if (currentEvent === 'error') {
                                const raw = data && (data.error || data.message || data.detail);
                                streamErrorText = typeof raw === 'string' ? raw : JSON.stringify(data);
                                console.error('[Chatbot] Tool-result stream error event:', streamErrorText);
                                clearInactivity();
                                streamAbort.abort();
                            }

                            if (currentEvent === 'messages/partial') {
                                const messages = Array.isArray(data) ? data : [data];
                                for (const msg of messages) {
                                    if (isStreamToolMessage(msg)) {
                                        let content = getStreamMessageContent(msg);
                                        if (msg.usage_metadata?.output_token_details?.reasoning) {
                                            addCurrentTurnReasoningTokens(msg.usage_metadata.output_token_details.reasoning);
                                        }
                                        addCurrentTurnReasoningText(getStreamReasoningContent(msg));
                                        // Native server tool calls on a follow-up hop get timeline rows too.
                                        if (msg.tool_calls && msg.tool_calls.length > 0) {
                                            beginServerToolSteps(msg.tool_calls);
                                        }
                                        if (content && content !== thisStreamingContent) {
                                            if (extractToolInstructionsFromContent(content)) {
                                                setChatMsg((prevChatMsg) => prevChatMsg.filter(m => m.messageId !== thisMessageId));
                                                hasStartedStreaming = false;
                                                // Handing the turn to the client tool chain: stop the
                                                // inactivity watchdog so it cannot abort this (already
                                                // finished) run while the browser executes tools.
                                                clearInactivity();
                                                await handleToolCall(content);
                                                continue;
                                            }

                                            if (looksLikeToolCallPrefix(content)) {
                                                continue;
                                            }
                                            if (msg.type === 'tool' || msg.role === 'tool') {
                                                // A server tool just finished: fold its output into the
                                                // timeline (synthesizing the row when no native tool_call
                                                // event began one — Kimi text-marker flows).
                                                completeServerToolMessage(msg, getStreamMessageContent(msg));
                                                continue;
                                            }
                                            // >>> LANGGRAPH CHATBOT INTEGRATION — Strip Kimi Markers (tool result) >>>
                                            if (content.includes('<|tool_calls_section_begin|>')) {
                                                kimiToolCallContent = content;
                                                content = content.split('<|tool_calls_section_begin|>')[0].trim();
                                            }
                                            // <<< LANGGRAPH CHATBOT INTEGRATION — Strip Kimi Markers (tool result) <<<

                                            thisStreamingContent = sanitizeStreamContent(content);
                                            if (thisStreamingContent && thisStreamingContent.trim()) {
                                                addThinkingPhase('Generating response');
                                            }
                                            if (!hasStartedStreaming && thisStreamingContent) {
                                                addMessage(thisStreamingContent, false, thisMessageId, null, null, null, true);
                                                trackAssistantMessage(thisMessageId);
                                                hasStartedStreaming = true;
                                            } else if (hasStartedStreaming) {
                                                updateMessage(thisMessageId, thisStreamingContent, null, null, true);
                                            }
                                        }
                                    }
                                }
                            }

                            if (currentEvent === 'messages/complete') {
                                const messages = Array.isArray(data) ? data : [data];
                                // Fold every tool event in the terminal batch into the action
                                // timeline first (tool-call units and results often bypass
                                // token streaming entirely).
                                foldCompletedMessagesIntoTimeline(messages);
                                // Only process the LAST relevant message — earlier ones were handled during their own streams
                                let lastIdx = -1;
                                for (let i = messages.length - 1; i >= 0; i--) {
                                    if (isStreamToolMessage(messages[i])) { lastIdx = i; break; }
                                }
                                for (let i = 0; i < messages.length; i++) {
                                    if (i !== lastIdx) continue;
                                    const msg = messages[i];
                                    if (!isStreamToolMessage(msg)) continue;
                                    // AI message that ALSO carries a tool_call: still show any user-facing text
                                    // (the agent sometimes attaches the real answer / a failure notice to a tool
                                    // call). Pure tool_calls with no content fall through to the `if (content)`
                                    // guard below and display nothing. (Empty-reply fix.)

                                    let content = getStreamMessageContent(msg);
                                    if (content) {
                                        // Check for another nested __TOOL_CALL__ (multi-step)
                                        if (extractToolInstructionsFromContent(content)) {
                                            setChatMsg((prevChatMsg) => prevChatMsg.filter(m => m.messageId !== thisMessageId));
                                            hasStartedStreaming = false;
                                            clearInactivity();
                                            await handleToolCall(content);
                                            continue;
                                        }
                                        if (msg.type === 'tool' || msg.role === 'tool') {
                                            // Server tool finished: fold into the timeline (sendToolResult hop).
                                            completeServerToolMessage(msg, getStreamMessageContent(msg));
                                            continue;
                                        }
                                        // Strip Kimi markers for clean display
                                        if (content.includes('<|tool_calls_section_begin|>')) {
                                            kimiToolCallContent = content;
                                            content = content.split('<|tool_calls_section_begin|>')[0].trim();
                                        }
                                        thisStreamingContent = sanitizeStreamContent(content);
                                        if (thisStreamingContent && thisStreamingContent.trim()) {
                                            addThinkingPhase('Generating response');
                                        }
                                        if (!hasStartedStreaming) {
                                            addMessage(thisStreamingContent, false, thisMessageId, null, null, null, false);
                                            trackAssistantMessage(thisMessageId);
                                            hasStartedStreaming = true;
                                        }
                                        updateMessage(thisMessageId, thisStreamingContent, citations, getCurrentTurnThinkingInfo(), false);
                                    }
                                }
                            }
                        } catch (e) {
                            console.warn('[Chatbot] Failed to parse tool result stream:', e);
                        }
                    }
                }
            }

            // If the conversation was cleared while a confirmation card was pending on a
            // nested hop, bail out instead of falling through to the state fetch / fallback
            // (prevents a stray 'I performed the requested action...' bubble after cancel).
            if (!threadId) { disarmToolStreamTimeout(); return; }

            // An error frame was the last thing received (the abort fired after the
            // final read resolved instead of rejecting it) — route it through the
            // catch so the turn visibly ends with the reason.
            if (streamErrorText) { throw new Error(String(streamErrorText).slice(0, 300)); }

            // >>> LANGGRAPH CHATBOT INTEGRATION — Execute Deferred Kimi Tool Call (tool result) >>>
            if (kimiToolCallContent && kimiToolCallContent.includes('<|tool_call_end|>')) {
                const parsed = parseKimiToolCall(kimiToolCallContent);
                if (parsed) {
                    await handleToolCall(parsed);
                    return; // handleToolCall opens its own stream
                }
            }
            // <<< LANGGRAPH CHATBOT INTEGRATION — Execute Deferred Kimi Tool Call (tool result) <<<

            try {
                const stateResponse = await fetch(`${LANGGRAPH_URL}/threads/${threadId}/state`, {
                    headers: getAuthHeaders(),
                    mode: 'cors'
                });

                if (stateResponse.ok) {
                    const state = await stateResponse.json();
                    const messages = state.values?.messages || [];

                    for (let index = messages.length - 1; index >= 0; index -= 1) {
                        const msg = messages[index];
                        if (!isStreamToolMessage(msg)) {
                            continue;
                        }

                        const content = getStreamMessageContent(msg);
                        if (!content) {
                            continue;
                        }

                        if (extractToolInstructionsFromContent(content)) {
                            await handleToolCall(content);
                            return;
                        }

                        if (!hasStartedStreaming) {
                            addMessage(content, false, thisMessageId, citations, getCurrentTurnThinkingInfo());
                            trackAssistantMessage(thisMessageId);
                            hasStartedStreaming = true;
                        } else {
                            updateMessage(thisMessageId, content, citations, getCurrentTurnThinkingInfo(), false);
                        }
                        thisStreamingContent = content;
                        break;
                    }
                }
            } catch (recoveryError) {
                console.warn('[Chatbot] Failed to recover nested tool stream from thread state:', recoveryError);
            }

            // M3: if the tool-result stream ended with nothing displayed (the
            // "loaded for a long time then empty reply" symptom), show a visible
            // fallback so the user is never left staring at a blank turn. The
            // guard prevents double-firing across recursive calls (only the first
            // level with zero assistant messages this turn emits it).
            disarmToolStreamTimeout();
            if (hasStartedStreaming) {
                updateMessage(thisMessageId, thisStreamingContent, citations, getCurrentTurnThinkingInfo(), false);
            } else if (threadId && !currentTurnAssistantMessageIdsRef.current.some(id => typeof id === 'string' && id.endsWith('-fallback'))) {
                // EMPTY-REPLY FIX: this terminal tool-result round-trip produced no
                // displayable assistant text (the run emitted nothing, or its only AI
                // message carried a tool_call and was suppressed). The previous guard
                // (...length === 0) skipped this fallback whenever earlier steps in the
                // same turn had already shown messages — leaving a blank bubble on long
                // flows (e.g. open→edit→save). Now we always surface a visible reply,
                // deduped per turn via the '-fallback' id suffix.
                addMessage(
                    'I performed the requested action in BMS, but the final confirmation did not come through clearly. ' +
                    'If you asked me to save or change something, it may not have been saved — a save that reports a ' +
                    '"Network Error" means the BMS report/document service is unavailable. Would you like me to re-open ' +
                    'the Shipping Order and verify the current status?',
                    false,
                    `${thisMessageId}-fallback`
                );
                trackAssistantMessage(`${thisMessageId}-fallback`);
            }
            setDisableInput(false);
            hideProcessingIndicator();

        } catch (error) {
            disarmToolStreamWatchdog();
            clearInactivity();
            console.error('[Chatbot] Error sending tool result:', error);
            // Close orphaned running rows ("Read booking"-style spinners) and detach
            // the streaming bubble so a failed hop visibly ends.
            finishTurnSteps('error');
            if (currentTurnAssistantMessageIdsRef.current.includes(thisMessageId)) {
                updateMessage(thisMessageId, thisStreamingContent || 'The response was interrupted.', undefined, getCurrentTurnThinkingInfo(), false);
            }
            setDisableInput(false);
            if (streamErrorText) {
                showError(`The assistant run failed: ${String(streamErrorText).slice(0, 200)}`);
            } else if (error && error.name === 'AbortError') {
                showError('The assistant stopped responding. Please try again.');
            } else {
                showError('Failed to execute tool. Please try again.');
            }
        }
    }
    // <<< LANGGRAPH CHATBOT INTEGRATION — Tool Call Handler <<<

    const sendMessage = async (message, fileAttachments = []) => {
        if (!threadId) {
            showError('No active conversation thread');
            return;
        }

        // The user sent a chat message (typed text or a __FLOW_EDIT__ card edit)
        // instead of clicking a pending card: retire the card LOCALLY — no
        // result envelope (that would be a server-side CANCEL of a flow the
        // user may still be completing). This also covers sendFlowEdit: an edit
        // starts a new engine turn, so the old card's promise must not linger.
        retirePendingCards();

        currentTurnAssistantMessageIdsRef.current = [];
        preparedFormChangesRef.current = [];
        // A resolved choice card whose turn ended without any reply (stream
        // error) would otherwise stay flagged forever — retire it now.
        setChatMsg(prevMsgs => prevMsgs.filter(m => !m.retireOnReply));
        beginCurrentTurnThinking();

        // Add user message to conversation history (multimodal if attachments)
        const msgContent = fileAttachments && fileAttachments.length > 0
            ? await _buildMultimodalContent(message, fileAttachments)
            : message;
        conversationHistory.push({
            role: 'user',
            content: msgContent
        });

        // Create a unique message ID for THIS specific message send
        // This prevents race conditions when multiple messages are sent quickly
        const thisMessageId = 'streaming-message-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
        let thisStreamingContent = '';

        // Track the number of messages BEFORE sending to detect new responses
        const messageCountBeforeSend = chatMsg.filter(m => !m.isUser).length;

        // Stream-failure hardening: abort the fetch when no bytes arrive for
        // STREAM_INACTIVITY_TIMEOUT_MS (the old 180s timer only fired when NOTHING
        // had streamed and never aborted anything, so a silent stream hung the
        // thinking panel forever). The catch closes the running timeline rows,
        // detaches the streaming bubble and shows one toast.
        const streamAbort = new AbortController();
        let inactivityTimer = null;
        const clearInactivity = () => {
            if (inactivityTimer) { clearTimeout(inactivityTimer); inactivityTimer = null; }
        };
        const kickInactivity = () => {
            clearInactivity();
            inactivityTimer = setTimeout(() => {
                console.error('Stream inactivity timeout: no data received for message', thisMessageId, '— aborting');
                streamAbort.abort();
            }, STREAM_INACTIVITY_TIMEOUT_MS);
        };
        let streamErrorText = null;

        try {
            console.log('Sending message to LangGraph:', {
                url: `${LANGGRAPH_URL}/threads/${threadId}/runs/stream`,
                threadId: threadId,
                assistant_id: ASSISTANT_ID,
                messageCount: conversationHistory.length,
                message: message,
                messageId: thisMessageId
            });

            const response = await fetch(
                `${LANGGRAPH_URL}/threads/${threadId}/runs/stream`,
                {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    mode: 'cors',
                    signal: streamAbort.signal,
                    body: JSON.stringify({
                        assistant_id: ASSISTANT_ID,
                        input: {
                            messages: conversationHistory
                        },
                        config: approvalModeConfig(),
                        stream_mode: ['messages', 'updates']
                    })
                }
            );

            // Handle thread not found (404) - recreate thread
            if (response.status === 404) {
                console.warn('Thread not found, creating new thread...');
                hideTypingIndicator();
                showError('Session expired. Creating new conversation...');

                // Clear old thread and create new one
                threadId = null;
                threadIdRef.current = null;
                sessionStorage.removeItem('langgraph_thread_id');
                const newThreadId = await initializeChat({ forceNew: true });

                if (newThreadId) {
                    // Retry sending the message with new thread
                    showTypingIndicator();
                    setTimeout(() => sendMessage(message, fileAttachments), 500);
                }
                return;
            }

            if (!response.ok) {
                const errorText = await response.text().catch(() => response.statusText);
                console.error('API Error:', response.status, errorText);
                throw new Error(`Failed to send message: ${response.statusText}`);
            }

            // Remove typing indicator and create a message placeholder for streaming
            hideTypingIndicator();
            showProcessingIndicator();
            streamingContent = '';
            let hasStartedStreaming = false;
            let kimiToolCallContent = null; // >>> LANGGRAPH CHATBOT INTEGRATION — Deferred Kimi tool call
            let toolChainInitiated = false; // Track if handleToolCall was called to skip recovery

            // Process streaming response (Server-Sent Events format)
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let currentEvent = '';
            let citations = [];

            // Thinking/phase tracking
            let thinkingPhases = [];
            let totalReasoningTokens = 0;
            let lastPhase = '';
            const addThinkingPhase = (phase) => {
                if (phase && phase !== lastPhase) {
                    thinkingPhases.push(phase);
                    lastPhase = phase;
                    setThinkingPhase(phase);
                    addCurrentTurnPhase(phase);
                }
            };
            addThinkingPhase('Analyzing your question');

            // Arm the no-data watchdog from the first read (covers the
            // nothing-ever-streamed case that the old 180s timer handled).
            kickInactivity();

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    clearInactivity();
                    break;
                }
                kickInactivity();

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');

                // Keep the last incomplete line in buffer
                buffer = lines.pop() || '';

                for (const line of lines) {
                    // Parse SSE format: "event: <type>" and "data: <json>"
                    if (line.startsWith('event: ')) {
                        currentEvent = line.slice(7).trim();
                    }
                    else if (line.startsWith('data: ')) {
                        try {
                            const jsonStr = line.slice(6).trim();
                            if (!jsonStr) continue;

                            const data = JSON.parse(jsonStr);

                            //console.log('Event:', currentEvent, 'Data:', data);

                            // LangGraph emits `event: error` frames when the run itself
                            // fails (graph exception, model error). These were parsed
                            // and silently dropped, which looked exactly like a frozen
                            // stream. Record the reason and abort — the catch below
                            // finalizes the turn's UI and shows it.
                            if (currentEvent === 'error') {
                                const raw = data && (data.error || data.message || data.detail);
                                streamErrorText = typeof raw === 'string' ? raw : JSON.stringify(data);
                                console.error('[Chatbot] Stream error event:', streamErrorText);
                                clearInactivity();
                                streamAbort.abort();
                            }

                            // Handle updates event to capture metadata/citations
                            if (currentEvent === 'updates') {
                                console.log('Updates event received:', JSON.stringify(data, null, 2));

                                // Multi-agent plan + cursor. planner emits agent_plan (>=2 entries)
                                // and current_agent_idx=0 once at turn start; handoff advances
                                // current_agent_idx before each subsequent agent streams. The agent
                                // currently streaming is INTERMEDIATE unless its idx is the last.
                                if (data?.planner?.agent_plan?.length >= 2) {
                                    currentTurnAgentPlanRef.current = data.planner.agent_plan;
                                    currentTurnAgentIdxRef.current = data.planner.current_agent_idx ?? 0;
                                }
                                if (data?.handoff && typeof data.handoff.current_agent_idx === 'number') {
                                    currentTurnAgentIdxRef.current = data.handoff.current_agent_idx;
                                }

                                // (Retrieval itself is shown by the tool step the
                                // tool_calls/tool-message events create — no separate phase here.)

                                // Check if the update contains metadata - try different paths
                                if (data && data.tool_node) {
                                    console.log('Processing tool_node event');

                                    // Check metadata
                                    if (data.tool_node.metadata) {
                                        console.log('Found tool_node.metadata:', JSON.stringify(data.tool_node.metadata, null, 2));
                                        citations = data.tool_node.metadata;
                                        console.log('Captured citations from tool_node.metadata:', citations);
                                    }

                                    // Check artifact field
                                    if (data.tool_node.artifact) {
                                        console.log('Found tool_node.artifact:', JSON.stringify(data.tool_node.artifact, null, 2));
                                        try {
                                            const artifactContent = typeof data.tool_node.artifact === 'string'
                                                ? JSON.parse(data.tool_node.artifact)
                                                : data.tool_node.artifact;
                                            console.log('Parsed artifact content:', artifactContent);

                                            // Check if artifact contains citations/documents
                                            if (artifactContent && (artifactContent.documents || artifactContent.citations)) {
                                                citations = artifactContent.documents || artifactContent.citations;
                                                console.log('Captured citations from artifact:', citations);
                                            }
                                        } catch (e) {
                                            console.log('Artifact is not JSON, raw value:', data.tool_node.artifact);
                                        }
                                    }
                                }
                                else if (data && data.metadata) {
                                    citations = data.metadata;
                                    console.log('Captured citations from data.metadata:', citations);
                                }
                                else if (data && typeof data === 'object') {
                                    // Try to find metadata anywhere in the object
                                    const searchForMetadata = (obj, path = '') => {
                                        if (!obj || typeof obj !== 'object') return;
                                        if (Array.isArray(obj)) {
                                            obj.forEach((item, idx) => searchForMetadata(item, `${path}[${idx}]`));
                                            return;
                                        }
                                        if (obj.metadata) {
                                            console.log(`Found metadata at ${path}:`, obj.metadata);
                                            if (Array.isArray(obj.metadata) && obj.metadata.length > 0) {
                                                citations = obj.metadata;
                                            }
                                        }
                                        // Also check for citations directly (not in metadata)
                                        if (obj.citations) {
                                            console.log(`Found citations at ${path}:`, obj.citations);
                                            if (Array.isArray(obj.citations) && obj.citations.length > 0) {
                                                citations = obj.citations;
                                            }
                                        }
                                        // Check for documents array
                                        if (obj.documents) {
                                            console.log(`Found documents at ${path}:`, obj.documents);
                                            if (Array.isArray(obj.documents) && obj.documents.length > 0) {
                                                citations = obj.documents;
                                            }
                                        }
                                        Object.keys(obj).forEach(key => {
                                            if (key !== 'metadata' && key !== 'citations' && key !== 'documents') {
                                                searchForMetadata(obj[key], `${path}.${key}`);
                                            }
                                        });
                                    };
                                    searchForMetadata(data, 'data');
                                }

                                // Final check: log what we ended up with
                                console.log('Final citations after extraction:', citations);
                                if (citations && citations.length > 0) {
                                    currentTurnCitationsRef.current = citations;
                                }
                                console.log('Citations length:', citations ? citations.length : 0);
                                // If the answer is already displayed, re-render with citations
                                // immediately (updates for subgraph nodes fire AFTER messages/complete).
                                if (citations && citations.length > 0 && hasStartedStreaming && thisStreamingContent) {
                                    updateMessage(thisMessageId, thisStreamingContent, citations);
                                }
                            }

                            // Handle messages/partial for streaming updates (token by token)
                            if (currentEvent === 'messages/partial') {
                                const messages = Array.isArray(data) ? data : [data];

                                for (const msg of messages) {
                                    // Check if this is an AI message
                                    if (isStreamToolMessage(msg)) {
                                        let content = getStreamMessageContent(msg);

                                        // Native tool calls from the SERVER model (manual retrieval,
                                        // analytics tools, …): each becomes a live timeline row with
                                        // its target preview, spinner while running.
                                        if (msg.tool_calls && msg.tool_calls.length > 0) {
                                            beginServerToolSteps(msg.tool_calls);
                                        }

                                        // If this partial has native tool_calls AND content, suppress display
                                        // The content is just a preamble like "I'll help you..." — the real action is the tool call
                                        if (msg.tool_calls && msg.tool_calls.length > 0 && content) {
                                            if (hasStartedStreaming && isFinalAgentStreaming()) {
                                                setChatMsg((prevChatMsg) => prevChatMsg.filter(m => m.messageId !== thisMessageId));
                                                hasStartedStreaming = false;
                                            }
                                            toolChainInitiated = true;
                                            // A server-side tool is about to run (its result
                                            // arrives as later chunks) — RE-ARM the watchdog,
                                            // don't clear it, so a hung tool still aborts.
                                            kickInactivity();
                                            continue;
                                        }

                                        // Extract reasoning tokens from usage_metadata
                                        if (msg.usage_metadata?.output_token_details?.reasoning) {
                                            totalReasoningTokens += msg.usage_metadata.output_token_details.reasoning;
                                            addCurrentTurnReasoningTokens(msg.usage_metadata.output_token_details.reasoning);
                                        }
                                        addCurrentTurnReasoningText(getStreamReasoningContent(msg));

                                        // Update on every chunk for real-time streaming
                                        if (content !== undefined && content !== thisStreamingContent) {
                                            if (extractToolInstructionsFromContent(content)) {
                                                setChatMsg((prevChatMsg) => prevChatMsg.filter(m => m.messageId !== thisMessageId));
                                                hasStartedStreaming = false;
                                                toolChainInitiated = true;
                                                // Handing the turn to the client tool chain:
                                                // stop the inactivity watchdog so it cannot
                                                // abort this (already finished) run while
                                                // the browser executes BMS tools.
                                                clearInactivity();
                                                await handleToolCall(content);
                                                return;
                                            }

                                            if (looksLikeToolCallPrefix(content)) {
                                                continue;
                                            }

                                            if (msg.type === 'tool' || msg.role === 'tool') {
                                                // A server tool just finished: fold its output into the
                                                // timeline (synthesizing the row when no native tool_call
                                                // event began one — Kimi text-marker flows).
                                                completeServerToolMessage(msg, getStreamMessageContent(msg));
                                                continue;
                                            }

                                            // >>> LANGGRAPH CHATBOT INTEGRATION — Strip Kimi Markers >>>
                                            // Kimi-K2.5 outputs tool calls as raw text tokens.
                                            // Strip them here for clean display; execute tool calls AFTER stream ends.
                                            if (content.includes('<|tool_calls_section_begin|>')) {
                                                kimiToolCallContent = content; // Save for post-loop execution
                                                content = content.split('<|tool_calls_section_begin|>')[0].trim();
                                            }
                                            // <<< LANGGRAPH CHATBOT INTEGRATION — Strip Kimi Markers <<<

                                            thisStreamingContent = sanitizeStreamContent(content);

                                            // Multi-agent: route an INTERMEDIATE agent's streamed text into the
                                            // thinking trace instead of a reply bubble. hasStartedStreaming is
                                            // never set here, so no bubble is ever created for the RAG answer
                                            // (which the next agent would otherwise flash-then-overwrite).
                                            if (thisStreamingContent && thisStreamingContent.trim() && !isFinalAgentStreaming()) {
                                                addThinkingPhase(currentAgentLabel());
                                                addCurrentTurnAgentStep(currentAgentLabel(), thisStreamingContent);
                                                continue;
                                            }

                                            // Phase detection: content = generating response
                                            if (thisStreamingContent && thisStreamingContent.trim()) {
                                                addThinkingPhase('Generating response');
                                            }

                                            // First token received - replace processing indicator with message
                                            if (!hasStartedStreaming) {
                                                // Only add message and hide processing indicator if we have actual content
                                                if (thisStreamingContent && thisStreamingContent.trim().length > 0) {
                                                    addMessage(thisStreamingContent, false, thisMessageId, null, null, null, true);
                                                    trackAssistantMessage(thisMessageId);
                                                    hasStartedStreaming = true;
                                                }
                                            }
                                            else {
                                                updateMessage(thisMessageId, thisStreamingContent, null, null, true);
                                            }
                                        }
                                    }
                                }
                            }

                            // Handle messages/complete for final message
                            if (currentEvent === 'messages/complete') {
                                const messages = Array.isArray(data) ? data : [data];
                                // Fold every tool event in the terminal batch into the action
                                // timeline first (tool-call units and results often bypass
                                // token streaming entirely).
                                foldCompletedMessagesIntoTimeline(messages);
                                // Only process the LAST relevant message — earlier ones were handled during their own streams
                                let lastIdx = -1;
                                for (let i = messages.length - 1; i >= 0; i--) {
                                    if (isStreamToolMessage(messages[i])) { lastIdx = i; break; }
                                }

                                for (let i = 0; i < messages.length; i++) {
                                    if (i !== lastIdx) continue;
                                    const msg = messages[i];
                                    if (!isStreamToolMessage(msg)) continue;
                                    // AI message that ALSO carries a tool_call: still show any user-facing text
                                    // (the agent sometimes attaches the real answer / a failure notice to a tool
                                    // call). Pure tool_calls with no content fall through to the `if (content)`
                                    // guard below and display nothing. (Empty-reply fix.)

                                    let content = getStreamMessageContent(msg);

                                    if (content) {
                                        // >>> LANGGRAPH CHATBOT INTEGRATION — Tool Call Check >>>
                                        if (extractToolInstructionsFromContent(content)) {
                                            console.log('[Chatbot] Received tool call from server');
                                            setChatMsg((prevChatMsg) => prevChatMsg.filter(m => m.messageId !== thisMessageId));
                                            hasStartedStreaming = false;
                                            toolChainInitiated = true;
                                            clearInactivity();
                                            const handled = await handleToolCall(content);
                                            if (handled) {
                                                continue; // Don't display tool call as chat message
                                            }
                                        }
                                        // Tool-type messages (retrieve_chroma_docs docs, tool outputs) are
                                        // internal context — NEVER a user-facing reply. BMS __TOOL_CALL__
                                        // markers were already caught above. (CoT leak fix.)
                                        if (msg.type === 'tool' || msg.role === 'tool') {
                                            continue;
                                        }
                                        // Strip Kimi markers for clean display
                                        if (content.includes('<|tool_calls_section_begin|>')) {
                                            kimiToolCallContent = content;
                                            content = content.split('<|tool_calls_section_begin|>')[0].trim();
                                        }
                                        // <<< LANGGRAPH CHATBOT INTEGRATION — Tool Call Check <<<

                                        thisStreamingContent = sanitizeStreamContent(content);

                                        // Multi-agent: keep intermediate agent output in the trace, not a bubble.
                                        if (thisStreamingContent && thisStreamingContent.trim() && !isFinalAgentStreaming()) {
                                            addThinkingPhase(currentAgentLabel());
                                            addCurrentTurnAgentStep(currentAgentLabel(), thisStreamingContent);
                                            continue;
                                        }

                                        // First content received - replace processing indicator
                                        if (!hasStartedStreaming) {
                                            // Only add message and hide processing indicator if we have actual content
                                            if (thisStreamingContent && thisStreamingContent.trim().length > 0) {
                                                addMessage(thisStreamingContent, false, thisMessageId, null, null, null, false);
                                                trackAssistantMessage(thisMessageId);
                                                hasStartedStreaming = true;
                                            }
                                        }

                                        // Update with final content and citations
                                        updateMessage(thisMessageId, thisStreamingContent, citations, null, false);
                                        console.log('Updated message with citations:', citations.length, 'citations');
                                    }
                                }
                            }
                        }
                        catch (e) {
                            console.warn('Failed to parse streaming data:', line, e);
                        }
                    }
                }
            }

            // An error frame was the last thing received (the abort fired after the
            // final read resolved instead of rejecting it) — route it through the
            // catch so the turn visibly ends with the reason, skipping recovery.
            if (streamErrorText) { throw new Error(String(streamErrorText).slice(0, 300)); }

            // >>> LANGGRAPH CHATBOT INTEGRATION — Execute Deferred Kimi Tool Call >>>
            if (kimiToolCallContent && kimiToolCallContent.includes('<|tool_call_end|>')) {
                const parsed = parseKimiToolCall(kimiToolCallContent);
                if (parsed) {
                    toolChainInitiated = true;
                    clearInactivity();
                    hideProcessingIndicator();
                    await handleToolCall(parsed);
                    return; // handleToolCall opens its own stream, skip rest of sendMessage
                }
            }
            // <<< LANGGRAPH CHATBOT INTEGRATION — Execute Deferred Kimi Tool Call <<<

            // After the stream is complete, build thinking info
            finishTurnSteps();
            const thinkingInfo = getCurrentTurnThinkingInfo() || (thinkingPhases.length > 0 ? {
                phases: thinkingPhases,
                reasoningTokens: totalReasoningTokens,
                duration: Date.now() - (processingStartTimeRef.current || Date.now()),
                steps: thinkingPhases.map((label, i) => ({
                    id: `fb-${i}`,
                    kind: 'phase',
                    label,
                    icon: (PHASE_META[label] || {}).icon || 'brain',
                    status: 'ok',
                })),
            } : null);

            // Apply final citations and thinking info if we have them
            if (hasStartedStreaming && citations && citations.length > 0) {
                console.log('Stream complete - applying citations:', citations);
                updateMessage(thisMessageId, thisStreamingContent, citations, thinkingInfo, false);
            } else if (hasStartedStreaming && thinkingInfo) {
                updateMessage(thisMessageId, thisStreamingContent, undefined, thinkingInfo, false);
            }

            // CRITICAL FIX: Ensure message was always added, even if streaming had issues
            hideProcessingIndicator();

            // If a tool chain was initiated (handleToolCall was called), skip recovery —
            // the tool chain handles its own display via sendToolResultToServer
            if (toolChainInitiated) {
                console.log('Tool chain was initiated, skipping recovery section');
            } else if (!hasStartedStreaming) {
                console.warn('Stream completed but hasStartedStreaming is false. ThisStreamingContent:', thisStreamingContent);
                // Fallback if no content was streamed
                if (!thisStreamingContent) {
                    console.log('No message in stream, fetching thread state...');
                    try {
                        const stateResponse = await fetch(`${LANGGRAPH_URL}/threads/${threadId}/state`, {
                            headers: getAuthHeaders(),
                            mode: 'cors'
                        });
                        if (stateResponse.ok) {
                            const state = await stateResponse.json();
                            console.log('Thread state:', state);

                            // Check for citations in the state
                            if (state.values && state.values.metadata) {
                                citations = state.values.metadata;
                                console.log('Found citations in state.values.metadata:', citations);
                            }

                            if (state.values && state.values.messages) {
                                const messages = state.values.messages;
                                // Count AI messages in thread state
                                const aiMessagesInState = messages.filter(m => m.type === 'ai' || m.role === 'assistant');

                                // Only try to recover if there are MORE AI messages in state than we've displayed
                                if (aiMessagesInState.length > messageCountBeforeSend) {
                                    // There's a new message in the state that we haven't displayed yet
                                    for (let i = messages.length - 1; i >= 0; i--) {
                                        const msg = messages[i];
                                        if (isStreamToolMessage(msg)) {
                                            const content = getStreamMessageContent(msg);
                                            if (content) {
                                                if (extractToolCallFromContent(content)) {
                                                    continue;
                                                }
                                                thisStreamingContent = content;
                                                addMessage(thisStreamingContent, false, thisMessageId, citations, thinkingInfo);
                                                hasStartedStreaming = true;
                                                console.log('Recovered NEW message from thread state (total AI messages:', aiMessagesInState.length, ', displayed:', messageCountBeforeSend, ')');
                                                return;
                                            }
                                        }
                                    }
                                } else {
                                    console.warn('No new messages in thread state (total AI messages:', aiMessagesInState.length, ', already displayed:', messageCountBeforeSend, ')');
                                    console.warn('The stream did not contain a new response. This may indicate a backend issue.');
                                    // EMPTY-REPLY FIX: the turn ended with no clean text reply — often the last
                                    // AI message carried a tool_call (so it was suppressed above) or the run
                                    // produced no message at all. Recover the last AI message's text content
                                    // from state — even if it also had tool_calls — so the user never sees a
                                    // blank bubble. Strip __TOOL_CALL__ / Kimi tool-call markers first.
                                    for (let i = messages.length - 1; i >= 0; i--) {
                                        const m = messages[i];
                                        if (!(m.type === 'ai' || m.role === 'assistant')) continue;
                                        let c = typeof m.content === 'string'
                                            ? m.content
                                            : (Array.isArray(m.content) ? m.content.filter(b => b && b.type === 'text').map(b => b.text).join('') : '');
                                        if (!c) continue;
                                        if (c.includes('<|tool_calls_section_begin|>')) {
                                            c = c.split('<|tool_calls_section_begin|>')[0];
                                        }
                                        c = c.replace(/__TOOL_CALL__:[\s\S]*$/,'').trim();
                                        if (c) {
                                            addMessage(c, false, thisMessageId, citations, thinkingInfo);
                                            hasStartedStreaming = true;
                                            console.log('Recovered last AI text content (tool-call-attached) to avoid blank reply');
                                            return;
                                        }
                                    }
                                    // Nothing recoverable — surface a notice instead of a blank bubble.
                                    addMessage("I processed your request but couldn't produce a final response. If you asked me to save or change something, the action may have failed (e.g. a backend 'Network Error'). Please try again or rephrase.", false, thisMessageId);
                                    hasStartedStreaming = true;
                                    return;
                                }
                            }
                        }
                    }
                    catch (e) {
                        console.error('Failed to fetch thread state:', e);
                    }

                    // Last resort - add error message only if we haven't already displayed something
                    if (!hasStartedStreaming) {
                        console.error('Unable to recover message content');
                        addMessage("I received your message but couldn't generate a proper response. Please try again.", false, thisMessageId);
                    }
                } else {
                    // Content exists but was never added to UI - add it now
                    console.log('Content exists but was never displayed, adding now:', thisStreamingContent.substring(0, 100));
                    addMessage(thisStreamingContent, false, thisMessageId, citations, thinkingInfo);
                    hasStartedStreaming = true;
                }
            } else {
                // Stream was successful - ensure final update includes citations
                console.log('Final update - checking for citations:', citations);

                // Fallback: if citations were not captured from stream, fetch from thread state
                if (!citations || citations.length === 0) {
                    console.log('No citations from stream, fetching from thread state...');
                    try {
                        const stateResponse = await fetch(`${LANGGRAPH_URL}/threads/${threadId}/state`, {
                            headers: getAuthHeaders(),
                            mode: 'cors'
                        });
                        if (stateResponse.ok) {
                            const state = await stateResponse.json();
                            if (state.values && state.values.metadata && Array.isArray(state.values.metadata) && state.values.metadata.length > 0) {
                                citations = state.values.metadata;
                                console.log('Recovered citations from thread state:', citations.length, 'citations');
                            }
                        }
                    } catch (e) {
                        console.error('Failed to fetch thread state for citations:', e);
                    }
                }

                if (citations && citations.length > 0) {
                    updateMessage(thisMessageId, thisStreamingContent, citations, thinkingInfo, false);
                }

                // Add AI response to conversation history
                if (thisStreamingContent) {
                    conversationHistory.push({
                        role: 'assistant',
                        content: thisStreamingContent
                    });
                    console.log('Conversation history now has', conversationHistory.length, 'messages');
                }
            }

        }
        catch (error) {
            console.error('Error sending message:', error);
            hideTypingIndicator();
            hideProcessingIndicator();
            clearInactivity();

            // A failed stream previously orphaned the live UI: the streaming bubble
            // kept its spinner and every running timeline row ("Read booking"…)
            // stayed 'running' forever while the elapsed clock kept ticking. Close
            // the rows as errored and detach the bubble so the turn visibly ends.
            finishTurnSteps('error');
            if (currentTurnAssistantMessageIdsRef.current.includes(thisMessageId)) {
                updateMessage(thisMessageId, thisStreamingContent || 'The response was interrupted.', undefined, getCurrentTurnThinkingInfo(), false);
            }

            // More specific error message
            if (streamErrorText) {
                showError(`The assistant run failed: ${String(streamErrorText).slice(0, 200)}`);
            }
            else if (error.name === 'AbortError') {
                showError('The assistant stopped responding (no data for 180s). Please try again.');
            }
            else if (error.message.includes('404')) {
                showError('Session expired. Please refresh and try again.');
            }
            else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
                showError('Network error. Please check your connection and try again.');
            }
            else {
                showError('Failed to send message. Please try again.');
            }
        }
        finally {
            // No need to reset global variables since we're using local variables now
        }
    };

    const toggleChat = async () => {
        isOpen = !isOpen;
        isOpenRef.current = isOpen;
        chatBubble.classList.toggle('active');
        chatWindow.classList.toggle('active');

        if (isOpen) {
            // Replied-while-minimized badge is cleared the moment the window opens.
            setUnreadCount(0);
            try {
                const activeThreadId = await initializeChat();
                if (!activeThreadId) {
                    showError('Unable to connect to the assistant. Please try again.');
                }
            }
            catch (error) {
                console.error('Failed to initialize chat:', error);
                showError('Failed to connect to the assistant. Please try again.');
                setChatInputEnabled(true, 'Connection failed. Retry...');
            }

            if (isOpen && inputRef && inputRef.current) {
                inputRef.current.focus();
            }
        }
        else {
            // Closing the window HIDES the chat — it must NOT wipe the
            // conversation (2026-10-12: a stray click on the bubble mid-flow —
            // flow clicks that miss their target can land on it, it overlays
            // the page's bottom-right where Save/Confirm buttons sit — closed
            // the widget, clearConversation() destroyed the thread while a
            // turn was parked, and the whole 5-prompt sequence died back at
            // the greeting). "New conversation" is the ONLY path that clears;
            // closing behaves exactly like minimizeChat below.
            isOpen = false;
            isOpenRef.current = false;
            chatBubble.classList.remove('active');
            chatWindow.classList.remove('active');
        }
    };

    const minimizeChat = () => {
        // Just hide the chat window without clearing conversation
        isOpen = false;
        isOpenRef.current = false;
        chatBubble.classList.remove('active');
        chatWindow.classList.remove('active');
        // Keep the connection status and conversation history intact
        // Don't call clearConversation() or updateConnectionStatus('idle')
    };

    // Header "new conversation" button: drop the current thread, clear the
    // transcript, and connect a fresh one (chips reshuffle via the welcome
    // effect). Blocked while a turn or confirmation is in flight.
    const startNewChat = async () => {
        if (disableInput) return;
        clearConversation();
        try {
            const activeThreadId = await initializeChat({ forceNew: true });
            if (!activeThreadId) {
                showError('Unable to start a new conversation. Please try again.');
            }
        }
        catch (error) {
            console.error('Failed to start new chat:', error);
            showError('Unable to start a new conversation. Please try again.');
        }
    };

    const initializeChat = async (options = {}) => {
        const normalizedOptions = typeof options === 'boolean' ? { forceNew: options } : options;
        const {
            forceNew = false,
            silent = false
        } = normalizedOptions;

        if (threadId && !forceNew) {
            if (!silent) {
                updateConnectionStatus('online');
                setChatInputEnabled(true);
            }
            return threadId;
        }

        if (isInitializingThread) {
            return threadInitializationPromise;
        }

        isInitializingThread = true;

        const shouldUpdateUi = !silent;
        if (shouldUpdateUi) {
            updateConnectionStatus('connecting');
            if (!threadId || forceNew) {
                setChatInputEnabled(false, forceNew ? 'Starting new chat...' : 'Connecting to assistant...');
            }
        }

        threadInitializationPromise = (async () => {
            if (!forceNew) {
                const savedThreadId = sessionStorage.getItem('langgraph_thread_id');
                if (savedThreadId) {
                    //console.log('Restoring thread from session:', savedThreadId);
                    try {
                        const exists = await verifyThreadExists(savedThreadId);
                        if (exists) {
                            threadId = savedThreadId;
                            threadIdRef.current = savedThreadId;
                            //console.log('Thread restored successfully');
                            return threadId;
                        }
                        console.warn('Saved thread no longer exists, creating new one');
                        sessionStorage.removeItem('langgraph_thread_id');
                    }
                    catch (error) {
                        console.error('Failed to verify existing thread:', error);
                        throw error;
                    }
                }
            }
            else {
                sessionStorage.removeItem('langgraph_thread_id');
            }

            try {
                const newThreadId = await createThread();
                if (!newThreadId) {
                    throw new Error('Thread ID missing from createThread response');
                }
                threadId = newThreadId;
                threadIdRef.current = newThreadId;
                //console.log('Chat initialized with new thread:', threadId);
                return threadId;
            }
            catch (error) {
                console.error('Failed to create new thread:', error);
                throw error;
            }
        })();

        try {
            const id = await threadInitializationPromise;
            if (!id) {
                if (shouldUpdateUi) {
                    setChatInputEnabled(true, 'Connection failed. Retry...');
                    updateConnectionStatus('error');
                }
                return null;
            }

            if (shouldUpdateUi) {
                updateConnectionStatus('online');
                setChatInputEnabled(true);
            }
            return id;
        }
        catch (error) {
            if (shouldUpdateUi) {
                const placeholder = error && error.name === 'AbortError'
                    ? 'Connection timed out. Retry...'
                    : 'Connection failed. Retry...';
                setChatInputEnabled(true, placeholder);
                updateConnectionStatus('error');
            } else {
                console.warn('Chat initialization failed (silent mode):', error);
            }
            return null;
        }
        finally {
            isInitializingThread = false;
            threadInitializationPromise = null;
        }
    };

    const clearConversation = () => {
        // Abandon-cancel BEFORE the thread id resets: rejectAllPendingConfirmations
        // sends the RESULT envelopes to the still-current thread.
        rejectAllPendingConfirmations();
        // Reset thread ID
        threadId = null;
        threadIdRef.current = null;
        sessionStorage.removeItem('langgraph_thread_id');
        setChatInputEnabled(true);

        if (inputRef && inputRef.current) {
            inputRef.current.value = '';
            setHasInputText(false);
        }

        // Clear conversation history
        conversationHistory = [];
        console.log('Conversation history cleared');

        // Clear the React message state. The welcome message + suggested-prompt
        // chips are rendered by React from the (now empty) state — see the JSX.
        // Previously this reset chatMessages.innerHTML directly, which fought
        // React: it clobbered the DOM but left chatMsg populated, so on reopen
        // the old conversation was visually gone yet the empty-state chips never
        // reappeared. Letting React own the render keeps state and DOM in sync.
        setChatMsg([]);

        // Reset transient UI: source panel, zoom, scroll pill.
        setSelectedCitation(null);
        setShowSourcePanel(false);
        setImageEnlarged(false);
        setSourceZoom(1);
        setShowScrollBottom(false);
        setScrollNewCount(0);
        isNearBottomRef.current = true;
        prevMsgCountRef.current = 0;

        updateConnectionStatus('idle');
    };

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const MAX_ATTACHMENTS = 5;
    const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf'];

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files || []);

        for (const file of files) {
            if (attachments.length >= MAX_ATTACHMENTS) {
                showError('Maximum ' + MAX_ATTACHMENTS + ' attachments allowed');
                break;
            }
            if (file.size > MAX_FILE_SIZE) {
                showError('File "' + file.name + '" exceeds 10MB limit');
                continue;
            }
            if (!ALLOWED_TYPES.includes(file.type)) {
                showError('File type "' + file.type + '" not supported. Use images or PDFs.');
                continue;
            }

            const reader = new FileReader();
            reader.onload = (event) => {
                const base64Data = event.target.result;
                const isImage = file.type.startsWith('image/');
                setAttachments(prev => [...prev, {
                    file,
                    name: file.name,
                    type: isImage ? 'image' : 'pdf',
                    mimeType: file.type,
                    base64: base64Data,
                    preview: isImage ? base64Data : null
                }]);
            };
            reader.readAsDataURL(file);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const resizeImage = (base64Data, maxWidth = 1600) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                if (img.width <= maxWidth) {
                    resolve(base64Data);
                    return;
                }
                const canvas = document.createElement('canvas');
                const ratio = maxWidth / img.width;
                canvas.width = maxWidth;
                canvas.height = Math.round(img.height * ratio);
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL('image/jpeg', 0.85));
            };
            img.src = base64Data;
        });
    };

    const _buildMultimodalContent = async (message, fileAttachments) => {
        if (!fileAttachments || fileAttachments.length === 0) {
            return message;
        }
        const contentBlocks = [];
        if (message) {
            contentBlocks.push({ type: 'text', text: message });
        }
        for (const att of fileAttachments) {
            if (att.type === 'image') {
                const resizedBase64 = await resizeImage(att.base64);
                contentBlocks.push({
                    type: 'image_url',
                    image_url: { url: resizedBase64 }
                });
            } else if (att.type === 'pdf') {
                contentBlocks.push({
                    type: 'text',
                    text: '__PDF_ATTACHMENT__:' + att.name + ':' + att.base64
                });
            }
        }
        return contentBlocks;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (e && e.type !== 'click' && e.type !== 'submit' && e.type !== 'keydown') return;
        if (!inputRef || !inputRef.current) return;

        const { current } = inputRef;
        const message = current.value.trim();
        const currentAttachments = attachments;

        // Require either text or attachments
        if (!message && currentAttachments.length === 0) return;

        if (!(await initializeChat())) {
            showError('Unable to connect to the assistant. Please try again.');
            return;
        }

        // Build attachment info for display
        const displayAttachments = currentAttachments.map(att => ({
            type: att.type,
            name: att.name,
            preview: att.preview,
            base64: att.base64
        }));

        // Add user message to chat (with attachment info for display)
        addMessage(message || 'Attachment(s)', true, null, null, null, displayAttachments);
        current.value = '';
        setHasInputText(false);

        // The user just sent something: always snap to the bottom and keep the
        // input focused (this is the one scroll that should never be suppressed).
        scrollToBottom({ force: true, focus: true });

        // Clear attachments immediately (already captured in currentAttachments above)
        setAttachments([]);

        // Show typing indicator
        showTypingIndicator();

        // Send message with attachments
        await sendMessage(message, currentAttachments);
    };

    // Clicking a suggested-prompt chip sends its full `prompt` text exactly like a
    // typed send (no attachments). Disabled while a turn is in flight.
    const sendSuggestedPrompt = async (prompt) => {
        const message = (prompt || '').trim();
        if (!message || disableInput) return;

        if (!(await initializeChat())) {
            showError('Unable to connect to the assistant. Please try again.');
            return;
        }

        addMessage(message, true);
        showTypingIndicator();
        scrollToBottom({ force: true, focus: true });
        await sendMessage(message, []);
    };

    // Card-edit transport for v2 flow pre-flight cards (design: flow-ux §5.3).
    // A __FLOW_EDIT__ marker is a NORMAL chat user message on the run/stream
    // transport — NOT the awaited tool-result promise path (an edit must START a
    // new engine turn that re-emits the card, which the envelope path cannot do)
    // and NOT the leave-guard's direct-fetch sendAbandonedCardResult. Display is
    // decoupled from transport: the user bubble is the readable "✏️ Label →
    // value" line (addMessage), while the server sees ONLY the raw marker
    // (sendMessage) — the same echo pattern as sendSuggestedPrompt above. Note
    // this is intentionally NOT gated on disableInput: while a card is pending
    // the input is locked, yet edits are exactly the interaction we want.
    const sendFlowEdit = async (flowId, item, edit) => {
        if (!flowId || !edit || !edit.slot || editInFlight) return;
        if (!(await initializeChat())) {
            showError('Unable to connect to the assistant. Please try again.');
            return;
        }
        const raw = '__FLOW_EDIT__:' + JSON.stringify({ v: 1, flow: flowId, edits: [edit] });
        addMessage(prettyEditLabel(item, edit), true);
        showTypingIndicator();
        scrollToBottom({ force: true });
        const seq = ++editSeqRef.current;
        setEditInFlight(true);
        try {
            await sendMessage(raw, []);
        } finally {
            // Primary clear happens in runFlowConfirmCard the moment the
            // re-emitted card renders (the ack) — this stream usually resolves
            // only after that card is answered. This fallback covers a failed /
            // empty stream with no ack card, and the seq guard keeps a slower
            // older stream from clearing a newer edit's flag.
            if (seq === editSeqRef.current) setEditInFlight(false);
        }
    };

    const normalizeMarkdownTables = (content) => {
        if (typeof content !== 'string') {
            return content;
        }

        const trimmed = content.trim();
        const isSingleLineTable = !trimmed.includes('\n')
            && trimmed.startsWith('|')
            && /\|\s*:?-{3,}/.test(trimmed);

        if (!isSingleLineTable) {
            return content;
        }

        // Some model responses collapse table rows into one line: "| col | col | |---|---| | ... |".
        return trimmed.replace(/\|\s+\|/g, '|\n|');
    };

    // Split a table row into trimmed cells, tolerating OPTIONAL leading/trailing
    // pipes so both GFM formats parse: "| a | b |" and "a | b" -> ["a","b"].
    const parseTableRow = (line) => {
        const inner = line.trim().replace(/^\s*\|/, '').replace(/\|\s*$/, '');
        return inner.split('|').map((cell) => cell.trim());
    };
    const normalizeTableRow = (row, columnCount) => {
        let normalized = [...row];

        if (normalized.length === columnCount + 1 && normalized[0] === '') {
            normalized = normalized.slice(1);
        }
        if (normalized.length === columnCount + 1 && normalized[normalized.length - 1] === '') {
            normalized = normalized.slice(0, -1);
        }

        if (normalized.length < columnCount) {
            return [...normalized, ...new Array(columnCount - normalized.length).fill('')];
        }
        if (normalized.length > columnCount) {
            return [
                ...normalized.slice(0, columnCount - 1),
                normalized.slice(columnCount - 1).join(' | ')
            ];
        }

        return normalized;
    };
    // Divider row = cells of only dashes/colons separated by pipes, outer pipes
    // optional. e.g. "---|---|---" and "| --- | --- |" both qualify.
    const isTableDivider = (line) => {
        const trimmed = line.trim();
        if (!trimmed.includes('|')) return false;
        const cells = parseTableRow(trimmed);
        return cells.length >= 2
            && cells.every((cell) => /^\s*:?-+:?\s*$/.test(cell) && cell.includes('-'));
    };
    // A potential table row = any non-empty line containing a pipe. The divider
    // on the following line is the authoritative table signal, so a stray "|"
    // inside ordinary prose will not trigger a table.
    const isTableRow = (line) => {
        const trimmed = line.trim();
        return trimmed.length > 0 && trimmed.includes('|');
    };

    const splitMarkdownContent = (content) => {
        const normalized = normalizeMarkdownTables(content);
        if (typeof normalized !== 'string') {
            return [{ type: 'markdown', content: '' }];
        }

        const lines = normalized.split('\n');
        const blocks = [];
        let markdownBuffer = [];

        const flushMarkdown = () => {
            if (markdownBuffer.length) {
                blocks.push({
                    type: 'markdown',
                    content: markdownBuffer.join('\n')
                });
                markdownBuffer = [];
            }
        };

        let index = 0;
        while (index < lines.length) {
            const currentLine = lines[index];
            const trimmedLine = currentLine.trim();

            if (
                isTableRow(trimmedLine)
                && index + 1 < lines.length
                && isTableDivider(lines[index + 1])
            ) {
                const headers = parseTableRow(trimmedLine);
                let rowIndex = index + 2;
                const rows = [];

                while (rowIndex < lines.length) {
                    const rowLine = lines[rowIndex].trim();
                    if (!isTableRow(rowLine)) {
                        break;
                    }

                    const row = normalizeTableRow(parseTableRow(rowLine), headers.length);
                    rows.push(row);
                    rowIndex += 1;
                }

                if (rows.length) {
                    flushMarkdown();
                    blocks.push({ type: 'table', headers, rows });
                    index = rowIndex;
                    continue;
                }
            }

            markdownBuffer.push(currentLine);
            index += 1;
        }

        flushMarkdown();

        return blocks.length ? blocks : [{ type: 'markdown', content: normalized }];
    };

    // Role gate: render nothing (no bubble, no window) for roles outside the allow-list.
    // if (!isChatbotAllowedForCurrentUser()) {
    //     return null;
    // }

    return (
        <div>
            {lightbox && (
                <div className="da-lightbox" onClick={() => setLightbox(null)}>
                    {lightbox.kind === 'img'
                        ? <img src={lightbox.src} alt="chart" />
                        : <DAChart spec={lightbox.spec} fullscreen />}
                </div>
            )}
            <button id="chatBubble" className="chat-bubble" aria-label="Open chat">
                <svg className="chat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                </svg>
                <svg className="close-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                {unreadCount > 0 && (
                    <span className="chat-bubble__badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
            </button>

            <div id="chatWindow" className="chat-window">
                <div className="resize-handle resize-top"></div>
                <div className="resize-handle resize-right"></div>
                <div className="resize-handle resize-bottom"></div>
                <div className="resize-handle resize-left"></div>
                <div className="resize-handle resize-top-left"></div>
                <div className="resize-handle resize-top-right"></div>
                <div className="resize-handle resize-bottom-left"></div>
                <div className="resize-handle resize-bottom-right"></div>

                <div className="chat-header">
                    <div className="chat-header-content">
                        <div className="chat-title">
                            <h3>AI Assistant</h3>
                            <span className="status" data-state="idle" aria-live="polite">
                                <span className="status-dot"></span>
                                <span className="status-text">Offline</span>
                            </span>
                        </div>
                    </div>
                    <div className="chat-header-controls">
                        <div className="approval-mode" ref={approvalMenuRef}>
                            <button
                                type="button"
                                className={'control-button approval-mode-button' + (approvalMode === 'auto' ? ' is-auto' : '')}
                                onClick={() => setApprovalMenuOpen((open) => !open)}
                                aria-haspopup="menu"
                                aria-expanded={approvalMenuOpen}
                                aria-label={'Approval mode: ' + APPROVAL_MODE_LABELS[approvalMode]}
                                title={'Approval mode: ' + APPROVAL_MODE_LABELS[approvalMode]}
                            >
                                <StepIcon name="shield" />
                                {approvalMode === 'auto' && <span className="approval-mode-badge" aria-hidden="true">A</span>}
                            </button>
                            {approvalMenuOpen && (
                                <div className="approval-mode-menu" role="menu" aria-label="Approval mode">
                                    <div className="approval-mode-menu__label">Approval mode</div>
                                    <button
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={approvalMode === 'ask'}
                                        className={'approval-mode-option' + (approvalMode === 'ask' ? ' is-selected' : '')}
                                        onClick={() => chooseApprovalMode('ask')}
                                    >
                                        <span className="approval-mode-option__title">Ask before changes</span>
                                        <span className="approval-mode-option__desc">Shows a confirmation card before the chatbot changes anything</span>
                                    </button>
                                    <button
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={approvalMode === 'auto'}
                                        className={'approval-mode-option' + (approvalMode === 'auto' ? ' is-selected' : '')}
                                        onClick={() => chooseApprovalMode('auto')}
                                    >
                                        <span className="approval-mode-option__title">Edit automatically</span>
                                        <span className="approval-mode-option__desc">Flows run start to finish without confirmation clicks</span>
                                    </button>
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            className="control-button newchat-button"
                            onClick={startNewChat}
                            disabled={disableInput}
                            aria-label="New conversation"
                            title="New conversation"
                        >
                            <StepIcon name="newchat" />
                        </button>
                        <button id="minimizeChat" className="control-button minimize-button" aria-label="Minimize chat" title="Minimize chat">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                        </button>
                        <button id="closeChat" className="close-button" aria-label="Close chat">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                </div>

                <div id="chatMessages" className="chat-messages" ref={chatMsgRef} onScroll={handleMessagesScroll}>
                    {isWelcome && (
                        <div className="chat-welcome">
                            <div className="chat-welcome__hero">
                                <div className="chat-welcome__avatar">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="4.5" y="7.5" width="15" height="12" rx="2.5" />
                                        <path d="M12 7.5V4" />
                                        <circle cx="12" cy="3" r="1.1" fill="currentColor" stroke="none" />
                                        <circle cx="9" cy="12.5" r="1.15" fill="currentColor" stroke="none" />
                                        <circle cx="15" cy="12.5" r="1.15" fill="currentColor" stroke="none" />
                                        <path d="M9.5 16h5" />
                                    </svg>
                                </div>
                                <h4 className="chat-welcome__title">{getWelcomeGreeting()}</h4>
                                <p className="chat-welcome__subtitle">I can work in your BMS and search the user manual. Ask me anything, or start here:</p>
                            </div>
                            <div className="chat-welcome__cards">
                                {CAPABILITY_CARDS.map((card) => (
                                    <button
                                        key={card.key}
                                        type="button"
                                        className="cap-card"
                                        disabled={disableInput}
                                        onClick={() => sendSuggestedPrompt(card.prompt)}
                                    >
                                        <span className="cap-card__icon"><StepIcon name={card.icon} /></span>
                                        <span className="cap-card__text">
                                            <span className="cap-card__title">{card.title}</span>
                                            <span className="cap-card__desc">{card.desc}</span>
                                        </span>
                                        <span className="cap-card__arrow"><StepIcon name="right" /></span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {
                        isWelcome && visiblePrompts.length > 0 && (
                            <div className="suggested-prompts">
                                <div className="suggested-prompts__label">Try asking</div>
                                <div className="suggested-prompts__chips">
                                    {visiblePrompts.map((s, i) => (
                                        <button
                                            key={`sugg-${i}`}
                                            type="button"
                                            className="suggested-prompt-chip"
                                            disabled={disableInput}
                                            onClick={() => sendSuggestedPrompt(s.prompt)}
                                            title={s.preview}
                                        >
                                            <span className="suggested-prompt-chip__icon"><StepIcon name={AGENT_CHIP_ICON[s.agent] || 'sparkle'} /></span>
                                            {s.preview}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )
                    }
                    {
                        !CheckIsEmptyArray(chatMsg) && (() => {
                            let prevDayKey = null;
                            return chatMsg.map((m, i) => {
                                // Day divider whenever the message's calendar day changes.
                                const dayKey = m.ts ? new Date(m.ts).toDateString() : null;
                                let dayDivider = null;
                                if (dayKey && dayKey !== prevDayKey) {
                                    prevDayKey = dayKey;
                                    dayDivider = (
                                        <div className="day-divider" key={`day-${i}`}>
                                            <span>{formatDayLabel(m.ts)}</span>
                                        </div>
                                    );
                                }
                                return (
                                    <Fragment key={m.messageId || ('msg-' + i)}>
                                        {dayDivider}
                                        <div id={m.messageId ? m.messageId : ''} className={`message ${m.isUser ? 'user-message' : 'bot-message'}`}>
                                    <div className='message-content'>
                                        {m.thinking && !m.isUser && !m.streaming && (() => {
                                            // The set tracks messages the user manually COLLAPSED — the
                                            // timeline is EXPANDED by default so the full COT stays visible.
                                            const isExpanded = !expandedThinking.has(m.messageId);
                                            const tools = m.thinking.tools || [];
                                            const sources = m.citations || [];
                                            // Count tool rows from the unified timeline (covers server-side
                                            // tools too); fall back to the BMS-only tool history.
                                            const actionCount = (m.thinking.steps || []).filter((s) => s.kind === 'tool').length || tools.length;
                                            return (
                                                <div className='thinking-section' onClick={() => toggleThinking(m.messageId)}>
                                                    <div className='thinking-toggle'>
                                                        <svg className={`thinking-chevron ${isExpanded ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <polyline points="6 9 12 15 18 9"></polyline>
                                                        </svg>
                                                        <span className='thinking-summary'>
                                                            {`Thought for ${(m.thinking.duration / 1000).toFixed(1)}s`}
                                                            {actionCount > 0 && ` \u00B7 ${actionCount} action${actionCount === 1 ? '' : 's'}`}
                                                            {sources.length > 0 && ` \u00B7 ${sources.length} source${sources.length === 1 ? '' : 's'}`}
                                                            {m.thinking.reasoningTokens > 0 && ` \u00B7 ${m.thinking.reasoningTokens} reasoning tokens`}
                                                        </span>
                                                    </div>
                                                    {isExpanded && (
                                                        <div onClick={(e) => e.stopPropagation()}>
                                                            <ActionTimeline
                                                                steps={m.thinking.steps}
                                                                citations={sources}
                                                                reasoningText={m.thinking.reasoningText}
                                                                agentSteps={m.thinking.agentSteps}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                        <div className='message-text'>
                                            {!m.streaming && !m.kind && (
                                                <button
                                                    type="button"
                                                    className={`msg-copy-btn${copiedId === (m.messageId || ('u' + i)) ? ' is-copied' : ''}`}
                                                    onClick={(e) => copyMessage(e, (m.messageId || ('u' + i)))}
                                                    title={copiedId === (m.messageId || ('u' + i)) ? 'Copied' : 'Copy'}
                                                    aria-label="Copy message"
                                                >
                                                    {copiedId === (m.messageId || ('u' + i)) ? (
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                            <polyline points="20 6 9 17 4 12" />
                                                        </svg>
                                                    ) : (
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                                            <rect x="9" y="9" width="11" height="11" rx="2" />
                                                            <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                                                        </svg>
                                                    )}
                                                </button>
                                            )}
                                            {m.attachments && m.attachments.length > 0 && (
                                                <div className='message-attachments'>
                                                    {m.attachments.map((att, idx) => (
                                                        att.type === 'image' ? (
                                                            <img key={idx} src={att.base64 || att.preview} alt={att.name} className='message-attachment-image' />
                                                        ) : (
                                                            <div key={idx} className='message-attachment-pdf'>
                                                                <FileIcon className="attachment-pdf-icon" />
                                                                {att.name}
                                                            </div>
                                                        )
                                                    ))}
                                                </div>
                                            )}


                                            {m.kind === 'user_choice' && m.userChoice ? (
                                                <UserChoiceCard
                                                    key={'uc-' + m.messageId}
                                                    payload={m.userChoice}
                                                    resolvedChoice={m.resolvedChoice}
                                                    stale={!!m.stale}
                                                    onSubmit={(sel) => resolveUserChoice('submit', sel)}
                                                    onCancel={() => resolveUserChoice('cancel')}
                                                />
                                            ) : m.kind === 'flow_confirmation' && m.flowConfirm ? (
                                                <FlowConfirmCard
                                                    key={'flow-' + m.messageId}
                                                    payload={m.flowConfirm}
                                                    resolvedChoice={m.resolvedChoice}
                                                    stale={!!m.stale}
                                                    editInFlight={editInFlight}
                                                    onConfirm={() => resolveFlowConfirm('confirm')}
                                                    onCancel={() => resolveFlowConfirm('cancel')}
                                                    onSendEdit={(item, edit) => sendFlowEdit(m.flowConfirm && m.flowConfirm.flow, item, edit)}
                                                />
                                            ) : m.kind === 'confirmation' && m.confirmation ? (
                                                <ConfirmationCard
                                                    key={(confirmationEdit && confirmationEdit.cardMessageId === m.messageId) ? ('edit-' + m.messageId) : ('view-' + m.messageId)}
                                                    payload={m.confirmation}
                                                    edit={m.messageId === (confirmationEdit && confirmationEdit.cardMessageId) ? confirmationEdit : null}
                                                    resolvedChoice={m.resolvedChoice}
                                                    onAccept={() => resolveConfirmation('accept')}
                                                    onKeep={() => startKeepEdit(m.messageId, m.confirmation)}
                                                    onReject={() => resolveConfirmation('reject')}
                                                    onApplyEdits={(items) => resolveConfirmation('keep', items)}
                                                    onCancelEdit={() => setConfirmationEdit(null)}
                                                />
                                            ) : m.isUser ? (
                                                <p style={{ whiteSpace: 'pre-wrap' }}>{m.content}</p>
                                            ) : m.streaming ? (() => {
                                                // nowTick (from the interval effect) drives re-renders so the
                                                // duration counter and the running rows' elapsed time stay live.
                                                void nowTick;
                                                const liveSecs = currentTurnStartedAtRef.current
                                                    ? Math.max(0, (Date.now() - currentTurnStartedAtRef.current) / 1000)
                                                    : null;
                                                return (
                    <div className="live-thinking">
                        <div className="live-thinking-shimmer" aria-hidden="true" />
                        <div className="live-thinking-header">
                                                            <span className="live-thinking-orb" aria-hidden="true" />
                                                            <span className="live-thinking-label">{thinkingPhase || 'Thinking'}</span>
                                                            {liveSecs !== null && (
                                                                <span className="live-thinking-duration">{liveSecs.toFixed(1)}s</span>
                                                            )}
                                                            {currentTurnToolHistoryRef.current.length > 0 && (
                                                                <span className="live-thinking-actions">
                                                                    {currentTurnToolHistoryRef.current.length} action{currentTurnToolHistoryRef.current.length === 1 ? '' : 's'}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {m.content && m.content.trim() && (
                                                            <div className="live-thinking-text">{m.content}<span className="live-thinking-cursor" /></div>
                                                        )}
                                                        <ActionTimeline
                                                            steps={liveSteps}
                                                            citations={currentTurnCitationsRef.current}
                                                            agentSteps={currentTurnAgentStepsRef.current}
                                                            active
                                                        />
                                                    </div>
                                                );
                                            })() : (
                                                (() => {
                                                    const blocks = splitMarkdownContent(m.content);

                                                    return blocks.map((block, blockIdx) => {
                                                        if (block.type === 'table') {
                                                            return (
                                                                <div className="chatbot-table-wrap" key={`table-wrap-${blockIdx}`}>
                                                                <table key={`table-${blockIdx}`}>
                                                                    <thead>
                                                                        <tr>
                                                                            {block.headers.map((header, headerIdx) => (
                                                                                <th key={`h-${blockIdx}-${headerIdx}`}>
                                                                                    <ReactMarkdown>{header}</ReactMarkdown>
                                                                                </th>
                                                                            ))}
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {block.rows.map((row, rowIdx) => (
                                                                            <tr key={`r-${blockIdx}-${rowIdx}`}>
                                                                                {row.map((cell, cellIdx) => (
                                                                                    <td key={`c-${blockIdx}-${rowIdx}-${cellIdx}`}>
                                                                                        <ReactMarkdown>{cell}</ReactMarkdown>
                                                                                    </td>
                                                                                ))}
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                                </div>
                                                            );
                                                        }

                                                        return (
                                                            <ReactMarkdown
                                                                key={`md-${blockIdx}`}
                                                                components={{
                                                                    p: ({ children }) => <p>{children}</p>,
                                                                    img: ({ src, alt }) => <img src={src} alt={alt || ''} className="da-chart-img" onClick={() => setLightbox({ kind: 'img', src })} />,
                                                                    strong: ({ children }) => <strong>{children}</strong>,
                                                                    em: ({ children }) => <em>{children}</em>,
                                                                    code: ({ inline, children, className, ...props }) => {
                                                                        const content = Array.isArray(children) ? children.join('') : children;
                                                                        if (inline) {
                                                                            return <code>{content}</code>;
                                                                        }
                                                                        if (className && className.indexOf('language-chart') !== -1) {
                                                                            return <ChartBlock content={content} openLightbox={openChartLightbox} />;
                                                                        }
                                                                        return (
                                                                            <pre className={className}>
                                                                                <code {...props}>{content}</code>
                                                                            </pre>
                                                                        );
                                                                    },
                                                                }}
                                                            >
                                                                {block.content}
                                                            </ReactMarkdown>
                                                        );
                                                    });
                                                })()
                                            )}
                                        </div>
                                        <div className='message-time'>{m.time}</div>
                                        {
                                            !CheckIsEmptyArray(m.citations) && <div className='message-citations'>
                                                {
                                                    (() => {
                                                        const seen = new Set();
                                                        const unique = m.citations.filter(c => {
                                                            const key = `${c.source}|${c.page_no}`;
                                                            if (seen.has(key)) return false;
                                                            seen.add(key);
                                                            return true;
                                                        });
                                                        return unique.map((citation, index) => {
                                                            const isActive = selectedCitation
                                                                && selectedCitation._msgId === m.messageId
                                                                && selectedCitation._index === index;
                                                            return (
                                                                <div
                                                                    id={`citation-${index}`}
                                                                    key={index}
                                                                    title={`Source: ${citation.source || 'Unknown'}${citation.page_no && citation.page_no !== 'N/A' ? `, Page ${citation.page_no}` : ''}`}
                                                                    className={`citation${isActive ? ' active' : ''}`}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();

                                                                        setImageEnlarged(false);
                                                                        setSourceZoom(1);
                                                                        if (isActive) {
                                                                            setSelectedCitation(null);
                                                                            setShowSourcePanel(false);
                                                                        } else {
                                                                            // Keep the message's full deduped list on the
                                                                            // selection so the panel can flip prev/next.
                                                                            setSelectedCitation({ ...citation, _index: index, _list: unique, _msgId: m.messageId });
                                                                            setShowSourcePanel(true);
                                                                        }
                                                                    }}
                                                                >
                                                                    <FileIcon className="citation__icon" />
                                                                    <span className="citation__name">{shortSourceName(citation.source)}</span>
                                                                    {citation.page_no && citation.page_no !== 'N/A' && (
                                                                        <span className="citation__page">{`p.${citation.page_no}`}</span>
                                                                    )}
                                                                </div>
                                                            );
                                                        });
                                                    })()
                                                }
                                            </div>
                                        }
                                    </div>
                                </div>
                                    </Fragment>
                                );
                            });
                        })()
                    }
                    {
                        /* Dots row is the pre-timeline heartbeat only. As soon as the
                           action timeline has rows it shows the same phase (spinner +
                           elapsed time), so hide the "••• Analyzing your question"
                           line — it duplicates the timeline row underneath. */
                        showProcessing && !anyStreaming && <div id='processingIndicator' className='thinking-indicator'>
                            {liveSteps.length === 0 && (
                                <div className='thinking-indicator-content'>
                                    <div className='thinking-dots'><span></span><span></span><span></span></div>
                                    <span className='thinking-phase-text'>{thinkingPhase || 'Processing'}</span>
                                    {currentTurnToolHistoryRef.current.length > 0 && (
                                        <span className='thinking-actions-count'>
                                            {currentTurnToolHistoryRef.current.length} action{currentTurnToolHistoryRef.current.length === 1 ? '' : 's'}
                                        </span>
                                    )}
                                </div>
                            )}
                            {/* Live action timeline: shows every step appearing in real time
                                while the turn is still thinking (before the first token). */}
                            {(liveSteps.length > 0 || (currentTurnCitationsRef.current || []).length > 0) && (
                                <ActionTimeline
                                    steps={liveSteps}
                                    citations={currentTurnCitationsRef.current}
                                    agentSteps={currentTurnAgentStepsRef.current}
                                    active
                                />
                            )}
                        </div>
                    }
                    {
                        showTyping && <div id='typingIndicator' className='typing-indicator'>
                            <div className='typing-dots'><span></span><span></span><span></span></div>
                        </div>
                    }
                    {
                        (errorMsg || '') !== '' && (
                            <div className='error-message'>
                                <div className='error-message__title'>
                                    <StepIcon name="alert" />
                                    Something went wrong
                                </div>
                                <div className='error-message__detail'>{errorMsg}</div>
                            </div>
                        )
                    }
                </div>

                {showScrollBottom && (
                    <button
                        type="button"
                        className="scroll-bottom-pill"
                        onClick={jumpToBottom}
                        aria-label="Scroll to latest messages"
                    >
                        <StepIcon name="down" />
                        {scrollNewCount > 0 && (
                            <span className="scroll-bottom-pill__count">{scrollNewCount > 9 ? '9+' : scrollNewCount}</span>
                        )}
                    </button>
                )}

                <div className="chat-input-container">
                    {attachments.length > 0 && (
                        <div className="attachment-preview-bar">
                            {attachments.map((att, index) => (
                                <div key={index} className="attachment-preview-item">
                                    {att.type === 'image' ? (
                                        <img src={att.preview} alt={att.name} className="attachment-preview-image" />
                                    ) : (
                                        <div className="attachment-preview-pdf">
                                            <FileIcon className="attachment-preview-pdf__icon" />
                                            <span className="pdf-name">{att.name}</span>
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        className="attachment-preview-remove"
                                        onClick={() => removeAttachment(index)}
                                        aria-label="Remove attachment"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <form
                        id="chatForm"
                        className="chat-form"
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/gif,image/webp,.pdf"
                            multiple
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        <button
                            type="button"
                            className="attachment-button"
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            disabled={disableInput}
                            aria-label="Attach file"
                            title="Attach image or PDF"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                            </svg>
                        </button>
                        <input
                            ref={inputRef}
                            disabled={disableInput}
                            type="text"
                            id="chatInput"
                            className="chat-input"
                            placeholder="Type your message..."
                            autoComplete="off"
                            onChange={(e) => setHasInputText(e.target.value.trim().length > 0)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSubmit(e);
                                }
                            }}
                        />
                        <button
                            onClick={(e) => handleSubmit(e)}
                            type="submit"
                            className="send-button"
                            aria-label="Send message"
                            disabled={disableInput || (!hasInputText && attachments.length === 0)}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="22" y1="2" x2="11" y2="13"></line>
                                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                            </svg>
                        </button>
                    </form>
                </div>
            </div>
            <div
                id="sourcePanel"
                className={`source-panel${showSourcePanel ? ' visible' : ''}`}
            >
                {selectedCitation && (
                    <>
                        <div className="source-panel-header">
                            <div className="source-panel-source-info">
                                <div className="source-panel-doc-icon">
                                    <FileIcon />
                                </div>
                                <div className="source-panel-meta">
                                    {selectedCitation.source && (
                                        <div className="source-panel-source">{selectedCitation.source}</div>
                                    )}
                                    {selectedCitation.page_no && selectedCitation.page_no !== 'N/A' && (
                                        <div className="source-panel-page">Page {selectedCitation.page_no}</div>
                                    )}
                                </div>
                            </div>
                            <div className="source-panel-tools">
                                {selectedCitation.image_data && (
                                    <>
                                        <button
                                            type="button"
                                            className="source-panel-btn"
                                            onClick={() => changeSourceZoom(-0.2)}
                                            disabled={sourceZoom <= 0.65}
                                            title="Zoom out"
                                            aria-label="Zoom out"
                                        >
                                            <StepIcon name="zoomout" />
                                        </button>
                                        <span className="source-panel-zoom-label">{Math.round(sourceZoom * 100)}%</span>
                                        <button
                                            type="button"
                                            className="source-panel-btn"
                                            onClick={() => changeSourceZoom(0.2)}
                                            disabled={sourceZoom >= 2.45}
                                            title="Zoom in"
                                            aria-label="Zoom in"
                                        >
                                            <StepIcon name="zoomin" />
                                        </button>
                                    </>
                                )}
                                {selectedCitation._list && selectedCitation._list.length > 1 && (
                                    <>
                                        <span className="source-panel-divider" />
                                        <button
                                            type="button"
                                            className="source-panel-btn"
                                            onClick={() => navigateCitation(-1)}
                                            disabled={selectedCitation._index <= 0}
                                            title="Previous source"
                                            aria-label="Previous source"
                                        >
                                            <StepIcon name="left" />
                                        </button>
                                        <span className="source-panel-nav-count">
                                            {`${selectedCitation._index + 1} / ${selectedCitation._list.length}`}
                                        </span>
                                        <button
                                            type="button"
                                            className="source-panel-btn"
                                            onClick={() => navigateCitation(1)}
                                            disabled={selectedCitation._index >= selectedCitation._list.length - 1}
                                            title="Next source"
                                            aria-label="Next source"
                                        >
                                            <StepIcon name="right" />
                                        </button>
                                    </>
                                )}
                                <span className="source-panel-divider" />
                                <button
                                    type="button"
                                    className="source-panel-btn"
                                    onClick={() => {
                                        setSelectedCitation(null);
                                        setShowSourcePanel(false);
                                        setImageEnlarged(false);
                                    }}
                                    title="Close"
                                    aria-label="Close source panel"
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div className="source-panel-body">
                            {selectedCitation.image_data ? (
                                <div className="source-panel-image-wrapper">
                                    <img
                                        src={`data:image/png;base64,${selectedCitation.image_data}`}
                                        alt={`Page ${selectedCitation.page_no || ''}`}
                                        className="source-panel-image"
                                        style={{ width: `${Math.round(sourceZoom * 100)}%` }}
                                    />
                                    <button
                                        className="source-panel-enlarge-btn"
                                        onClick={(e) => { e.stopPropagation(); setImageEnlarged(true); }}
                                        title="Enlarge image"
                                    >
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="15 3 21 3 21 9"></polyline>
                                            <polyline points="9 21 3 21 3 15"></polyline>
                                            <line x1="21" y1="3" x2="14" y2="10"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </svg>
                                    </button>
                                </div>
                            ) : selectedCitation.text ? (
                                <div className="source-panel-text">
                                    {selectedCitation.text.length > 500
                                        ? selectedCitation.text.substring(0, 500) + '...'
                                        : selectedCitation.text
                                    }
                                </div>
                            ) : (
                                <div className="source-panel-placeholder">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                        <polyline points="21 15 16 10 5 21"></polyline>
                                    </svg>
                                    <span>No preview available</span>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
            {/* Enlarged image overlay */}
            {imageEnlarged && selectedCitation && selectedCitation.image_data && (
                <div className="image-enlarge-overlay" onClick={() => setImageEnlarged(false)}>
                    <div className="image-enlarge-content" onClick={(e) => e.stopPropagation()}>
                        <button className="image-enlarge-close" onClick={() => setImageEnlarged(false)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                        <img
                            src={`data:image/png;base64,${selectedCitation.image_data}`}
                            alt={`Page ${selectedCitation.page_no || ''}`}
                            className="image-enlarge-img"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
// Log configuration (helpful for debugging)
if (CONFIG.isDevelopment) {
    console.log('%c=== LangGraph Chat Configuration ===', 'color: #00ff00; font-weight: bold');
    console.log('%cEnvironment:', 'color: #00ff00; font-weight: bold', 'Development');
    console.log('%cMode:', 'color: #00ff00; font-weight: bold', CONFIG.mode.toUpperCase());
    console.log('%cLangGraph URL:', 'color: #00ff00; font-weight: bold', CONFIG.LANGGRAPH_URL);
    console.log('%cAssistant ID:', 'color: #00ff00; font-weight: bold', CONFIG.ASSISTANT_ID);
    console.log('%c====================================', 'color: #00ff00; font-weight: bold');
}
